import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ArrowLeft, FileText, Palette, Sparkles, Plus, X, Download, Loader2, ChevronLeft, ChevronRight, ChevronDown, Image, Type, Upload, Edit3, Wand2, Save, Layers, Eye, Zap, Video, Film, Globe, File, CheckCircle2, LayoutGrid, Play, Check, Send, Paperclip, MessageSquare, Crosshair, FileImage, FileType, BarChart3, Share2, ShieldCheck, Trash2, GraduationCap, History } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { withBackend, normalizeImageUrl } from '../utils/api';
import { applyThemeToExportedPptx } from '../utils/pptxTheme';
import { hoistDecorativeInlines } from '../utils/pptxExportPrep';
import { authHeaders } from '../utils/authHeaders';
import { useFileDrop } from '../hooks/useFileDrop';
import { supabaseHelpers } from '../lib/supabase';
import { useProjectPresence } from '../hooks/useProjectPresence';
import { toast } from 'sonner';
import ShareContentModal from './ShareContentModal';
import BrandReferenceUpload from './BrandReferenceUpload';
import LayerEditor from './LayerEditor';
import InlineSlideEditor from './InlineSlideEditor';
import HtmlSlideEditor from './HtmlSlideEditor';
import CssPropertyPanel from './CssPropertyPanel';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import ThemeCustomizer from './ThemeCustomizer';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import PresentationQuestionnaire from './PresentationQuestionnaire';
import PresentationViewer from './PresentationViewer';
import OutlinePromptSidebar from './OutlinePromptSidebar';
import { ThemeGallery } from './ThemeGallery';
import { PPTXTemplateGallery } from './PPTXTemplateGallery';
import DriveFactVerifier from './DriveFactVerifier';
import { generateSlidesWithTemplates, renderTemplateToImage, renderTemplateBackgroundOnly } from '../lib/templateRenderer';
import { generateEditableLayers } from '../lib/templateRenderer';
import { getTemplateForSlide, themes } from '../data/themes';
import { getDefaultModel } from '../utils/models';

// Renders slide as live HTML iframe (main preview) or static image (thumbnails).
// Pass thumbnail=true for sidebar/strip thumbnails to avoid heavy iframes.
// onOverflow(renderedHeight) called when content exceeds 720px after render.
function SlidePreview({ slide, idx, className = '', draggable, thumbnail = false, onOverflow }) {
  const containerRef = React.useRef(null);
  const iframeRef = React.useRef(null);
  const [scale, setScale] = React.useState(null);
  const [overflowHeight, setOverflowHeight] = React.useState(null);

  React.useEffect(() => {
    if (thumbnail || !slide?.html_source || !containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const w = el.offsetWidth;
      if (w > 0) setScale(w / 1280);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [thumbnail, slide?.html_source]);

  // Post-render overflow detection. We deliberately do NOT auto-scale the
  // body here: the old `transform: scale(720/height)` band-aid shrank every
  // slide that overflowed, which is exactly what made text tiny and left the
  // right edge vacant. Scaling masks the real problem (the generated HTML
  // exceeds 720px) instead of surfacing it. Now we clip to the slide bounds
  // (so the preview matches the exported PPTX, which also clips) and show an
  // honest warning so the overflow is visible and fixable upstream.
  const handleIframeLoad = React.useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      const renderedHeight = doc.body.scrollHeight;
      if (renderedHeight > 720) {
        setOverflowHeight(renderedHeight);
        onOverflow?.(renderedHeight);
        // Clip to bounds (no scaling) — text keeps its real, readable size.
        doc.body.style.height = '720px';
        doc.body.style.overflow = 'hidden';
      } else {
        setOverflowHeight(null);
      }
    } catch (e) {
      // cross-origin guard (shouldn't happen with srcDoc)
    }
  }, [onOverflow]);

  // Thumbnails always use static screenshot
  if (!thumbnail && slide?.html_source) {
    // Wrap only inline scripts in IIFE (skip <script src="..."> CDN tags)
    let safeHtml = slide.html_source.replace(
      /<script(?![^>]*\bsrc\b)([^>]*)>([\s\S]*?)<\/script>/gi,
      (_, attrs, body) => body.trim() ? `<script${attrs}>(function(){${body}})()</script>` : `<script${attrs}></script>`
    );
    // Inject minimal safety CSS — no max-height clipping so overflow can be measured
    const overflowStyle = `<style id="slide-safety-fe">
html{margin:0;padding:0;}
body{margin:0;padding:0;width:1280px;min-height:720px;box-sizing:border-box;}
*,*::before,*::after{box-sizing:border-box;}
h1,h2,h3,h4,h5,h6,p,li,span{overflow-wrap:break-word;word-break:break-word;}
</style>`;
    if (safeHtml.includes('<head>')) {
      safeHtml = safeHtml.replace('<head>', '<head>' + overflowStyle);
    } else if (safeHtml.includes('<html')) {
      safeHtml = safeHtml.replace(/<html([^>]*)>/, `<html$1><head>${overflowStyle}</head>`);
    } else {
      safeHtml = overflowStyle + safeHtml;
    }
    const overflowPercent = overflowHeight ? Math.round((overflowHeight / 720 - 1) * 100) : 0;
    return (
      <div ref={containerRef} className={className || 'w-full h-full'} style={{ overflow: 'hidden', position: 'relative' }}>
        {scale != null && (
          <iframe
            ref={iframeRef}
            srcDoc={safeHtml}
            title={`Slide ${(idx || 0) + 1}`}
            onLoad={handleIframeLoad}
            style={{
              width: 1280,
              height: 720,
              border: 'none',
              pointerEvents: 'none',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
          />
        )}
        {overflowHeight && (
          <div style={{
            position: 'absolute', bottom: 6, right: 6,
            background: 'rgba(220,38,38,0.95)', color: '#fff',
            fontSize: 10, fontWeight: 600, padding: '2px 7px',
            borderRadius: 4, pointerEvents: 'none', zIndex: 10,
          }}>
            ⚠ {overflowPercent}% over 720px — content clipped
          </div>
        )}
      </div>
    );
  }
  if (slide?.preview_url) {
    return <img src={slide.preview_url} alt={`Slide ${(idx || 0) + 1}`} className={className || 'w-full h-full object-contain bg-white'} draggable={draggable} />;
  }
  // No preview yet — show a shimmering skeleton with a "Generating slide N…"
  // hint instead of a blank flat panel. Lets the user open a deck mid-
  // generation (e.g. via a Claude link) and immediately understand the
  // empty slots are still being rendered rather than failed.
  return (
    <div className={`${className || 'w-full h-full'} relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900`}>
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/60 dark:via-white/10 to-transparent" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <div className="w-7 h-7 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs font-medium">Generating slide {(idx || 0) + 1}…</span>
      </div>
      <style>{`@keyframes shimmer { 100% { transform: translateX(100%); } }`}</style>
    </div>
  );
}

const CreatePPTWorkspace = forwardRef(function CreatePPTWorkspace({ onBack, onComplete, onUpdate, onFullscreen, userId, existingProject, preloadedOutline, externalChatMode, onChatMessage, chatMessages, onProjectCreated, onUsageRefresh, onExternalCancel }, ref) {
  // Step management. Three cases:
  //   1. New project, no preload → step 1 (input form)
  //   2. Existing project with slides → step 5 (review/edit)
  //   3. Existing project mid-generation (slides array still empty) →
  //      ALSO step 5, NOT step 1. Otherwise users clicking a Claude
  //      link before the first slide renders get dumped on the
  //      input-form screen as if they'd never started anything.
  const [currentStep, setCurrentStep] = useState(
    existingProject?.externalFeed ? 4  // r140: external feed mounts generating
      : existingProject?.projectId || existingProject?.slides?.length > 0 ? 5 : 1
  );

  // r140: EXTERNAL FEED — UnifiedWorkspace's direct pipeline streams slides
  // into this workspace so the user watches the deck fill like a normal run
  // (they previously stared at a bare chat for the whole generation).
  const externalFeedRef = useRef(false);
  // r144: deck-level manual edit mode — one header button drives every
  // slide editor; the style sidebar stays open for the whole session.
  const [deckEditMode, setDeckEditMode] = useState(false);
  const deckEditModeRef = useRef(false);
  useEffect(() => {
    if (!existingProject?.externalFeed) return;
    externalFeedRef.current = true;
    setSlideCount(existingProject.externalFeedTotal || 0);
    setGeneratedSlides([]);
    setGenerating(true);
    setGenerationStatusMessage('Designing your slides...');
    setGenerationProgress(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Content input state
  const [slides, setSlides] = useState([
    { id: 1, title: '', content: '', bulletPoints: [] }
  ]);
  const [presentationTitle, setPresentationTitle] = useState(existingProject?.title || '');
  const [slideCount, setSlideCount] = useState(5);
  const [inputMode, setInputMode] = useState('structured'); // 'structured', 'raw', or 'prompt'
  const [rawContent, setRawContent] = useState('');
  const [enhanceText, setEnhanceText] = useState(true); // Toggle for text enhancement
  const [importMode, setImportMode] = useState(null); // null, 'file', or 'url' - for inline import in paste content
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [excelData, setExcelData] = useState(null); // { sheets: [...], filename: '...' }
  const [redesignImage, setRedesignImage] = useState(null); // base64 data URL of uploaded slide image
  const [keepSourceDesign, setKeepSourceDesign] = useState(false); // r176: reproduce the original's identity
  const [embedImages, setEmbedImages] = useState(null); // [{slot_id,data_url,name,description}] — user images placed verbatim on slides
  const contentFileInputRef = useRef(null);

  // Prompt-based generation state
  const [topicPrompt, setTopicPrompt] = useState('');
  const [webResearchContext, setWebResearchContext] = useState('');
  const [generatedOutline, setGeneratedOutline] = useState(null);
  const [outlineLoading, setOutlineLoading] = useState(false);
  const [outlineFromEnhance, setOutlineFromEnhance] = useState(false); // Track if outline came from enhanced text mode
  const [textStyle, setTextStyle] = useState('preserve'); // 'condense' = Minimal, 'preserve' = Essential, 'enhance' = Detailed
  const [contentTone, setContentTone] = useState('professional'); // 'professional', 'casual', 'academic'
  const [contentLanguage, setContentLanguage] = useState('en');
  const [aiInstructions, setAiInstructions] = useState('');
  const [llmProvider, setLlmProvider] = useState('openai'); // 'openai' or 'gemini'

  // Edit state
  const [editPrompt, setEditPrompt] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSlides, setEditingSlides] = useState(false);
  const [editScope, setEditScope] = useState('all'); // 'all' or 'current'
  const [highlightRegion, setHighlightRegion] = useState(null); // {x, y, width, height} as % of slide
  const [isDrawingRegion, setIsDrawingRegion] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [referenceSlideIndices, setReferenceSlideIndices] = useState([]);
  const editSlidePreviewRef = useRef(null);
  const [regeneratingSlide, setRegeneratingSlide] = useState(false);
  const [editModeTab, setEditModeTab] = useState('prompt'); // 'prompt' or 'manual'

  // HTML slide chat edit state
  const [htmlChatHistory, setHtmlChatHistory] = useState([]); // [{role, content, slideIndex?, timestamp}]
  const [htmlChatInput, setHtmlChatInput] = useState('');
  const [htmlChatLoading, setHtmlChatLoading] = useState(false);
  const [htmlChatImages, setHtmlChatImages] = useState([]); // attached reference images (base64)
  const [htmlRegionMode, setHtmlRegionMode] = useState(false); // region selection toggle
  const [htmlApplyAll, setHtmlApplyAll] = useState(false); // apply edit to all slides toggle
  const [htmlEditModel, setHtmlEditModel] = useState('flash'); // 'flash' or 'pro' for chat edits
  const [htmlHighlightRegion, setHtmlHighlightRegion] = useState(null);
  const htmlChatScrollRef = useRef(null);
  const htmlChatFileRef = useRef(null);
  const htmlSlideEditorRef = useRef(null);
  const [selectedCssElement, setSelectedCssElement] = useState(null);
  const [rightPanelTab, setRightPanelTab] = useState('chat'); // 'chat' | 'style' | 'verify'
  const [showDriveFactVerifier, setShowDriveFactVerifier] = useState(false);

  // Variation passthrough
  const [variationStyleRef, setVariationStyleRef] = useState('');
  const [variationDirection, setVariationDirection] = useState(null); // { name, description, colors }
  const [preGeneratedVariationSlides, setPreGeneratedVariationSlides] = useState([]);
  const [variationSampleIndices, setVariationSampleIndices] = useState([]);

  // Brand identity — ref mirrors state to avoid stale closure in auto-triggered generation
  const [brandIdentityId, setBrandIdentityId] = useState(existingProject?.metadata?.brand_identity_id || null);
  // Template-guided generation (T1): saved deck template driving the design.
  const deckTemplateIdRef = useRef(existingProject?.metadata?.deck_template_id || null);
  const brandIdentityIdRef = useRef(existingProject?.metadata?.brand_identity_id || null);

  // Early slide-1 pipelining: handed over from UnifiedWorkspace via
  // flowConfig.earlyFirstSlide ({ promise, status, result, outlineKey,
  // controller }). Consumed once in handleGenerateWithHTML.
  const earlyFirstSlideRef = useRef(null);

  // Fact-check a slide's text content
  const handleFactCheck = async (slideIdx, text) => {
    onChatMessage?.({ text: `Fact-checking slide ${slideIdx + 1}...`, isStatus: true });
    try {
      const response = await fetch(withBackend('/api/fact-check'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, slide_index: slideIdx }),
      });
      if (!response.ok) throw new Error('Fact-check request failed');
      const result = await response.json();
      onChatMessage?.({ text: result.summary || 'Fact-check complete.', isFactCheck: true, claims: result.claims || [], slideIndex: slideIdx });
    } catch (err) {
      onChatMessage?.(`Fact-check failed: ${err.message}`);
    }
  };

  // QA slide: vision model scans for visual defects (overlap, overflow,
  // misalignment, bad contrast). Shows issues in the chat; the user can
  // confirm and the fixes get applied via /edit-html-slide.
  const handleQaSlide = async (slideIdx, html_source, preview_url) => {
    toast.loading('Scanning slide for visual issues…', { id: 'qa-slide' });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2-min hard cap
    try {
      const resp = await fetch(withBackend('/api/qa-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html_source, slide_index: slideIdx, preview_url: preview_url || '' }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const issues = data.issues || [];
      if (issues.length === 0) {
        toast.success('No issues detected — slide looks clean.', { id: 'qa-slide' });
        const cleanMsg = `✨ Slide ${slideIdx + 1}: No issues found. Slide looks clean.`;
        setHtmlChatHistory(prev => [...prev, {
          role: 'bot', content: cleanMsg, timestamp: Date.now(), slideIndex: slideIdx,
        }]);
        onChatMessage?.({ text: cleanMsg, slideIndex: slideIdx });
        return;
      }
      toast.success(`Found ${issues.length} issue${issues.length > 1 ? 's' : ''}`, { id: 'qa-slide' });
      const headline = `QA found ${issues.length} issue${issues.length > 1 ? 's' : ''} on slide ${slideIdx + 1}.`;
      setHtmlChatHistory(prev => [...prev, {
        role: 'bot', content: headline, timestamp: Date.now(), slideIndex: slideIdx, qaIssues: issues,
      }]);
      onChatMessage?.({ text: headline, isQa: true, qaIssues: issues, slideIndex: slideIdx });
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('[QA-SLIDE] failed:', err);
      const msg = err.name === 'AbortError' ? 'QA timed out after 2 minutes' : `QA failed: ${err.message}`;
      toast.error(msg, { id: 'qa-slide' });
    }
  };

  // Slide variation: generate an alternative design of the current slide
  // with the same content. Brand-locked. The HtmlSlideEditor previews
  // the result inline with Keep / Discard buttons; Discard snaps back to
  // the original via a normal slide update.
  const handleSlideVariation = async (slideIdx, html_source) => {
    toast.loading('Generating a different design…', { id: 'slide-variation' });
    try {
      const resp = await fetch(withBackend('/api/slide-variation'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          slide_html: html_source,
          html_model: htmlEditModel || 'flash',
          brand_identity_id: brandIdentityId || brandIdentityIdRef.current || '',
          slide_index: slideIdx,
          total_slides: generatedSlides.length,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      toast.success('Variation ready — Keep or Discard.', { id: 'slide-variation', duration: 4000 });
      return data;  // { html, preview_url, slide_index }
    } catch (e) {
      toast.error(`Variation failed: ${e.message}`, { id: 'slide-variation' });
      return null;
    }
  };

  // Apply QA fixes: build a detailed edit prompt from the issues and call
  // the standard slide-edit flow so the LLM regenerates the slide with
  // each specific fix applied.
  const handleApplyQaFixes = async (slideIdx, issues) => {
    if (!issues || issues.length === 0) return;
    // r160b (review P1-1): issues arrive as dicts OR plain strings — a raw
    // string built "[undefined] undefined" into a prompt that still ordered
    // "apply EVERY fix precisely". Normalizing HERE covers every call site
    // (single Fix, Fix all, Apply all).
    const normed = issues.map(it =>
      typeof it === 'string' ? { type: 'issue', description: it } : (it || {}));
    const fixLines = normed.map((it, i) =>
      `${i + 1}. [${it.type || 'issue'}] ${it.description || ''}\n   Suggested fix: ${it.fix || 'Resolve this issue.'}`
    ).join('\n\n');
    const editPrompt = `You are fixing specific visual defects on this slide. Apply EVERY fix below precisely. Do not ignore any item. Do not introduce new issues. Preserve all existing content, styling, and layout except for the specific adjustments required by each fix.\n\nISSUES TO FIX:\n${fixLines}\n\nReturn the full corrected HTML.`;
    setCurrentSlideIndex(slideIdx);
    // Respect the user's edit-model picker (round 67): this used to force
    // modelOverride 'opus', so "Fix all issues" ignored the picked model and
    // could hit the premium gate the user had deliberately routed around.
    await handleHtmlChatSend(editPrompt, { targetSlide: slideIdx });
  };

  // Expose sendEdit, chat controls, and export for external chat mode
  useImperativeHandle(ref, () => ({
    sendEdit: (message, options) => handleHtmlChatSend(message, options),
    sendStructural: (op) => handleStructuralEdit(op),
    // r145: stop an in-flight chat edit / apply-all sweep — checked between
    // iterations and wired to the fetch AbortController.
    cancelChatEdit: () => {
      editCancelRef.current = true;
      try { editAbortRef.current?.abort(); } catch { /* noop */ }
    },
    applyQaFixes: (slideIdx, issues) => handleApplyQaFixes(slideIdx, issues),
    getChatHistory: () => htmlChatHistory,
    present: () => setShowPresentMode(true),
    save: () => handleSaveProject(),
    saveAsTemplate: () => {
      setTemplateNameDraft(presentationTitle || '');
      setShowTemplateModal(true);
    },
    exportImage: () => handleExport('image'),
    // Editable PPTX export now goes through the SVG + Convert-to-Shape
    // pipeline — pixel-perfect rendering via SVG, editability via
    // PowerPoint's native Convert to Shape on the embedded SVG.
    exportEditable: () => handleExportPptxSvg(),
    exportImages: () => handleExportImages(),
    exportPdf: () => handleExportPDF(),
    // r136: the top toolbar (UnifiedWorkspace) opens the teach-brand dialog
    teachBrand: () => {
      if (brandIdentityId || brandIdentityIdRef.current) setTeachBrandOpen(true);
      else toast.info('This deck has no brand attached — Teach brand records rules on a brand.');
    },
    // r160: version checkpoint before risky batch operations (fail-open)
    snapshotVersion: (label) => snapshotVersion(label),
    // r140: external feed — the direct pipeline streams slides in
    adoptExternalSlide: (idx, slide, done, totalCount) => {
      setGeneratedSlides(prev => {
        const updated = [...prev];
        while (updated.length <= idx) updated.push(null);
        updated[idx] = slide;
        return updated;
      });
      setCurrentSlideIndex(idx);
      if (totalCount > 0) {
        setGenerationProgress(Math.round((done / totalCount) * 100));
        setGenerationStatusMessage(`Rendering slide ${done} of ${totalCount}...`);
      }
    },
    // r144: header-driven deck edit mode. Returns the NEW state so the
    // header button can style itself without a mirror poll.
    toggleEditMode: () => {
      if (generating || liveGenerating) {
        toast.info('Wait for generation to finish before editing.');
        return false;
      }
      const next = !deckEditModeRef.current;
      deckEditModeRef.current = next;
      setDeckEditMode(next);
      if (!next) setSelectedCssElement(null);
      return next;
    },
    getEditMode: () => deckEditModeRef.current,
    finishExternalGeneration: () => {
      externalFeedRef.current = false;
      // r140b P0: existingProject initializes hasAutoSavedRef TRUE ("already
      // saved") — but a fed deck was never saved. Without this reset the
      // step-5 autosave never fires and the whole deck is lost on refresh.
      hasAutoSavedRef.current = false;
      setActiveGeneratingIndices([]);
      setGeneratedSlides(prev => {
        const cleaned = prev.filter(s => s !== null && s !== undefined);
        setTimeout(() => { setGenerating(false); setCurrentStep(5); }, 50);
        return cleaned;
      });
    },
    spikeSvgExport: () => handleSpikeSvgExport(),
    getExportState: () => ({ exporting, saving }),
    // Chat control state for UnifiedWorkspace to render buttons
    getChatControls: () => ({
      regionMode: htmlRegionMode,
      applyAll: htmlApplyAll,
      editModel: htmlEditModel,
      hasImages: htmlChatImages.length > 0,
      hasActiveRegion: !!htmlHighlightRegion,
      regionSlideIdx: htmlHighlightRegion?.slideIdx ?? null,
      generationMode,
      currentSlideIndex,
    }),
    setRegionMode: (v) => setHtmlRegionMode(v),
    setApplyAll: (v) => setHtmlApplyAll(v),
    setEditModel: (v) => setHtmlEditModel(v),
    attachImage: () => htmlChatFileRef.current?.click(),
    getFileInputRef: () => htmlChatFileRef.current,
    // Snapshot a slide's current state — used by the chat-level batch
    // revert flow in UnifiedWorkspace to capture pre-batch state.
    // r180: proposal coverage — re-run on demand, and record a fact supplied
    // while filling placeholders (frozen-block exception: appended to the
    // scope AND to special instructions so a later regeneration does not
    // re-emit a placeholder already answered).
    runCoverageAudit: () => runCoverageAudit(),
    recordPostDraftFact: (fact) => {
      const f = String(fact || '').trim();
      if (!f) return;
      setQuestionnaireAnswers(prev => {
        const scope = prev?.proposalScope || {};
        const facts = [...(scope.post_draft_facts || []), f];
        const line = `FACT SUPPLIED AFTER THE DRAFT (authoritative and resolved - compose into finished content, no placeholder): ${f}`;
        return {
          ...prev,
          proposalScope: { ...scope, post_draft_facts: facts },
          specialInstructions: [prev?.specialInstructions, line].filter(Boolean).join('\n'),
        };
      });
    },
    getSlideSnapshot: (slideIdx) => {
      const slide = generatedSlidesRef.current?.[slideIdx];
      if (!slide?.html_source) return null;
      return { slideIdx, uid: slide._uid || null, html_source: slide.html_source, preview_url: slide.preview_url };
    },
    // Restore a batch of pre-edit snapshots in one apply. r169: entries that
    // carry a uid match by IDENTITY (a structural op may have shifted the
    // indices since the snapshot); index matching stays for legacy entries.
    restoreSlideSnapshots: (snapshots, opts = {}) => {
      if (!Array.isArray(snapshots) || snapshots.length === 0) return;
      // r169b (review): count what actually matched — a snapshot of a since-
      // deleted slide must not toast a false success.
      const live = generatedSlidesRef.current || [];
      const matched = snapshots.filter(x =>
        x.uid ? live.some(s => s?._uid === x.uid) : !!live[x.slideIdx]).length;
      if (matched === 0) {
        toast.error('That slide no longer exists — nothing to restore.');
        return;
      }
      setGeneratedSlides(curr => curr.map((s, i) => {
        const snap = snapshots.find(x => (x.uid ? s?._uid === x.uid : x.slideIdx === i));
        return snap ? { ...s, html_source: snap.html_source, preview_url: snap.preview_url, _needsRescreenshot: !snap.preview_url } : s;
      }));
      setHasUnsavedEdits(true);
      toast.success(`${opts.verb || 'Reverted'} ${matched} slide${matched !== 1 ? 's' : ''}`, { duration: 2000 });
    },
    share: () => setShowShareModal(true),
    getShareData: () => ({
      contentType: 'presentation',
      title: presentationTitle || 'Presentation',
      slidesData: generatedSlides.filter(Boolean).map(s => ({ html_source: s.html_source, preview_url: s.preview_url, title: s.title })),
    }),
    getData: () => ({
      title: presentationTitle || 'Presentation',
      type: 'presentation',
      slides: generatedSlides.filter(Boolean).map((s, i) => {
        // Extract text from HTML for context
        let textContent = '';
        if (s.html_source) {
          const div = document.createElement('div');
          div.innerHTML = s.html_source.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
          textContent = (div.textContent || '').replace(/\s+/g, ' ').trim();
        }
        return {
          index: i + 1,
          title: s.title || `Slide ${i + 1}`,
          content: textContent,
          html_source: s.html_source || '', // Include HTML for design/color analysis
          // r146b P0: without these the parent's uid resolution and the
          // cross-slide screenshots were dead on arrival
          _uid: s._uid || null,
          preview_url: s.preview_url || '',
        };
      }),
    }),
  }));

  // Brand guidelines state
  const [brandGuidelines, setBrandGuidelines] = useState(existingProject?.brandGuidelines || null);
  const [useBrandGuidelines, setUseBrandGuidelines] = useState(false); // Disabled - use brand reference upload instead
  const [loadingBrandGuidelines, setLoadingBrandGuidelines] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null); // Uploaded logo URL for slide injection
  const [templateFile, setTemplateFile] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);

  // Brand reference state
  const [brandReferences, setBrandReferences] = useState({ images: [], weight: 'medium' });

  // Template mode state
  const [generationMode, setGenerationMode] = useState(() => {
    if (existingProject?.metadata?.generation_mode) return existingProject.metadata.generation_mode;
    if (existingProject?.slides?.some(s => s.extractedLayers?.source === 'template')) return 'templates';
    return 'ai';
  }); // 'ai', 'templates', 'pptx', or 'video'
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const themeId = existingProject?.metadata?.theme_id;
    if (themeId) return themes.find(t => t.id === themeId) || null;
    return null;
  });
  const [selectedPPTXTemplate, setSelectedPPTXTemplate] = useState(null);

  // Questionnaire state
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({
    audience: null, presentationType: null, visualStyle: null, includeImages: null, imageStyle: null
  });

  // AI Style preset state (for Nano Banana Pro mode)
  const [aiStylePreset, setAiStylePreset] = useState('ai-create'); // Default to AI-created theme
  const [customThemeRequest, setCustomThemeRequest] = useState(''); // Free-text theme request from questionnaire

  // Video generation state
  const [videoOptions, setVideoOptions] = useState({
    format: 'landscape', // 'landscape', 'portrait', 'square'
    quality: 'standard', // 'preview', 'standard', 'high'
    slideDuration: 5,
    animationStyle: 'modern', // 'modern', 'minimal', 'dynamic', 'corporate'
  });
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState(existingProject?.slides || []);
  // Per-slide undo history. Map of slideIdx -> [{html_source, preview_url}, ...].
  // Capped at 10 snapshots per slide. Pushed only by chat-driven edits, NOT
  // by inline text edits — so the user can fix-then-undo a chat instruction
  // without losing their typing.
  const [slideHistory, setSlideHistory] = useState({});
  // r169: per-slide REDO stack (same uid keys as slideHistory). Undo pushes
  // the pre-undo state here; any fresh edit clears the slide's redo branch.
  const [slideRedo, setSlideRedo] = useState({});
  const generatedSlidesRef = useRef(generatedSlides);
  useEffect(() => { generatedSlidesRef.current = generatedSlides; }, [generatedSlides]);

  // r145: STABLE SLIDE IDS. Undo history, edit-result writes, and batch
  // snapshots were all keyed by array index — a delete/duplicate mid-flight
  // shifted every later index and undo pasted the wrong slide's content.
  // Lazy backfill covers every entry path (generation, restore, feed, add).
  const genUid = () => (window.crypto?.randomUUID?.() || `u${Date.now()}_${Math.random().toString(36).slice(2)}`);
  useEffect(() => {
    setGeneratedSlides(prev => prev.some(s => s && !s._uid)
      ? prev.map(s => (s && !s._uid) ? { ...s, _uid: genUid() } : s)
      : prev);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedSlides]);
  const uidAt = (idx) => generatedSlidesRef.current?.[idx]?._uid || `i${idx}`;
  // r145: monotonic message ids — Date.now() collided within a millisecond
  // and progress updates overwrote the user's own bubble.
  const msgSeqRef = useRef(Date.now());
  const nextMsgId = () => ++msgSeqRef.current;
  // r145: cancel plumbing for chat edits (there was NO way to stop one).
  const editCancelRef = useRef(false);
  const editAbortRef = useRef(null);
  // r145: which slide the edit loop is ACTUALLY working on — the overlay
  // used currentSlideIndex, so apply-all showed "Redesigning" on one card
  // while every other slide was being rewritten invisibly.
  const [activeEditIdx, setActiveEditIdx] = useState(null);

  // Live-generation polling state. The polling EFFECT itself is defined
  // lower down, after projectId / isSaveUpdateRef are declared — placing
  // the effect here would evaluate its [projectId] dependency array
  // during render before projectId exists (temporal dead zone crash).
  const [liveGenerating, setLiveGenerating] = useState(false);
  const [liveTotal, setLiveTotal] = useState(0);
  const livePollRef = useRef(null);

  // ── Undo history helpers ──
  // Defined here (after slideHistory + generatedSlidesRef are declared) so
  // their dependency arrays don't hit a TDZ during the render's variable
  // initialization phase.
  const recordSlideHistory = useCallback((slideIdx, explicitSnapshot = null, tag = null, uidOverride = null, keepRedo = false) => {
    // If explicitSnapshot is provided, store that. Otherwise capture the
    // slide's current state at this moment. Variation flows pass the
    // pre-variation original so an undo after Keep reverts cleanly.
    const snap = explicitSnapshot || (() => {
      // r146b: a mid-flight splice shifts indices — when the caller captured
      // a uid, snapshot THAT slide, wherever it lives now.
      const arr = generatedSlidesRef.current || [];
      const slide = uidOverride ? arr.find(s => s?._uid === uidOverride) : arr[slideIdx];
      if (!slide || !slide.html_source) return null;
      return { html_source: slide.html_source, preview_url: slide.preview_url };
    })();
    if (!snap?.html_source) return;
    const histKey = uidOverride || generatedSlidesRef.current?.[slideIdx]?._uid || `i${slideIdx}`; // r145: uid key
    // r169: a NEW edit invalidates the redo branch (classic undo/redo).
    // The redo flow itself records history with keepRedo=true.
    if (!keepRedo) {
      setSlideRedo(prev => (prev[histKey]?.length ? { ...prev, [histKey]: [] } : prev));
    }
    setSlideHistory(prev => {
      const stack = prev[histKey] || [];
      // r118: collapse a run of MANUAL edits into a single snapshot. Recording
      // one per drag would fill the 10-deep stack in a single session and
      // evict the pre-chat snapshot, making the AI edit un-undoable. One entry
      // per manual session is what a user means by "undo my hand edits".
      if (tag === 'manual' && stack.length && stack[stack.length - 1].tag === 'manual') {
        return prev;
      }
      const newStack = [...stack, { ...snap, ts: Date.now(), tag }].slice(-10);
      return { ...prev, [histKey]: newStack };
    });
  }, []);

  const undoSlideEdit = useCallback((slideIdx) => {
    const histKey = generatedSlidesRef.current?.[slideIdx]?._uid || `i${slideIdx}`; // r145: uid key
    const stack = slideHistory[histKey] || [];
    if (stack.length === 0) return;
    const last = stack[stack.length - 1];
    // r169: the state being undone becomes redoable.
    const cur = (generatedSlidesRef.current || []).find(s => s?._uid === histKey)
      || generatedSlidesRef.current?.[slideIdx];
    if (cur?.html_source) {
      setSlideRedo(prev => ({
        ...prev,
        [histKey]: [...(prev[histKey] || []), { html_source: cur.html_source, preview_url: cur.preview_url }].slice(-10),
      }));
    }
    setGeneratedSlides(curr => curr.map((s, i) =>
      (s?._uid ? s._uid === histKey : i === slideIdx)
        ? { ...s, html_source: last.html_source, preview_url: last.preview_url, _needsRescreenshot: false } : s
    ));
    setSlideHistory(prev => ({ ...prev, [histKey]: stack.slice(0, -1) }));
    setHasUnsavedEdits(true);
    toast.success(`Reverted slide ${slideIdx + 1}`, { duration: 2000 });
  }, [slideHistory]);

  // r169: redo — restores the state a prior undo stepped away from. The
  // pre-redo state goes back onto the UNDO stack (keepRedo, or this very
  // redo would erase its own remaining branch).
  const redoSlideEdit = useCallback((slideIdx) => {
    const histKey = generatedSlidesRef.current?.[slideIdx]?._uid || `i${slideIdx}`;
    const stack = slideRedo[histKey] || [];
    if (stack.length === 0) return;
    const nextState = stack[stack.length - 1];
    const cur = (generatedSlidesRef.current || []).find(s => s?._uid === histKey)
      || generatedSlidesRef.current?.[slideIdx];
    if (cur?.html_source) {
      recordSlideHistory(slideIdx, { html_source: cur.html_source, preview_url: cur.preview_url }, null, null, true);
    }
    setGeneratedSlides(curr => curr.map((s, i) =>
      (s?._uid ? s._uid === histKey : i === slideIdx)
        ? { ...s, html_source: nextState.html_source, preview_url: nextState.preview_url, _needsRescreenshot: !nextState.preview_url } : s
    ));
    setSlideRedo(prev => ({ ...prev, [histKey]: stack.slice(0, -1) }));
    setHasUnsavedEdits(true);
    toast.success(`Redid edit on slide ${slideIdx + 1}`, { duration: 2000 });
  }, [slideRedo, recordSlideHistory]);

  // r180: PROPOSAL COVERAGE AUDIT (spec §6). Fire-and-forget after generation
  // completes and after edits settle: digest the deck (visible text + an
  // explicit placeholder flag per slide), ask the judge for one verdict per
  // RFP requirement, and post a coverage card to the chat. Snapshot guard:
  // the report is discarded if the deck changed while the judge ran. Fails
  // open: a transport hiccup means no report, never a fabricated gap.
  const coverageSigRef = useRef('');
  const _deckSignature = () => (generatedSlidesRef.current || []).filter(Boolean).map(s => (s?.html_source || '').length).join(',');
  const runCoverageAudit = useCallback(async () => {
    const scope = questionnaireAnswers?.proposalScope;
    const reqs = scope?.requirements || [];
    if (!questionnaireAnswers?.proposalMode || !reqs.length) return;
    const slides = (generatedSlidesRef.current || []).filter(Boolean);
    if (!slides.length) return;
    const sig = _deckSignature();
    if (sig === coverageSigRef.current) return; // nothing changed since the last report
    const digest = slides.map((s, i) => {
      let text = '';
      try {
        const d = document.createElement('div');
        d.innerHTML = (s.html_source || '').replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
        text = (d.textContent || '').replace(/\s+/g, ' ').trim();
      } catch { /* digest stays empty for this slide */ }
      return {
        index: i, title: s.title || '', text: text.slice(0, 1500),
        // deterministic detection of OUR OWN injected placeholder artifact
        has_placeholder: /wiz-placeholder|\[PLACEHOLDER/i.test(s.html_source || ''),
      };
    });
    try {
      const resp = await fetch(withBackend('/api/proposal/coverage-audit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ requirements: reqs, slides: digest }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (_deckSignature() !== sig) return; // deck moved on — stale report
      coverageSigRef.current = sig;
      const items = data.items || [];
      const open = items.filter(it => it.status !== 'covered');
      const headline = open.length
        ? `Coverage check: ${open.length} of ${reqs.length} RFP requirements still need attention`
        : `Coverage check: all ${reqs.length} RFP requirements are addressed`;
      onChatMessage?.({ text: headline, isCoverage: true, coverageItems: items, coverageRequirements: reqs });
    } catch { /* fail-open: no report */ }
  }, [questionnaireAnswers, onChatMessage]);

  useEffect(() => {
    if (!questionnaireAnswers?.proposalMode || !questionnaireAnswers?.proposalScope?.requirements?.length) return;
    if (liveGenerating || generating || currentStep !== 5 || generatedSlides.length === 0) return;
    const t = setTimeout(() => { runCoverageAudit(); }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, liveGenerating, generating, generatedSlides]);
  // r120: which slide cards are actually on screen. Off-screen cards render a
  // static preview instead of a live editing iframe.
  const [visibleSlideIdx, setVisibleSlideIdx] = useState(() => new Set([0]));
  const slideCardRefs = useRef(new Map());
  const slideObsRef = useRef(null);

  const registerSlideCard = useCallback((idx, node) => {
    const map = slideCardRefs.current;
    const prev = map.get(idx);
    if (prev && slideObsRef.current) slideObsRef.current.unobserve(prev);
    if (node) {
      map.set(idx, node);
      if (slideObsRef.current) slideObsRef.current.observe(node);
    } else {
      map.delete(idx);
    }
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      // No observer (old browser, jsdom): keep every slide live. Degrading to
      // the old behaviour is correct — slower, never broken.
      setVisibleSlideIdx(new Set(Array.from({ length: 512 }, (_, i) => i)));
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      setVisibleSlideIdx((prev) => {
        const next = new Set(prev);
        let changed = false;
        entries.forEach((e) => {
          const i = Number(e.target.getAttribute('data-slide-idx'));
          if (Number.isNaN(i)) return;
          if (e.isIntersecting) {
            if (!next.has(i)) { next.add(i); changed = true; }
          } else if (next.has(i)) { next.delete(i); changed = true; }
        });
        return changed ? next : prev;
      });
    }, {
      // A generous margin so a card is live well before it scrolls in: the
      // editor injects on mount and a visible flip would be worse than the
      // cost it saves.
      root: null, rootMargin: '600px 0px', threshold: 0.01,
    });
    slideObsRef.current = obs;
    slideCardRefs.current.forEach((node) => { if (node) obs.observe(node); });
    return () => { obs.disconnect(); slideObsRef.current = null; };
  }, []);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  // r145/r151: a region belongs to the slide it was drawn on — navigating
  // away clears it. MUST sit BELOW the currentSlideIndex declaration: dep
  // arrays are evaluated during render, and referencing a const declared
  // later is a temporal-dead-zone ReferenceError that white-screened EVERY
  // presentation mount in production (29/08).
  useEffect(() => {
    setHtmlHighlightRegion(prev =>
      (prev && prev.slideIdx != null && prev.slideIdx !== currentSlideIndex) ? null : prev);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex]);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatusMessage, setGenerationStatusMessage] = useState('');
  const [activeGeneratingIndices, setActiveGeneratingIndices] = useState([]); // Slide indices currently being generated in parallel
  // Cancel support: the background job id for the in-flight generation, plus a
  // flag so the Cancel button can show a pending state once clicked.
  const currentJobIdRef = useRef(null);
  // r137: cancel must work BEFORE the job id exists (early-slide handoff +
  // job POST take tens of seconds) and on the direct-stream fallback, which
  // never has a job. The ref survives closures; the controller kills the
  // stream fetch.
  const cancelRequestedRef = useRef(false);
  const streamAbortRef = useRef(null);
  // r138b #2: the placeholder project row the job POST created for THIS
  // mount's generation — a retry after a 0-slide stop reuses it instead of
  // inserting a duplicate row. Deliberately a separate ref from projectId
  // state: loaded/saved projects must never be reused as placeholders.
  const retryProjectIdRef = useRef(null);
  const [cancelRequested, setCancelRequested] = useState(false);

  // Turn-into-template modal (T1)
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateNameDraft, setTemplateNameDraft] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const handleSaveAsTemplate = async () => {
    if (!projectId) {
      toast.error('Save the deck first, then turn it into a template');
      return;
    }
    const name = templateNameDraft.trim() || presentationTitle || 'Untitled template';
    setSavingTemplate(true);
    try {
      const resp = await fetch(withBackend('/api/deck-templates'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, project_id: projectId, name }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      toast.success(`Template "${data.template?.name || name}" saved`);
      setShowTemplateModal(false);
    } catch (e) {
      toast.error(e.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState('image'); // 'image' or 'editable'

  // Structured slide model (Phase 1 test) — experimental
  const [structuredTesting, setStructuredTesting] = useState(false);
  const [structuredModel, setStructuredModel] = useState(null);
  const [structuredError, setStructuredError] = useState(null);
  const [showStructuredModal, setShowStructuredModal] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState(existingProject?.projectId || null); // UUID from projects table
  const [pptProjectId, setPptProjectId] = useState(existingProject?.pptProjectId || existingProject?.metadata?.ppt_project_id || null); // ppt_xxx from ppt_projects table
  const [lastSaved, setLastSaved] = useState(null);
  const autosaveTimerRef = useRef(null);
  const hasAutoSavedRef = useRef(!!existingProject); // skip first autosave for existing projects (already saved)
  const isSaveUpdateRef = useRef(false); // prevents save → setGeneratedSlides → autosave loop
  // Round 61: job-path decks skip the mid-flight autosave (the job persists
  // slides itself), but the CLIENT-only metadata (brand id, ppt mirror,
  // onComplete card swap) still needs one save — this flag requests it and
  // an effect fires it once generation has actually finished.
  const [baselineSaveDue, setBaselineSaveDue] = useState(false);
  const baselineRetryRef = useRef(0); // r152: cap the deferred-save retry loop

  // ── Collaboration (Phase 0+1) ──────────────────────────────────────────
  // revRef: optimistic-locking revision for versioned saves. null = unknown;
  // fetched lazily on the first versioned save (covers cache-hit loads and
  // fresh generations without threading rev through every load path).
  const revRef = useRef(existingProject?.rev ?? null);
  const [conflictInfo, setConflictInfo] = useState(null); // save rejected: newer rev exists
  const [remoteUpdate, setRemoteUpdate] = useState(null); // a peer saved while we're open
  // Phase 2: per-slide saves. Timers read the existing generatedSlidesRef
  // mirror (declared above) at fire time; dirty set tracks locally-edited
  // slide indices; one debounce timer per slide.
  const dirtySlidesRef = useRef(new Set());
  const slideSaveTimersRef = useRef({});
  const perSlideUnavailableRef = useRef(false);

  // A peer atomically saved ONE slide: hot-swap it into our deck unless we
  // have local unsaved edits on that same slide (then it's a real overlap —
  // fall back to the banner and let the human decide).
  const handleRemoteSlideUpdate = async ({ index, by }) => {
    if (!projectId || dirtySlidesRef.current.has(index)) {
      setRemoteUpdate({ by: by || 'A teammate' });
      return;
    }
    try {
      const { data } = await supabaseHelpers.getProjectFull(projectId);
      const remote = data?.analysis_data?.slides?.[index];
      if (remote) {
        isSaveUpdateRef.current = true;
        setGeneratedSlides(prev => prev.map((s, i) => (i === index ? remote : s)));
        if (data?.rev != null) revRef.current = data.rev;
        toast(`${by || 'A teammate'} updated slide ${index + 1}`, { duration: 2500 });
      }
    } catch (_) {
      setRemoteUpdate({ by: by || 'A teammate' });
    }
  };

  const { peers, setEditingSlide, broadcastDeckUpdated, broadcastSlideUpdated } = useProjectPresence({
    projectId,
    userId,
    onRemoteUpdate: (p) => {
      if (p?.kind === 'slide' && Number.isInteger(p.index)) handleRemoteSlideUpdate(p);
      else setRemoteUpdate({ by: p?.by || 'A teammate' });
    },
  });

  // Debounced atomic save of ONE slide (2s after the last edit to it). Falls
  // back silently to the whole-deck autosave when the backend/migration
  // isn't ready. Reads the slide fresh at fire time so the re-screenshot
  // update (which lands after the edit) is included.
  const schedulePerSlideSave = (idx) => {
    if (!projectId || perSlideUnavailableRef.current) return;
    // While the backend job is writing this project, a per-slide save would
    // report success and then be destroyed by the job's next cumulative
    // PATCH (the backend also refuses it with generation_in_progress).
    if (liveGenerating) return;
    dirtySlidesRef.current.add(idx);
    if (slideSaveTimersRef.current[idx]) clearTimeout(slideSaveTimersRef.current[idx]);
    slideSaveTimersRef.current[idx] = setTimeout(async () => {
      delete slideSaveTimersRef.current[idx];
      const slide = generatedSlidesRef.current[idx];
      if (!slide) return;
      try {
        const [processed] = await supabaseHelpers.processSlidesForSave(userId, pptProjectId || projectId, [slide]);
        const resp = await fetch(withBackend(`/api/projects/${projectId}/slides/${idx}/save`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, slide: processed }),
        });
        const result = await resp.json().catch(() => ({}));
        if (resp.status === 501) {
          perSlideUnavailableRef.current = true; // migration not run — whole-deck saves cover it
          return;
        }
        if (!resp.ok) return; // whole-deck autosave remains the safety net
        dirtySlidesRef.current.delete(idx);
        if (result.rev != null) revRef.current = result.rev;
        broadcastSlideUpdated(idx, result.rev);
      } catch (_) { /* whole-deck autosave remains the safety net */ }
    }, 2000);
  };

  // Clear any pending per-slide timers on unmount.
  useEffect(() => () => {
    Object.values(slideSaveTimersRef.current).forEach(t => clearTimeout(t));
  }, []);

  // Pull the latest saved deck (peer's version wins; local unsaved edits are
  // discarded — the banner says so before the user clicks).
  const refreshFromRemote = async () => {
    if (!projectId) return;
    try {
      const { data } = await supabaseHelpers.getProjectFull(projectId);
      const remoteSlides = data?.analysis_data?.slides;
      if (Array.isArray(remoteSlides) && remoteSlides.length) {
        isSaveUpdateRef.current = true;
        setGeneratedSlides(remoteSlides);
      }
      if (data?.rev != null) revRef.current = data.rev;
      dirtySlidesRef.current.clear(); // local edits were just replaced
      setRemoteUpdate(null);
      setConflictInfo(null);
      toast.success('Deck refreshed with the latest saved version');
    } catch (e) {
      toast.error('Could not refresh the deck');
    }
  };

  // ── Live generation polling ──
  // When a project is opened while still generating (Claude link, or a
  // refresh mid-generation), the workspace mounts with whatever slides
  // existed at that instant and would otherwise never update — leaving a
  // blank slide area. Poll the project row every 3s, merge slides in as
  // the background job persists them, and show the "still generating"
  // banner. Stops once the project flips to completed/failed. Defined
  // HERE (after projectId + isSaveUpdateRef) so the [projectId] dep array
  // doesn't hit a temporal-dead-zone crash during render.
  useEffect(() => {
    if (!projectId) return undefined;
    if (existingProject?.externalFeed) return undefined; // r149: the feed tab OWNS the run — polling would clobber the stream with the DB placeholder
    const meta = existingProject?.metadata || {};
    const startedGenerating = meta.generating === true
      || (existingProject?.slides?.length || 0) === 0;
    if (!startedGenerating) return undefined;

    let cancelled = false;
    setLiveGenerating(true);

    const poll = async () => {
      try {
        const { data, error } = await supabaseHelpers.getProjectFull(projectId);
        if (cancelled) return;
        if (!error && data) {
          // r149: direct-feed placeholders pre-size the array with nulls —
          // show only the slides that actually exist.
          const slides = (data.analysis_data?.slides || []).filter(Boolean);
          const total = data.total_slides || data.slide_count || slides.length || 0;
          setLiveTotal(total);
          // The backend tags the project row with its background job id.
          // Adopt it so Cancel works from a reopened tab too — without this
          // the job id only ever existed in the tab that started the run.
          // ('cancel:'-prefixed values are the cross-worker cancel sentinel,
          // not a usable job id.)
          if (data.job_id && !String(data.job_id).startsWith('cancel:') && !currentJobIdRef.current) {
            currentJobIdRef.current = data.job_id;
          }
          if (slides.length > 0) {
            isSaveUpdateRef.current = true; // suppress autosave echo
            setGeneratedSlides(slides);
            if (currentStep !== 5) setCurrentStep(5);
          }
          if (data.status === 'completed' || data.status === 'failed') {
            setLiveGenerating(false);
            // Round 61: the mid-flight autosave skip means the client-side
            // metadata was never saved — request the deferred baseline save
            // now that the job is done and the backend guard will pass.
            if (currentJobIdRef.current) setBaselineSaveDue(true);
            return; // stop — no reschedule
          }
          // r152: a direct-feed run has no server-side job — if the owning
          // tab died, nothing ever flips the status and this poller would
          // spin forever with the deck stuck read-only (review P1). A stale
          // heartbeat means the run is dead: finish the row as its owner;
          // the NEXT poll observes the flip and stops normally.
          const _fm = data.analysis_data?.metadata || {};
          if (_fm.direct_pipeline && data.status === 'generating') {
            const _hbMs = Number(_fm.job_heartbeat || 0) * 1000;
            const _startMs = data.created_at ? new Date(data.created_at).getTime() : Date.now();
            const _last = _hbMs || _startMs;
            if (Date.now() - _last > (_hbMs ? 300000 : 600000)) {
              try {
                await fetch(withBackend(`/api/projects/direct-feed/${projectId}`), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
                  body: JSON.stringify({ op: 'finish', user_id: userId || '' }),
                });
              } catch (_) { /* transient — the next poll retries */ }
            }
          }
        }
      } catch (_) { /* transient — keep polling */ }
      if (!cancelled) livePollRef.current = setTimeout(poll, 3000);
    };
    poll();

    return () => {
      cancelled = true;
      if (livePollRef.current) clearTimeout(livePollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Orchestration context for edit/add calls — style anchor + theme + brand
  const deckThemeRef = useRef(null); // cached deck_theme_summary (extracted once per generation)
  const getOrchestrationCtx = useCallback(() => {
    const slides = generatedSlides || [];
    const anchor = slides.find(s => s?.html_source);
    return {
      style_anchor_screenshot: anchor?.preview_url || '',
      deck_theme_summary: deckThemeRef.current || null,
      brand_identity_id: brandIdentityId || brandIdentityIdRef.current || '',
    };
  }, [generatedSlides, brandIdentityId]);

  // Autosave: trigger on first generation complete (step → 5) and after edits
  useEffect(() => {
    // Autosave when generation completes and we land on the review step
    if (currentStep === 5 && generatedSlides.length > 0 && userId && !saving) {
      if (!hasAutoSavedRef.current) {
        hasAutoSavedRef.current = true;
        // Job-path decks are persisted server-side by the job itself, and
        // the live poller can flip to step 5 MID-generation — saving the
        // client's 1-slide snapshot here overwrote a completed 5-slide
        // project (round 61 incident). Mark the baseline as saved (so the
        // edit autosave still arms) but skip the redundant, dangerous write.
        if (!liveGenerating) handleSaveProject();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Deferred baseline save (round 61): fires ONCE after a job-path
  // generation ends — persists brand/theme metadata, mirrors ppt_projects
  // and triggers onComplete, everything the skipped mid-flight autosave
  // used to do. Reads fresh state via effect deps (a setTimeout from the
  // poller's closure would capture the mount-time empty slides array).
  useEffect(() => {
    if (!baselineSaveDue || liveGenerating || saving) return;
    if (generatedSlides.length === 0 || !userId) return;
    setBaselineSaveDue(false);
    handleSaveProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineSaveDue, liveGenerating, saving, generatedSlides.length]);

  // Autosave after edits (debounced 5s) — triggers when slides change on review step
  useEffect(() => {
    if (isSaveUpdateRef.current) { isSaveUpdateRef.current = false; return; }
    // Don't autosave while the backend job is still writing slides — the
    // poller is pulling its writes in; saving here would race the job.
    if (liveGenerating) return;
    if (currentStep !== 5 || !hasAutoSavedRef.current || generatedSlides.length === 0 || !userId || saving) return;
    // Clear previous timer
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      if (!saving) {
        handleSaveProject();
      }
    }, 5000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedSlides]);

  // Screenshot recovery: if any slides have html_source but no preview_url
  // (backend Playwright failed/timed out), request screenshots now.
  const screenshotRecoveryDone = useRef(false);
  useEffect(() => {
    if (currentStep !== 5 || generating || screenshotRecoveryDone.current) return;
    const missing = generatedSlides
      .map((s, i) => (s?.html_source && !s.preview_url ? i : -1))
      .filter(i => i >= 0);
    if (missing.length === 0) return;
    screenshotRecoveryDone.current = true;
    console.log(`[SCREENSHOT-RECOVERY] ${missing.length} slides missing preview_url, requesting screenshots...`);

    (async () => {
      for (const idx of missing) {
        try {
          const resp = await fetch(withBackend('/screenshot-html'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html_source: generatedSlides[idx].html_source }),
          });
          if (resp.ok) {
            const { preview_url } = await resp.json();
            if (preview_url) {
              setGeneratedSlides(prev => prev.map((s, i) =>
                i === idx ? { ...s, preview_url } : s
              ));
              console.log(`[SCREENSHOT-RECOVERY] Slide ${idx + 1} screenshot recovered`);
            }
          }
        } catch (err) {
          console.warn(`[SCREENSHOT-RECOVERY] Slide ${idx + 1} failed:`, err);
        }
      }
    })();
    // r160b: generatedSlides is a dep so a RESTORE (which blanks previews
    // and resets the once-guard) re-triggers recovery in-session — the
    // once-guard keeps the loop from re-running on ordinary slide edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, generating, generatedSlides]);

  // Theme customization state
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const [showPresentMode, setShowPresentMode] = useState(false);

  // ── Brand fidelity & consistency check ──
  const [brandFidelity, setBrandFidelity] = useState(null);
  const [showBrandFidelity, setShowBrandFidelity] = useState(false);
  const [brandFidelityLoading, setBrandFidelityLoading] = useState(false);

  const runBrandFidelity = async () => {
    const slides = generatedSlidesRef.current || [];
    if (!slides.length) { toast.error('No slides to check yet'); return; }
    setShowBrandFidelity(true);
    setBrandFidelityLoading(true);
    try {
      const resp = await fetch(withBackend(`/api/projects/${projectId || 'unsaved'}/brand-fidelity`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || '',
          brand_identity_id: brandIdentityId || brandIdentityIdRef.current || '',
          slides: slides.map(s => ({
            // Embedded base64 images aren't styling — strip them so a 7-slide
            // deck doesn't ship megabytes of pixels to a text analysis.
            html_source: (s.html_source || '').replace(/src="data:[^"]*"/g, 'src=""'),
            preview_url: s.preview_url || '',
          })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Brand check failed');
      setBrandFidelity(data);
    } catch (e) {
      toast.error(e.message || 'Brand check failed');
      setShowBrandFidelity(false);
    } finally {
      setBrandFidelityLoading(false);
    }
  };

  const fixFidelityViolation = async (v) => {
    // Logo violations get a DETERMINISTIC fix: the LLM edit path can't add a
    // logo (it has no URL to add — pressing Fix used to change nothing).
    if (v.type === 'logo' && v.slide > 0) {
      const idx = v.slide - 1;
      const slide = generatedSlidesRef.current?.[idx];
      const brandId = brandIdentityId || brandIdentityIdRef.current || '';
      if (slide?.html_source && brandId) {
        try {
          const resp = await fetch(withBackend('/api/brand-fidelity/inject-logo'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId || '',
              brand_identity_id: brandId,
              html: slide.html_source,
              slide_index: idx,
              total_slides: generatedSlidesRef.current?.length || 0,
            }),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) throw new Error(data.error || 'Logo fix failed');
          if (data.changed && data.html) {
            setGeneratedSlides(prev => prev.map((s, i) => (i === idx ? { ...s, html_source: data.html } : s)));
            toast.success(`Logo added to slide ${v.slide}${data.variant === 'reversed' ? ' (reversed variant for the dark background)' : ''}`);
            // Refresh the slide's screenshot so thumbnails/saves don't keep
            // showing the logo-less version (the editor itself renders live
            // html_source and is already correct).
            fetch(withBackend('/screenshot-html'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ html_source: data.html }),
            }).then(r => (r.ok ? r.json() : null)).then(shot => {
              const pv = shot?.preview_url || (shot?.screenshot ? `data:image/png;base64,${shot.screenshot}` : null);
              if (pv) setGeneratedSlides(prev => prev.map((s, i) => (i === idx ? { ...s, preview_url: pv } : s)));
            }).catch(() => { /* thumbnail refresh is best-effort */ });
          } else if (data.reason === 'already_present') {
            toast.info(`Slide ${v.slide} already carries a brand logo — re-run Brand check`);
          } else {
            toast.error('The brand logo URL is unreachable — check the logo in the brand editor');
          }
          setShowBrandFidelity(false);
          return;
        } catch (e) {
          toast.error(e.message || 'Logo fix failed');
          return;
        }
      }
    }
    setShowBrandFidelity(false);
    const scope = v.slide > 0 ? `On slide ${v.slide}, ` : '';
    handleHtmlChatSend(`${scope}fix this ${v.type === 'consistency' ? 'design consistency' : 'brand'} issue: ${v.detail}. Change only what is needed to fix it.`);
  };
  const [showShareModal, setShowShareModal] = useState(false);
  const [customColorScheme, setCustomColorScheme] = useState(existingProject?.metadata?.custom_color_scheme || null);

  // Streaming animation state
  const [animatingSlideIndex, setAnimatingSlideIndex] = useState(null);
  const [navigationAnimating, setNavigationAnimating] = useState(false);

  // Element extraction state
  const [extractingElements, setExtractingElements] = useState(false);
  const [extractedLayers, setExtractedLayers] = useState(null);
  const [showLayersModal, setShowLayersModal] = useState(false);
  const [selectedLayerIndex, setSelectedLayerIndex] = useState(0);
  const [showLayerEditor, setShowLayerEditor] = useState(false);
  const [makingEditable, setMakingEditable] = useState(false);
  const [editableProgress, setEditableProgress] = useState({ current: 0, total: 0 });
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [editableScope, setEditableScope] = useState('current'); // 'current' or 'all'

  // Background extraction state (runs automatically as slides are generated)
  const [bgExtractionProgress, setBgExtractionProgress] = useState({ current: 0, total: 0, isActive: false });
  const extractionQueueRef = useRef([]);
  const extractionInProgressRef = useRef(0);
  const maxConcurrentExtractions = 2;
  const preloadedAutoGenRef = useRef(false);

  // Handle preloaded outline from AgenticFlow — set outline + questionnaire answers and jump to outline review
  useEffect(() => {
    if (preloadedOutline && !existingProject) {
      const { outline, questionnaireData, flowConfig } = preloadedOutline;
      // Set the generated outline
      if (outline) {
        setGeneratedOutline(outline);
        setPresentationTitle(outline.title || '');
        if (outline.slides?.length) setSlideCount(outline.slides.length);
      }
      // Set questionnaire answers from the agentic flow
      if (questionnaireData) {
        setQuestionnaireAnswers({
          audience: questionnaireData.audience || null,
          presentationType: questionnaireData.presentationType || null,
          visualStyle: questionnaireData.visualStyle || null,
          includeImages: questionnaireData.includeImages || null,
          imageStyle: questionnaireData.imageStyle || null,
          imageryMode: questionnaireData.imageryMode || questionnaireData.imagery_mode || null,
          artDirection: questionnaireData.artDirection || questionnaireData.art_direction || null,
          htmlModel: questionnaireData.htmlModel || getDefaultModel(),
          specialInstructions: questionnaireData.specialInstructions || '',
          // Theme direction picked in the clarifier (round 70) — without
          // this pick the THEME LOCK dies here and never reaches the
          // custom_theme_request form field the deck endpoints read.
          customThemeRequest: questionnaireData.customThemeRequest || '',
          exportTarget: questionnaireData.exportTarget || null,
          // Default 'yes' so existing/legacy flows keep the QA pass on. The
          // user can opt out via the questionnaire to skip QA on the deck.
          runQa: questionnaireData.runQa || flowConfig?.runQa || 'yes',
          proposalMode: questionnaireData.proposalMode || flowConfig?.proposalMode || existingProject?.metadata?.proposal_mode || false,
          // r180: the frozen scope (questions, requirements, answers, post-draft
          // facts) survives reopen so the coverage audit keeps its checklist.
          proposalScope: questionnaireData.proposalScope || flowConfig?.proposalScope || existingProject?.metadata?.proposal_scope || null,
        });
      }
      // Set generation mode and other config
      if (flowConfig) {
        if (flowConfig.generationMode) setGenerationMode(flowConfig.generationMode);
        if (flowConfig.htmlModel) setHtmlEditModel(flowConfig.htmlModel);
        if (flowConfig.llmProvider) setLlmProvider(flowConfig.llmProvider);
        if (flowConfig.prompt) setTopicPrompt(flowConfig.prompt);
        if (flowConfig.language) setContentLanguage(flowConfig.language);
        if (flowConfig.logoUrl) setLogoUrl(flowConfig.logoUrl);
        if (flowConfig.webResearch) setWebResearchContext(flowConfig.webResearch);
        if (flowConfig.excelData) setExcelData(flowConfig.excelData);
        // Variation style passthrough
        if (flowConfig.variationStyleReference) setVariationStyleRef(flowConfig.variationStyleReference);
        if (flowConfig.variationPreGeneratedSlides) setPreGeneratedVariationSlides(flowConfig.variationPreGeneratedSlides);
        if (flowConfig.variationSampleIndices) setVariationSampleIndices(flowConfig.variationSampleIndices);
        if (flowConfig.variationDirection) setVariationDirection(flowConfig.variationDirection);
        // Early slide-1 pipelining handoff (live Promise — ref, not state).
        if (flowConfig.earlyFirstSlide) earlyFirstSlideRef.current = flowConfig.earlyFirstSlide;
        if (flowConfig.redesignImage) setRedesignImage(flowConfig.redesignImage);
        if (flowConfig.keepSourceDesign) setKeepSourceDesign(true); // r176
        if (flowConfig.embedImages?.length) setEmbedImages(flowConfig.embedImages);
        console.log('[BRAND-DEBUG] CreatePPTWorkspace flowConfig.brandIdentityId:', flowConfig.brandIdentityId);
        if (flowConfig.brandIdentityId) {
          setBrandIdentityId(flowConfig.brandIdentityId);
          brandIdentityIdRef.current = flowConfig.brandIdentityId;
        }
        if (flowConfig.deckTemplateId) deckTemplateIdRef.current = flowConfig.deckTemplateId;
      }
      // Set input mode to prompt-based
      setInputMode('prompt');
      // Stay at step 2 briefly while state settles, then auto-trigger generation
      setCurrentStep(2);
      preloadedAutoGenRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-trigger generation when preloaded outline is set (from AgenticFlow)
  useEffect(() => {
    if (preloadedAutoGenRef.current && generatedOutline?.slides?.length > 0) {
      preloadedAutoGenRef.current = false;
      // Short delay to let React finish batched state updates
      setTimeout(() => handleApplyOutlineAndProceed(), 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedOutline]);

  // Hide global nav in fullscreen review mode (step 5)
  useEffect(() => {
    onFullscreen?.(currentStep === 5);
    return () => onFullscreen?.(false);
  }, [currentStep, onFullscreen]);

  // Generation sub-step animation — cycles descriptive sub-steps while waiting for each slide
  const [generationElapsed, setGenerationElapsed] = useState(0);
  useEffect(() => {
    if (!generating) {
      setGenerationElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setGenerationElapsed(prev => prev + 1);
    }, 8000);
    return () => clearInterval(interval);
  }, [generating, generatedSlides.length]);

  // Restore layers for saved projects on mount
  // Template slides: regenerate editable layers from theme + content
  // AI slides: clear stripped extractedLayers so "Make Editable" can re-extract
  useEffect(() => {
    if (!existingProject?.slides?.length) return;
    const restoredTheme = selectedTheme;
    const colorScheme = customColorScheme || restoredTheme?.colorScheme;

    setGeneratedSlides(prev => prev.map((slide, i) => {
      const hasRealLayers = slide.extractedLayers?.layers?.length > 0;
      if (hasRealLayers) return slide; // Already has real layer data

      if (slide.extractedLayers?.source === 'template' && restoredTheme) {
        // Template slide: regenerate layers from theme
        try {
          const template = getTemplateForSlide(
            { ...restoredTheme, colorScheme },
            slide, i, prev.length
          );
          const content = {
            title: slide.title,
            content: slide.content || '',
            bullet_points: slide.bullet_points || [],
            bulletPoints: slide.bullet_points || [],
          };
          const newLayers = generateEditableLayers(template, content, colorScheme);
          const newImage = renderTemplateToImage(template, content, colorScheme);
          const bgOnly = renderTemplateBackgroundOnly(template, content, colorScheme);
          // renderTemplateToImage returns a promise, handle async below
          return { ...slide, _pendingRegenerate: { template, content, colorScheme, index: i } };
        } catch (err) {
          console.error('Failed to regenerate template layers for slide', i, err);
          return slide;
        }
      } else if (slide.extractedLayers && !slide.extractedLayers.layers) {
        // AI slide with stripped layers: clear so re-extraction can work
        return { ...slide, extractedLayers: null };
      }
      return slide;
    }));

    // AI slides with stripped layers are cleared above (line 199) so
    // "Make Editable" can re-extract on demand. We no longer auto-trigger
    // extraction on load — the user can manually click "Make Editable".

    // Handle async regeneration for template slides
    if (restoredTheme) {
      const colorSchemeAsync = customColorScheme || restoredTheme?.colorScheme;
      existingProject.slides.forEach(async (slide, i) => {
        const hasRealLayers = slide.extractedLayers?.layers?.length > 0;
        if (hasRealLayers || slide.extractedLayers?.source !== 'template') return;

        try {
          // If slide has an image, set needsImage so getTemplateForSlide returns the image variant
          const slideForTemplate = slide._slideImageUrl
            ? { ...slide, needsImage: true }
            : slide;
          const template = getTemplateForSlide(
            { ...restoredTheme, colorScheme: colorSchemeAsync },
            slideForTemplate, i, existingProject.slides.length
          );
          const content = {
            title: slide.title,
            content: slide.content || '',
            bullet_points: slide.bullet_points || [],
            bulletPoints: slide.bullet_points || [],
            slideType: slide.slideType || slide.template_id || '',
            _slideImageUrl: slide._slideImageUrl || '',
          };
          const newLayers = generateEditableLayers(template, content, colorSchemeAsync);
          const newImage = await renderTemplateToImage(template, content, colorSchemeAsync);
          const bgOnly = await renderTemplateBackgroundOnly(template, content, colorSchemeAsync);

          setGeneratedSlides(prev => prev.map((s, idx) =>
            idx === i ? {
              ...s,
              preview_url: newImage,
              extractedLayers: {
                ...s.extractedLayers,
                layers: newLayers,
                background: newImage,
                background_image: newImage,
                backgroundOnly: bgOnly,
              },
              _pendingRegenerate: undefined,
            } : s
          ));
        } catch (err) {
          console.error('Failed to async regenerate template slide', i, err);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Keyboard arrow navigation for slide review (Step 5)
  useEffect(() => {
    if (currentStep !== 5 || generating) return;
    const handleKeyDown = (e) => {
      // Don't navigate if user is typing in an input/textarea
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentSlideIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentSlideIndex(prev => Math.min(generatedSlides.length - 1, prev + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, generating, generatedSlides.length]);

  // Auto-scroll HTML chat to bottom
  useEffect(() => {
    if (htmlChatScrollRef.current) {
      htmlChatScrollRef.current.scrollTop = htmlChatScrollRef.current.scrollHeight;
    }
  }, [htmlChatHistory, htmlChatLoading]);

  // Load brand guidelines on mount (only if not already provided by existingProject)
  useEffect(() => {
    if (userId && !existingProject?.brandGuidelines) {
      loadBrandGuidelines();
    }
  }, [userId, existingProject]);

  const loadBrandGuidelines = async () => {
    setLoadingBrandGuidelines(true);
    try {
      // Load from Supabase
      const { data, error } = await supabaseHelpers.getBrandGuidelines(userId);
      if (data && !error) {
        setBrandGuidelines(data);
      }
    } catch (error) {
      console.error('Failed to load brand guidelines:', error);
    } finally {
      setLoadingBrandGuidelines(false);
    }
  };

  // Slide management
  const addSlide = () => {
    const newId = Math.max(...slides.map(s => s.id)) + 1;
    setSlides([...slides, { id: newId, title: '', content: '', bulletPoints: [] }]);
  };

  const removeSlide = (id) => {
    if (slides.length > 1) {
      setSlides(slides.filter(s => s.id !== id));
    }
  };

  const updateSlide = (id, field, value) => {
    setSlides(slides.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const addBulletPoint = (slideId) => {
    setSlides(slides.map(s => {
      if (s.id === slideId) {
        return { ...s, bulletPoints: [...s.bulletPoints, ''] };
      }
      return s;
    }));
  };

  const updateBulletPoint = (slideId, index, value) => {
    setSlides(slides.map(s => {
      if (s.id === slideId) {
        const newBullets = [...s.bulletPoints];
        newBullets[index] = value;
        return { ...s, bulletPoints: newBullets };
      }
      return s;
    }));
  };

  const removeBulletPoint = (slideId, index) => {
    setSlides(slides.map(s => {
      if (s.id === slideId) {
        return { ...s, bulletPoints: s.bulletPoints.filter((_, i) => i !== index) };
      }
      return s;
    }));
  };

  // Shared SSE reader for outline streaming (used by both prompt and structure flows)
  const readOutlineStream = async (response, { isEnhance = false } = {}) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let slidesReceived = 0;
    let outlineTitle = presentationTitle;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case 'thinking':
                // Thinking tokens from Gemini — logged for now, shown in AgenticFlow
                break;
              case 'title':
                outlineTitle = event.title;
                setPresentationTitle(event.title);
                break;
              case 'slide':
                slidesReceived++;
                setGeneratedOutline(prev => {
                  const slides = prev ? [...prev.slides, event.slide] : [event.slide];
                  return { title: outlineTitle, slides };
                });
                break;
              case 'progress':
                // Progress shown inline on outline page
                break;
              case 'complete':
                // Completion shown inline on outline page
                break;
              case 'error':
                throw new Error(event.error);
              default:
                break;
            }
          } catch (parseError) {
            if (parseError.message && !parseError.message.includes('JSON')) {
              throw parseError; // Re-throw non-parse errors (like our 'error' event)
            }
            console.warn('Failed to parse outline SSE event:', parseError);
          }
        }
      }
    }

    if (slidesReceived === 0) {
      throw new Error('No slides received from outline generation');
    }
  };

  // Outline generation and management
  const handleGenerateOutline = async (answers) => {
    if (!topicPrompt.trim()) {
      toast.error('Please enter a topic or prompt');
      return;
    }

    setOutlineLoading(true);
    setGeneratedOutline({ title: presentationTitle, slides: [] });
    setOutlineFromEnhance(false);
    setCurrentStep(2); // Show outline review page immediately
    // No toast — status is shown inline on the outline page

    const qa = answers || questionnaireAnswers;

    try {
      const response = await fetch(withBackend('/api/generate-outline-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: topicPrompt,
          num_slides: slideCount,
          language: contentLanguage,
          text_style: textStyle,
          tone: contentTone,
          instructions: aiInstructions,
          llm_provider: llmProvider,
          audience: qa?.audience || null,
          presentation_type: qa?.presentationType || null,
          visual_style: qa?.visualStyle || null,
          include_images: qa?.includeImages || null,
          image_style: qa?.imageStyle || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate outline');
      }

      await readOutlineStream(response, { isEnhance: false });
    } catch (error) {
      console.error('Outline generation error:', error);
      toast.error(error.message || 'Failed to generate outline', { id: 'outline-gen' });
      setCurrentStep(1); // Go back on error
      setGeneratedOutline(null);
    } finally {
      setOutlineLoading(false);
    }
  };

  // Structure pasted content into slides and go to outline review
  const handleStructureContent = async (answers) => {
    if (!rawContent.trim() && !excelData) {
      toast.error('Please paste or import content first');
      return;
    }

    setOutlineLoading(true);
    setGeneratedOutline({ title: presentationTitle, slides: [] });
    setOutlineFromEnhance(true);
    setCurrentStep(2); // Show outline review page immediately
    // Status shown inline on outline page

    const qa = answers || questionnaireAnswers;

    try {
      const response = await fetch(withBackend('/api/content/structure-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: rawContent,
          num_slides: slideCount,
          title: presentationTitle,
          audience: qa?.audience || null,
          presentation_type: qa?.presentationType || null,
          visual_style: qa?.visualStyle || null,
          llm_provider: llmProvider,
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to structure content');
      }

      await readOutlineStream(response, { isEnhance: true });
    } catch (error) {
      console.error('Content structuring error:', error);
      toast.error(error.message || 'Failed to structure content', { id: 'outline-gen' });
      setCurrentStep(1);
      setGeneratedOutline(null);
    } finally {
      setOutlineLoading(false);
    }
  };

  // Import content from file or URL into the paste content textarea
  const handleContentImport = async (file, urlToImport) => {
    setImporting(true);
    try {
      let response;

      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('structure', 'false');
        // Visual extraction for PPT/PDF — LLM sees design + layout
        if (/\.(pptx?|pdf)$/i.test(file.name)) formData.append('visual', 'true');
        response = await fetch(withBackend('/api/content/extract'), {
          method: 'POST',
          body: formData,
        });
      } else if (urlToImport) {
        response = await fetch(withBackend('/api/content/extract'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: urlToImport, structure: false }),
        });
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Extraction failed');

      if (data.excel_data) {
        // Excel file — store structured data and show preview
        setExcelData({
          sheets: data.excel_data,
          filename: file?.name || 'spreadsheet.xlsx',
          source_info: data.source_info
        });
        setRawContent(prev => prev ? prev + '\n\n' + data.extracted_text : data.extracted_text);
        setEnhanceText(true); // Excel always needs AI analysis
        toast.success(`Excel imported: ${data.excel_data.length} sheet(s) found`);
        setImportMode(null);
      } else if (data.extracted_text) {
        setRawContent(prev => prev ? prev + '\n\n' + data.extracted_text : data.extracted_text);
        toast.success('Content imported successfully');
        setImportMode(null);
        setImportUrl('');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to import content');
    } finally {
      setImporting(false);
      if (contentFileInputRef.current) {
        contentFileInputRef.current.value = '';
      }
    }
  };

  const updateOutlineSlide = (index, field, value) => {
    setGeneratedOutline(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) =>
        i === index ? { ...slide, [field]: value } : slide
      )
    }));
  };

  const removeOutlineSlide = (index) => {
    if (generatedOutline?.slides?.length <= 1) return;
    setGeneratedOutline(prev => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== index)
    }));
  };

  const addOutlineSlide = () => {
    const newSlide = {
      slide_number: (generatedOutline?.slides?.length || 0) + 1,
      slide_type: 'content',
      title: 'New Slide',
      content: '',
      bullet_points: [''],
      instructions: '',
      suggested_image: ''
    };
    setGeneratedOutline(prev => ({
      ...prev,
      slides: [...(prev?.slides || []), newSlide]
    }));
  };

  const updateOutlineBulletPoint = (slideIndex, pointIndex, value) => {
    setGeneratedOutline(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) => {
        if (i !== slideIndex) return slide;
        const newPoints = [...(slide.bullet_points || [])];
        newPoints[pointIndex] = value;
        return { ...slide, bullet_points: newPoints };
      })
    }));
  };

  const removeOutlineBulletPoint = (slideIndex, pointIndex) => {
    setGeneratedOutline(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) => {
        if (i !== slideIndex) return slide;
        return {
          ...slide,
          bullet_points: slide.bullet_points.filter((_, j) => j !== pointIndex)
        };
      })
    }));
  };

  const addOutlineBulletPoint = (slideIndex) => {
    setGeneratedOutline(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) => {
        if (i !== slideIndex) return slide;
        return {
          ...slide,
          bullet_points: [...(slide.bullet_points || []), '']
        };
      })
    }));
  };

  const handleApplyOutlineAndProceed = () => {
    if (!generatedOutline?.slides?.length) {
      toast.error('No outline to apply');
      return;
    }

    // Convert outline slides to the structured format used for generation
    const structuredSlides = generatedOutline.slides.map((slide, idx) => ({
      id: idx + 1,
      title: slide.title || '',
      content: slide.content || '',
      bulletPoints: slide.bullet_points || [],
      slideType: slide.slide_type || 'content',
      needsImage: slide.needs_image || false,
      suggestedImage: slide.suggested_image || '',
    }));

    setSlides(structuredSlides);
    setInputMode('structured');
    setEnhanceText(false);

    // Route directly to the appropriate generation function to avoid
    // stale-state validation issues in handleGenerate (React batches setState)
    if (generationMode === 'html') {
      handleGenerateWithHTML();
    } else if (generationMode === 'templates') {
      handleGenerateWithTemplates();
    } else {
      // For AI/Imaginative mode, call triggerGeneration which skips validation
      triggerGeneration();
    }
  };

  // Trigger generation (extracted from handleGenerate for reuse)
  const triggerGeneration = async () => {
    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedSlides([]);
    setGenerationStatusMessage('Analyzing content...');
    setCurrentStep(4);

    // Reset background extraction state for new generation
    extractionQueueRef.current = [];
    extractionInProgressRef.current = 0;
    setBgExtractionProgress({ current: 0, total: 0, isActive: false });

    try {
      // Use outline slides if available (avoids stale state from React batching)
      const slidesToSend = generatedOutline?.slides?.length > 0
        ? generatedOutline.slides.map(s => ({
            title: s.title || '',
            content: s.content || '',
            bulletPoints: (s.bullet_points || s.bulletPoints || []).filter(b => b && b.toString().trim()),
            slideType: s.slide_type || s.slideType || 'content',
          }))
        : slides;

      // Prepare form data
      const formData = new FormData();
      formData.append('presentation_title', presentationTitle);
      formData.append('input_mode', 'structured');
      formData.append('slides_content', JSON.stringify(slidesToSend));
      formData.append('slide_count', slidesToSend.length.toString());
      formData.append('user_id', userId || '');
      formData.append('enhance_text', 'false'); // Already enhanced

      if (brandGuidelines && useBrandGuidelines) {
        formData.append('brand_guidelines', JSON.stringify(brandGuidelines));
      }

      // Add brand references if any
      if (brandReferences.images.length > 0) {
        formData.append('brand_references', JSON.stringify(brandReferences.images.map(img => img.base64)));
        formData.append('reference_weight', brandReferences.weight);
      }

      if (templateFile) {
        formData.append('template', templateFile);
      }

      // Add AI style preset for Nano Banana Pro
      if (aiStylePreset && aiStylePresets[aiStylePreset]) {
        const preset = aiStylePresets[aiStylePreset];
        if (preset.isAiGenerated) {
          formData.append('ai_create_theme', 'true');
        } else if (!preset.isBrandReference) {
          formData.append('style_preset', JSON.stringify(preset));
        }
      }

      // Add custom theme request if provided
      if (questionnaireAnswers?.customThemeRequest) {
        formData.append('custom_theme_request', questionnaireAnswers.customThemeRequest);
      }

      // Add questionnaire answers for design context
      if (questionnaireAnswers?.audience) formData.append('questionnaire_audience', questionnaireAnswers.audience);
      if (questionnaireAnswers?.presentationType) formData.append('questionnaire_type', questionnaireAnswers.presentationType);
      if (questionnaireAnswers?.visualStyle) formData.append('questionnaire_visual_style', questionnaireAnswers.visualStyle);
      if (questionnaireAnswers?.includeImages) formData.append('questionnaire_include_images', questionnaireAnswers.includeImages);

      // Add brand identity ID if selected
      if (brandIdentityId || brandIdentityIdRef.current) formData.append('brand_identity_id', brandIdentityId || brandIdentityIdRef.current);
      if (deckTemplateIdRef.current) formData.append('deck_template_id', deckTemplateIdRef.current);

      // Add variation direction for style context (from variation preview selection)
      if (variationDirection) formData.append('variation_direction', JSON.stringify(variationDirection));

      // Pass user's original prompt and custom instructions so the imaginative
      // generator knows the intent beyond the structured slide list.
      if (topicPrompt) formData.append('user_prompt', topicPrompt);
      if (aiInstructions) formData.append('ai_instructions', aiInstructions);

      // Use streaming endpoint for progressive slide display
      const response = await fetch(withBackend('/create-ppt-stream'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to start slide generation');
      }

      // Process the SSE stream (same as handleGenerate)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'slide') {
                setGeneratedSlides(prev => {
                  const newSlides = [...prev];
                  newSlides[data.index] = data.slide;
                  return newSlides;
                });
                setGenerationProgress(Math.round(((data.index + 1) / data.total) * 100));
                setCurrentSlideIndex(data.index);

                // Queue background extraction
                if (data.slide?.preview_url) {
                  queueSlideForExtraction(data.slide);
                }
              } else if (data.type === 'complete') {
                setGenerating(false);
                setCurrentStep(5);
                toast.success(`Generated ${data.total} slides!`);
                onChatMessage?.(`Presentation generated! ${data.total} slides ready. You can now edit them or ask questions.`);
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.message || 'Failed to generate slides');
      setGenerating(false);
      setCurrentStep(2);
    }
  };

  // Enhance content and show review page
  const handleEnhanceAndReview = async ({ keepAsIs = false } = {}) => {
    setOutlineLoading(true);
    setGeneratedOutline({ title: presentationTitle, slides: [] });
    setOutlineFromEnhance(true);
    setCurrentStep(2); // Show outline review page immediately
    // Status shown inline on outline page

    try {
      // Prepare content based on input mode
      let contentToEnhance = '';
      if (inputMode === 'raw') {
        contentToEnhance = rawContent;
      } else {
        // Convert structured slides to text
        contentToEnhance = slides.map((s, i) => {
          let slideText = `Slide ${i + 1}: ${s.title || 'Untitled'}\n`;
          if (s.content) slideText += `${s.content}\n`;
          if (s.bulletPoints?.length > 0) {
            slideText += s.bulletPoints.filter(b => b.trim()).map(b => `- ${b}`).join('\n');
          }
          return slideText;
        }).join('\n\n');
      }

      // Different instructions for Keep As-Is vs Enhance
      let modeInstructions;
      if (excelData) {
        modeInstructions = 'Analyze this spreadsheet data and create a data-driven presentation with charts.';
      } else if (keepAsIs) {
        modeInstructions = 'KEEP AS-IS MODE: Read and analyze the user\'s content, then organize it into a structured slide outline. Do NOT rewrite, rephrase, enhance, or add new content. Use the user\'s EXACT original wording. Your job is only to: (1) figure out how to split the content across slides, (2) assign a title to each slide, (3) place each piece of content as a separate bullet_point or in the content field, and (4) pick appropriate slide_types. Preserve the original text verbatim.';
      } else {
        modeInstructions = 'Enhance and improve this existing content while preserving its core message. IMPORTANT: If the content already has slide separators (e.g. "Slide 1", "Slide 2"), titles, and bullet points, you MUST preserve the exact slide structure, number of slides, titles, and bullet-point organization. Polish the language but do NOT merge slides, flatten bullets into paragraphs, or restructure. Each slide\'s bullet points must remain as separate bullet_points array items.';
      }

      const response = await fetch(withBackend('/api/generate-outline-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: contentToEnhance,
          num_slides: slideCount,
          language: contentLanguage,
          text_style: keepAsIs ? 'preserve' : textStyle,
          tone: contentTone,
          instructions: `${modeInstructions} ${aiInstructions}`,
          llm_provider: llmProvider,
          audience: questionnaireAnswers?.audience || null,
          presentation_type: questionnaireAnswers?.presentationType || null,
          visual_style: questionnaireAnswers?.visualStyle || null,
          include_images: questionnaireAnswers?.includeImages || null,
          image_style: questionnaireAnswers?.imageStyle || null,
          excel_data: excelData?.sheets || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to enhance content');
      }

      await readOutlineStream(response, { isEnhance: true });
    } catch (error) {
      console.error('Enhance content error:', error);
      toast.error(error.message || 'Failed to enhance content', { id: 'outline-gen' });
      setCurrentStep(1);
      setGeneratedOutline(null);
    } finally {
      setOutlineLoading(false);
    }
  };

  // Template file handling
  const handleTemplateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplateFile(file);
      // Create preview for first slide (would need backend to extract)
      toast.success(`Template selected: ${file.name}`);
    }
  };

  // Generate presentation with templates (instant)
  const handleGenerateWithTemplates = async () => {
    if (!selectedTheme) {
      toast.error('Please select a theme first');
      return;
    }

    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedSlides([]);
    setCurrentStep(4);

    // Reset background extraction state
    extractionQueueRef.current = [];
    extractionInProgressRef.current = 0;
    setBgExtractionProgress({ current: 0, total: 0, isActive: false });

    try {
      let slidesToRender;

      if (inputMode === 'raw' && rawContent.trim()) {
        // For raw content, first try to use AI to structure it (like Gamma)
        toast.loading('Analyzing content structure...', { id: 'template-gen' });

        try {
          // Call the AI content structuring endpoint
          const response = await fetch(withBackend('/api/content/structure'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: rawContent,
              num_slides: slideCount,
              title: presentationTitle
            })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.slides) {
              // AI returns slide_type for each slide (like Gamma)
              slidesToRender = data.slides.map((s, idx) => ({
                title: s.title || `Slide ${idx + 1}`,
                content: s.content || '',
                bulletPoints: s.bullet_points || s.bulletPoints || [],
                // AI-recommended slide type for intelligent layout selection
                slideType: s.slide_type || s.slideType || null,
                // For stat-highlight slides, capture the key statistic
                stat: s.stat || null,
              }));
              console.log('AI structured slides with types:', slidesToRender.map(s => ({ title: s.title, type: s.slideType })));
            }
          }
        } catch (aiError) {
          console.warn('AI structuring failed, falling back to pattern parsing:', aiError);
        }

        // Fallback to pattern-based parsing if AI fails
        if (!slidesToRender || slidesToRender.length === 0) {
          slidesToRender = parseRawContentToSlides(rawContent, slideCount);
        }
      } else {
        // Use structured input
        slidesToRender = slides.filter(s => s.title.trim() || s.content.trim() || s.bulletPoints.length > 0);
      }

      if (!slidesToRender || slidesToRender.length === 0) {
        throw new Error('No content to render. Please add slide content first.');
      }

      toast.loading(`Generating ${slidesToRender.length} slides with templates...`, { id: 'template-gen' });

      // Image generator callback for slides that need AI-generated photographs
      const imageGenerator = async (imageDescription, slideTitle) => {
        try {
          const response = await fetch(withBackend('/api/generate-slide-image'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image_description: imageDescription, slide_title: slideTitle }),
          });
          if (!response.ok) return null;
          const data = await response.json();
          return data.image_url || null;
        } catch (err) {
          console.warn('Image generation failed:', err);
          return null;
        }
      };

      // Generate slides using templates with streaming (Gamma-style)
      // Slides appear one by one as they're rendered
      const generated = await generateSlidesWithTemplates(
        selectedTheme,
        slidesToRender,
        useBrandGuidelines ? brandGuidelines : null,
        // Progress callback
        (current, total) => {
          setGenerationProgress(Math.round((current / total) * 100));
        },
        // Streaming callback - add each slide as it's generated (Gamma-style)
        (slide, index, total) => {
          // Switch to review step on first slide so user sees slides appear one by one
          if (index === 0) {
            setCurrentStep(5);
          }
          setGeneratedSlides(prev => [...prev, slide]);
          setCurrentSlideIndex(index);
          setAnimatingSlideIndex(index);
          toast.loading(`Generating slide ${index + 1} of ${total}...`, { id: 'template-gen' });
        },
        // Image generator for slides that need AI-generated photographs
        questionnaireAnswers?.includeImages === 'yes' ? imageGenerator : null,
      );

      // Ensure step 5 is set even if callback didn't fire (edge case)
      if (currentStep !== 5) setCurrentStep(5);
      toast.success(`Generated ${generated.length} slides!`, { id: 'template-gen' });

      // Handle async image generation — images load in background after slides appear
      if (generated._imagePromises) {
        const totalImageSlides = generated.filter(s => s._needsImage).length;
        toast.loading(`Generating ${totalImageSlides} image${totalImageSlides > 1 ? 's' : ''}...`, { id: 'slide-images' });
        generated._imagePromises.then(async (imageResults) => {
          if (!imageResults || imageResults.length === 0) {
            toast.error(`Image generation failed (0/${totalImageSlides})`, { id: 'slide-images' });
            return;
          }

          // Re-render each slide with its image using proper template resolution
          const { renderTemplateToImage: reRender, renderTemplateBackgroundOnly: reRenderBg, generateEditableLayers: reGenLayers } = await import('../lib/templateRenderer');
          const { getTemplateForSlide: getTemplate } = await import('../data/themes');

          let rendered = 0;
          for (const { index: idx, imageUrl } of imageResults) {
            try {
              // Use the generated array directly — React state may not be committed yet
              const slideData = generated[idx];
              if (!slideData) {
                console.warn(`Slide ${idx} not found in generated array (length=${generated.length}), skipping image`);
                continue;
              }

              // Re-resolve template with needsImage=true to get the image variant
              const slideForTemplate = {
                needsImage: true,
                slideType: slideData.slideType || slideData.template_id || '',
                title: slideData.title || '',
                bulletPoints: slideData.bullet_points || [],
              };
              const template = getTemplate(selectedTheme, slideForTemplate, idx, generated.length);

              const content = {
                title: slideData.title || '',
                content: slideData.content || '',
                bullet_points: slideData.bullet_points || [],
                bulletPoints: slideData.bullet_points || [],
                slideType: slideData.slideType || slideData.template_id || '',
                _slideImageUrl: imageUrl,
              };
              const cs = selectedTheme.colorScheme;

              const newPreview = await reRender(template, content, cs);
              const newBgOnly = await reRenderBg(template, content, cs);
              const newLayers = reGenLayers(template, content, cs);

              setGeneratedSlides(prev => {
                const u = [...prev];
                if (u[idx]) {
                  u[idx] = {
                    ...u[idx],
                    preview_url: newPreview,
                    backgroundOnlyUrl: newBgOnly,
                    template_id: template.id,
                    layout: template.layout,
                    _slideImageUrl: imageUrl,
                    extractedLayers: {
                      ...u[idx].extractedLayers,
                      background: newPreview,
                      background_image: newPreview,
                      backgroundOnly: newBgOnly,
                      layers: newLayers,
                    },
                  };
                }
                return u;
              });
              rendered++;
              console.log(`[IMAGE] Re-rendered slide ${idx + 1} with image (${rendered}/${imageResults.length})`);
              toast.loading(`Added image ${rendered}/${imageResults.length}...`, { id: 'slide-images' });
            } catch (err) {
              console.error(`[IMAGE] Failed to re-render slide ${idx + 1} with image:`, err);
            }
          }
          if (rendered === totalImageSlides) {
            toast.success(`${rendered} image${rendered > 1 ? 's' : ''} added!`, { id: 'slide-images' });
          } else {
            toast.success(`${rendered}/${totalImageSlides} images added`, { id: 'slide-images' });
          }
        }).catch(err => {
          console.warn('Image generation failed:', err);
          toast.error('Image generation failed', { id: 'slide-images' });
        });
      }

    } catch (error) {
      console.error('Template generation error:', error);
      toast.error(error.message || 'Failed to generate with templates', { id: 'template-gen' });
      setCurrentStep(2);
    } finally {
      setGenerating(false);
    }
  };

  // Generate presentation with uploaded PPTX template
  const handleGenerateWithPPTXTemplate = async () => {
    if (!selectedPPTXTemplate) {
      toast.error('Please select a PPTX template first');
      return;
    }

    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedSlides([]);
    setCurrentStep(4);

    // Reset background extraction state
    extractionQueueRef.current = [];
    extractionInProgressRef.current = 0;
    setBgExtractionProgress({ current: 0, total: 0, isActive: false });

    try {
      // Get slides content - parse raw content into multiple slides
      const slidesToRender = inputMode === 'raw'
        ? parseRawContentToSlides(rawContent, slideCount).map(s => ({
            title: s.title,
            content: s.content,
            bullet_points: s.bulletPoints || []
          }))
        : slides.filter(s => s.title.trim() || s.content.trim() || s.bulletPoints.length > 0)
            .map(s => ({
              title: s.title,
              content: s.content,
              bullet_points: s.bulletPoints.filter(b => b.trim())
            }));

      if (slidesToRender.length === 0) {
        throw new Error('No content to render');
      }

      console.log('Template slides to render:', slidesToRender);
      toast.loading(`Populating template with ${slidesToRender.length} slides...`, { id: 'pptx-gen' });

      const response = await fetch(withBackend(`/api/templates/${selectedPPTXTemplate.id}/generate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slides: slidesToRender,
          use_ai: enhanceText,
          brand_guidelines: useBrandGuidelines ? brandGuidelines : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate from template');
      }

      // Convert previews to generated slides format
      const generated = data.output.previews.map((preview, idx) => ({
        slide_index: idx,
        preview_url: preview,
        title: slidesToRender[idx]?.title || `Slide ${idx + 1}`,
        download_url: data.output.download_url,
      }));

      setGeneratedSlides(generated);
      setCurrentStep(5);
      toast.success(`Generated ${generated.length} slides from template!`, { id: 'pptx-gen' });

    } catch (error) {
      console.error('PPTX template generation error:', error);
      toast.error(error.message || 'Failed to generate from template', { id: 'pptx-gen' });
      setCurrentStep(2);
    } finally {
      setGenerating(false);
    }
  };

  // Parse raw content into slides based on markers or slide count
  const parseRawContentToSlides = (content, targetSlideCount) => {
    console.log('Parsing raw content, length:', content.length);

    // Split content by "Slide X" patterns (handles **Slide 1**, Slide 1 -, Slide 1:, etc.)
    const slidePattern = /\*{0,2}Slide\s*\d+\s*[-–—:]\s*/gi;
    const parts = content.split(slidePattern);

    // Find all slide markers to get their titles
    const markerPattern = /\*{0,2}Slide\s*(\d+)\s*[-–—:]\s*([^\n*]*)\*{0,2}/gi;
    const markers = [...content.matchAll(markerPattern)];

    console.log('Found markers:', markers.length, markers.map(m => m[0]));

    if (markers.length >= 2 && parts.length > 1) {
      // Content has slide markers - use them
      const parsedSlides = [];

      for (let i = 0; i < markers.length; i++) {
        const slideTitle = markers[i][2]?.trim().replace(/\*+$/, '') || `Slide ${i + 1}`;
        const slideContent = parts[i + 1]?.trim() || '';

        // Extract bullet points (lines starting with -, •, *, or numbered like 1. 2) 3-)
        const lines = slideContent.split('\n').map(l => l.trim()).filter(l => l);
        const isBullet = l => /^[-•*]\s/.test(l) || /^\d+[.):\-]\s/.test(l);
        const cleanBullet = l => l.replace(/^[-•*]\s*/, '').replace(/^\d+[.):\-]\s*/, '').trim();
        const bullets = lines.filter(isBullet).map(cleanBullet);
        const nonBullets = lines.filter(l => !isBullet(l)).join(' ');

        parsedSlides.push({
          title: slideTitle,
          content: nonBullets,
          bulletPoints: bullets,
          type: i === 0 ? 'title' : i === markers.length - 1 ? 'closing' : bullets.length > 0 ? 'bullets' : 'content',
        });
      }

      console.log('Parsed slides:', parsedSlides.length);
      return parsedSlides;
    }

    console.log('No markers found, splitting by paragraphs');

    // No markers found - split content evenly by paragraphs/sections
    const paragraphs = content.split(/\n\n+/).filter(p => p.trim());
    const slidesFromParagraphs = [];
    const itemsPerSlide = Math.max(1, Math.ceil(paragraphs.length / targetSlideCount));

    for (let i = 0; i < targetSlideCount && i * itemsPerSlide < paragraphs.length; i++) {
      const slideParas = paragraphs.slice(i * itemsPerSlide, (i + 1) * itemsPerSlide);
      const slideText = slideParas.join('\n');
      const lines = slideText.split('\n').map(l => l.trim()).filter(l => l);
      const isBulletLine = l => /^[-•*]\s/.test(l) || /^\d+[.):\-]\s/.test(l);
      const cleanBulletLine = l => l.replace(/^[-•*]\s*/, '').replace(/^\d+[.):\-]\s*/, '').trim();
      const bullets = lines.filter(isBulletLine).map(cleanBulletLine);
      const title = i === 0 ? presentationTitle : lines[0]?.replace(/^[-•*]\s*/, '').replace(/^\d+[.):\-]\s*/, '') || `Slide ${i + 1}`;

      slidesFromParagraphs.push({
        title: title,
        content: lines.filter(l => !isBulletLine(l)).slice(i === 0 ? 0 : 1).join(' '),
        bulletPoints: bullets,
        type: i === 0 ? 'title' : i === targetSlideCount - 1 ? 'closing' : bullets.length > 0 ? 'bullets' : 'content',
      });
    }

    return slidesFromParagraphs.length > 0 ? slidesFromParagraphs : [{
      title: presentationTitle,
      content: content,
      bulletPoints: [],
      type: 'title'
    }];
  };

  // Generate video presentation with Remotion
  const handleGenerateVideo = async () => {
    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedVideoUrl(null);
    setCurrentStep(4);

    try {
      // Get slides content
      const slidesToRender = inputMode === 'raw'
        ? parseRawContentToSlides(rawContent, slideCount)
        : slides.filter(s => s.title.trim() || s.content.trim() || s.bulletPoints.length > 0)
            .map((s, idx, arr) => ({
              title: s.title,
              content: s.content,
              bulletPoints: s.bulletPoints.filter(b => b.trim()),
              type: idx === 0 ? 'title' : idx === arr.length - 1 ? 'closing' : s.bulletPoints.length > 0 ? 'bullets' : 'content',
            }));

      if (slidesToRender.length === 0) {
        throw new Error('No content to render');
      }

      console.log('Slides to render:', slidesToRender);
      toast.loading(`Generating ${slidesToRender.length}-slide video...`, { id: 'video-gen' });

      // Convert brand guidelines to brand style
      const brandStyle = brandGuidelines && useBrandGuidelines ? {
        primaryColor: brandGuidelines.colors?.[0]?.hex || '#6366f1',
        secondaryColor: brandGuidelines.colors?.[1]?.hex || '#8b5cf6',
        accentColor: brandGuidelines.colors?.[2]?.hex || '#f59e0b',
        backgroundColor: '#0f172a',
        textColor: '#f8fafc',
        fontFamily: brandGuidelines.fonts?.[0]?.name || "'Inter', 'Segoe UI', sans-serif",
      } : {
        primaryColor: '#6366f1',
        secondaryColor: '#8b5cf6',
        accentColor: '#f59e0b',
        backgroundColor: '#0f172a',
        textColor: '#f8fafc',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      };

      const response = await fetch(withBackend('/api/video-ppt/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slides: slidesToRender,
          brandStyle,
          options: videoOptions,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate video');
      }

      setGeneratedVideoUrl(data.video.download_url);
      setGenerationProgress(100);

      // Create a pseudo-slide to show in review step
      setGeneratedSlides([{
        slide_index: 0,
        preview_url: null,
        title: presentationTitle,
        video_url: data.video.download_url,
        video_info: data.video,
      }]);

      setCurrentStep(5);
      toast.success(`Video generated! ${data.video.slide_count} slides, ${data.video.duration}s`, { id: 'video-gen' });

    } catch (error) {
      console.error('Video generation error:', error);
      toast.error(error.message || 'Failed to generate video', { id: 'video-gen' });
      setCurrentStep(2);
    } finally {
      setGenerating(false);
    }
  };

  // Generate with HTML slides mode (Gemini text -> Playwright screenshot)
  const handleGenerateWithHTML = async () => {
    // r138b #1: re-entrancy latch — the chat parking auto-trigger (a
    // stale-closure setTimeout) and the ready-panel button can both fire
    // inside the same ~100ms window; two concurrent runs interleave SSE
    // writes and clobber the abort handle. The handle is armed
    // synchronously below and released in finally, so a retry AFTER a
    // stop stays allowed. A state check cannot work here (stale closure).
    if (streamAbortRef.current) return;
    // Determine actual slides to send — prefer outline slides if available
    const slidesToSend = generatedOutline?.slides?.length > 0
      ? generatedOutline.slides.map(s => ({
          title: s.title || '',
          content: s.content || '',
          bulletPoints: (s.bullet_points || s.bulletPoints || []).filter(b => b && b.toString().trim()),
          slide_type: s.slide_type || s.slideType || '',
          needsImage: s.needs_image || s.needsImage || false,
          suggestedImage: s.suggested_image || s.suggestedImage || '',
          chart_config: s.chart_config || s.chartConfig || null,
        }))
      : slides.map(s => ({
          title: s.title,
          content: s.content,
          bulletPoints: s.bulletPoints?.filter(b => b.trim()) || [],
          slide_type: s.slideType || s.slide_type || '',
          needsImage: s.needsImage || s.needs_image || false,
          suggestedImage: s.suggestedImage || s.suggested_image || '',
          chart_config: s.chart_config || s.chartConfig || null,
        }));

    const actualSlideCount = slidesToSend.length;
    console.log(`[HTML] Generating ${actualSlideCount} slides (slideCount=${slideCount}, slides.length=${slides.length}, outline=${generatedOutline?.slides?.length || 0})`);

    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedSlides([]);
    setGenerationStatusMessage('Preparing HTML slides...');
    setCurrentStep(4);
    // r137: fresh run — reset cancel state, arm the stream abort handle
    cancelRequestedRef.current = false;
    setCancelRequested(false);
    // r137b #1: a stale job id from the previous run must not hijack this
    // run's cancel (POSTing to a finished job returns already_finished with
    // a success-sounding toast while THIS run keeps generating)
    currentJobIdRef.current = null;
    streamAbortRef.current = new AbortController();

    // Reset background extraction state
    extractionQueueRef.current = [];
    extractionInProgressRef.current = 0;
    setBgExtractionProgress({ current: 0, total: 0, isActive: false });

    try {
      const formData = new FormData();
      formData.append('presentation_title', presentationTitle);
      formData.append('input_mode', inputMode === 'prompt' ? 'structured' : inputMode);

      if (inputMode === 'raw') {
        formData.append('raw_content', rawContent);
      } else {
        formData.append('slides_content', JSON.stringify(slidesToSend));
      }

      formData.append('slide_count', actualSlideCount.toString());
      formData.append('enhance_text', enhanceText.toString());

      // Build brand guidelines payload — include logo even without full brand guidelines
      const brandPayload = useBrandGuidelines && brandGuidelines
        ? { ...brandGuidelines, logoUrl: logoUrl || brandGuidelines.logoUrl || '' }
        : logoUrl ? { logoUrl } : null;
      if (brandPayload) {
        formData.append('brand_guidelines', JSON.stringify(brandPayload));
      }

      if (questionnaireAnswers?.audience) formData.append('audience', questionnaireAnswers.audience);
      if (questionnaireAnswers?.presentationType) formData.append('presentation_type', questionnaireAnswers.presentationType);
      if (questionnaireAnswers?.visualStyle) formData.append('visual_style', questionnaireAnswers.visualStyle);
      if (questionnaireAnswers?.htmlModel) formData.append('html_model', questionnaireAnswers.htmlModel);
      // Always send imagery_mode (never conditionally): a missing field made
      // the backend default to 'auto', which put photos on a "no images"
      // deck. 'none' also carries the legacy include_images belt.
      const _imageryMode = questionnaireAnswers?.imageryMode || 'auto';
      formData.append('imagery_mode', _imageryMode);
      if (_imageryMode === 'none') formData.append('include_images', 'no');
      if (questionnaireAnswers?.artDirection) formData.append('art_direction', questionnaireAnswers.artDirection);
      if (questionnaireAnswers?.specialInstructions) formData.append('special_instructions', questionnaireAnswers.specialInstructions);
      // PPTX-safe mode: the clarifier's "Edit in PowerPoint" export target —
      // generation avoids constructs the element-level PPTX converter loses.
      if (questionnaireAnswers?.exportTarget === 'pptx') formData.append('pptx_safe', 'true');
      // Optional QA pass. Default 'true' (existing quality behavior); the user can opt out
      // to skip the screenshot+vision review step for faster, cheaper generation.
      formData.append('run_qa', questionnaireAnswers?.runQa === 'no' ? 'false' : 'true');
      // r131: proposal mode — backend appends the facts-only contract
      if (questionnaireAnswers?.proposalMode) formData.append('proposal_mode', 'true');

      // Add theme/color preset for Intelligent mode
      if (aiStylePreset && aiStylePresets[aiStylePreset]) {
        const preset = aiStylePresets[aiStylePreset];
        if (preset.isAiGenerated) {
          formData.append('ai_create_theme', 'true');
        } else {
          formData.append('style_preset', JSON.stringify(preset));
        }
      }
      if (questionnaireAnswers?.customThemeRequest) {
        formData.append('custom_theme_request', questionnaireAnswers.customThemeRequest);
      }

      // Pass original user prompt and web research so HTML generation can follow user instructions
      if (topicPrompt) {
        formData.append('user_prompt', topicPrompt);
      }
      if (webResearchContext) {
        formData.append('web_research', webResearchContext);
      }
      if (excelData) {
        formData.append('excel_data', JSON.stringify(excelData));
      }
      if (redesignImage) {
        formData.append('redesign_reference_image', redesignImage);
        // r176: tells the deck endpoints to REPRODUCE the original's identity
        // instead of the default 'redesign and improve the color scheme'.
        if (keepSourceDesign) formData.append('keep_source_design', 'true');
      }
      // User-provided images to embed verbatim on slides (role==='embed_asset').
      // One append reaches both the background-job and direct-stream endpoints.
      if (embedImages?.length) {
        formData.append('embed_images', JSON.stringify(embedImages));
      }

      // Variation style passthrough — use chosen variation's style reference and pre-generated slides
      if (variationStyleRef) {
        formData.append('style_reference_html', variationStyleRef);
      }
      if (preGeneratedVariationSlides?.length > 0 && variationSampleIndices?.length > 0) {
        formData.append('pre_generated_slides', JSON.stringify(
          preGeneratedVariationSlides.map((slide, i) => ({
            outline_index: variationSampleIndices[i],
            html_source: slide?.html_source || '',
          })).filter(s => s.html_source)
        ));
      } else if (earlyFirstSlideRef.current) {
        // Early slide-1 pipelining: bounded-await the pre-fired slide-1
        // generation and attach it as a pre-generated slide so the backend
        // skips index 0 (it becomes the deck's style anchor). Staleness-
        // checked against the slide content actually being sent; any
        // failure/timeout/mismatch falls back silently to full generation.
        // Awaiting even a still-pending early call is net-positive: it
        // started 10-25s before this point, so it always finishes sooner
        // than a fresh slide-1 generation would.
        const _early = earlyFirstSlideRef.current;
        try {
          const early = await Promise.race([
            _early.promise,
            new Promise(resolve => setTimeout(() => resolve(null), 90000)),
          ]);
          if (early?.html_source && _early.outlineKey === JSON.stringify(slidesToSend[0])) {
            // regen_ok: this slide is a pipelining artifact, NOT user-approved
            // content — the job may re-verify and repair it before it becomes
            // the deck's style anchor (variation picks stay preserved).
            formData.append('pre_generated_slides', JSON.stringify([
              { outline_index: 0, html_source: early.html_source, regen_ok: true },
            ]));
            console.log('[early-slide1] attached pre-generated slide 1');
          } else {
            try { _early.controller?.abort(); } catch { /* noop */ }
            console.log('[early-slide1] discarded (stale, failed, or timed out)');
          }
        } catch { /* promise never rejects, but stay safe */ }
        earlyFirstSlideRef.current = null;
      }
      // Pass variation direction (colors, style hints) so remaining slides match the chosen theme
      if (variationDirection) {
        formData.append('variation_direction', JSON.stringify(variationDirection));
      }

      // Add brand identity ID if selected
      const effectiveBrandId = brandIdentityId || brandIdentityIdRef.current;
      if (effectiveBrandId) formData.append('brand_identity_id', effectiveBrandId);
      if (deckTemplateIdRef.current) formData.append('deck_template_id', deckTemplateIdRef.current);

      // Add user_id for background job tracking
      if (userId) formData.append('user_id', userId);
      // r138b #2: a retry after a cancelled/failed run reuses the
      // placeholder row that run created (server verifies ownership)
      if (retryProjectIdRef.current) formData.append('project_id', retryProjectIdRef.current);

      // If user is logged in, use background job endpoint so generation persists even if tab closes
      let streamUrl = withBackend('/create-html-slides-stream');
      // Capture the job's project id in function scope so the job-not-found
      // fallback below can poll the project record (the job persists slides there)
      // instead of re-generating.
      let jobProjectId = null;
      // r137: Cancel clicked during preparation (before any job exists) —
      // stop here rather than creating a job that would run unwatched.
      if (cancelRequestedRef.current) {
        toast('Stopped.', { id: 'html-gen' });
        setCancelRequested(false); // r137b #4: terminal hygiene, like the 'cancelled' event
        setActiveGeneratingIndices([]);
        setGenerating(false);
        setCurrentStep(2);
        return;
      }
      if (userId) {
        try {
          const jobResp = await fetch(withBackend('/api/create-presentation-job'), {
            method: 'POST',
            body: formData,
          });
          if (jobResp.ok) {
            const { job_id, project_id } = await jobResp.json();
            if (project_id) {
              jobProjectId = project_id;
              retryProjectIdRef.current = project_id; // r138b #2
              onProjectCreated?.(project_id, presentationTitle || 'Untitled Presentation');
              // Store so handleSaveProject can reference it instead of creating a duplicate
              setProjectId(project_id);
            }
            if (job_id) {
              streamUrl = withBackend(`/api/jobs/${job_id}/stream`);
              // Retain the job id so the Cancel button can stop it mid-flight.
              currentJobIdRef.current = job_id;
              // r137b #5: Cancel clicked while this POST was in flight —
              // the abort handle is already spent, so the run finalizes
              // locally via the AbortError path; forward the stop to the
              // server too (awaited, best-effort — the job persists its
              // slides to the project either way).
              if (cancelRequestedRef.current) {
                try { await fetch(withBackend(`/api/jobs/${job_id}/cancel`), { method: 'POST' }); } catch { /* best effort */ }
              }
            }
          }
        } catch (jobErr) {
          console.warn('[CreatePPT] Job endpoint failed, falling back to direct stream:', jobErr);
        }
      }

      // Fetch and read SSE stream, with automatic fallback to direct stream if job is not found.
      // "Job not found" happens when Render restarts between job creation and stream connection —
      // the in-memory job dict is wiped. We retry transparently with the direct endpoint.
      const readStream = async (url, isGet) => {
        const resp = await fetch(url, {
          method: isGet ? 'GET' : 'POST',
          body: isGet ? undefined : formData,
          signal: streamAbortRef.current?.signal, // r137: local cancel
        });
        if (!resp.ok) throw new Error('Failed to start HTML slide generation');

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // A job stream that ends WITHOUT a terminal event (complete/
        // cancelled/fatal_error) is a failure, not a success: the SSE died
        // early (worker recycle, proxy timeout) while the job keeps writing
        // slides server-side. Treating it as success left the UI stuck on
        // the 1-slide preview — and a later autosave clobbered the real deck.
        let sawTerminal = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6));
                switch (event.type) {
                  case 'start':
                    toast.loading(`Generating ${event.total} HTML slides...`, { id: 'html-gen' });
                    break;
                  case 'progress': {
                    const htmlPct = event.total > 0 ? Math.round((event.current / event.total) * 100) : 0;
                    setGenerationProgress(isNaN(htmlPct) ? 0 : htmlPct);
                    setGenerationStatusMessage(event.status || `Rendering slide ${event.current || '?'} of ${event.total || '?'}...`);
                    if (event.generating_indices) setActiveGeneratingIndices(event.generating_indices);
                    toast.loading(event.status, { id: 'html-gen' });
                    break;
                  }
                  case 'slide': {
                    const slideIndex = event.index ?? event.slide?.slide_index;
                    setGeneratedSlides(prev => {
                      const updated = [...prev];
                      while (updated.length <= slideIndex) updated.push(null);
                      updated[slideIndex] = event.slide;
                      return updated;
                    });
                    setActiveGeneratingIndices(prev => prev.filter(i => i !== slideIndex));
                    setCurrentSlideIndex(slideIndex ?? 0);
                    break;
                  }
                  case 'error':
                    toast.error(`Slide ${(event.slide_index || 0) + 1} failed: ${event.error}`);
                    break;
                  case 'complete':
                    sawTerminal = true;
                    toast.success(`Generated ${event.total} HTML slides!`, { id: 'html-gen' });
                    onChatMessage?.(`Presentation generated! ${event.total} slides ready. You can now edit them or ask questions.`);
                    setActiveGeneratingIndices([]);
                    onUsageRefresh?.();
                    setGeneratedSlides(prev => {
                      const cleaned = prev.filter(s => s !== null && s !== undefined);
                      setTimeout(() => { setGenerating(false); setCurrentStep(5); }, 50);
                      return cleaned;
                    });
                    break;
                  case 'cancelled':
                    sawTerminal = true;
                    // User stopped the job. The backend preserves slides already
                    // rendered before the cancel; keep them and finish gracefully.
                    toast.success(`Stopped — ${event.completed_slides ?? 0} slide(s) kept.`, { id: 'html-gen' });
                    setActiveGeneratingIndices([]);
                    setCancelRequested(false);
                    onUsageRefresh?.();
                    setGeneratedSlides(prev => {
                      const cleaned = prev.filter(s => s !== null && s !== undefined);
                      setTimeout(() => { setGenerating(false); setCurrentStep(5); }, 50);
                      return cleaned;
                    });
                    break;
                  case 'fatal_error':
                    // Propagate with a flag so the caller can retry if it's a job-not-found
                    throw Object.assign(new Error(event.error), { isJobNotFound: event.error === 'Job not found' });
                  case 'keepalive':
                    break;
                  default:
                    break;
                }
              } catch (parseError) {
                if (parseError.message && !parseError.message.includes('Unexpected')) throw parseError;
                console.warn('Failed to parse SSE event:', parseError);
              }
            }
          }
        }
        if (isGet && !sawTerminal) {
          // Route into the same recovery as an explicit job-not-found: the
          // project-polling loop below pulls the job's slides from the DB.
          throw Object.assign(new Error('Job stream ended before completing'), { isJobNotFound: true });
        }
      };

      const isJobStream = streamUrl.includes('/api/jobs/');
      try {
        await readStream(streamUrl, isJobStream);
      } catch (streamErr) {
        if (streamErr.isJobNotFound && jobProjectId) {
          // The job exists but its in-memory stream landed on a different worker
          // (the job dict is per-worker), so this connection can't see it.
          // Do NOT re-generate via the direct stream — that double-bills the
          // model, orphans the original job, and the second long SSE connection
          // can drop slides. The job is already persisting every slide to the
          // project record, so poll that and merge slides until it finishes.
          console.warn('[CreatePPT] Job stream not on this worker; polling project record instead of re-generating');
          toast.loading('Generating slides...', { id: 'html-gen' });
          let done = false;
          let lastCount = -1;
          let lastProgressAt = Date.now();
          let lastUpdatedAt = null;
          const NO_PROGRESS_LIMIT_MS = 15 * 60 * 1000; // dead job: exit with partials
          while (!done) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const { data, error: pErr } = await supabaseHelpers.getProjectFull(jobProjectId);
              if (pErr || !data) continue;
              const polled = (data.analysis_data?.slides || []).filter(Boolean);
              const total = data.total_slides || data.slide_count || polled.length || 0;
              // Round 75: the QA phase repairs slides WITHOUT changing the
              // count — a long QA pass tripped the 15-minute "stalled" exit
              // on a healthy, actively-writing job. Any row write counts.
              if (polled.length !== lastCount || (data.updated_at && data.updated_at !== lastUpdatedAt)) {
                lastCount = polled.length;
                lastUpdatedAt = data.updated_at || lastUpdatedAt;
                lastProgressAt = Date.now();
              }
              if (polled.length > 0) {
                setGeneratedSlides(polled);
                setCurrentSlideIndex(polled.length - 1);
                setGenerationProgress(total > 0 ? Math.round((polled.length / total) * 100) : 0);
                setGenerationStatusMessage(`Rendering slide ${polled.length} of ${total}...`);
                toast.loading(`Generating slide ${polled.length} of ${total}...`, { id: 'html-gen' });
              }
              if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
                done = true;
                const finalSlides = (data.analysis_data?.slides || []).filter(Boolean);
                setGeneratedSlides(finalSlides);
                setActiveGeneratingIndices([]);
                // A user cancel is recorded as status='failed' + metadata.cancelled
                // (the status enum has no 'cancelled') — show the graceful stop.
                const wasCancelled = data.status === 'cancelled' || data.analysis_data?.metadata?.cancelled;
                if (data.status === 'completed') {
                  toast.success(`Generated ${finalSlides.length} HTML slides!`, { id: 'html-gen' });
                  onChatMessage?.(`Presentation generated! ${finalSlides.length} slides ready. You can now edit them or ask questions.`);
                } else if (wasCancelled) {
                  toast.success(`Stopped — ${finalSlides.length} slide(s) kept.`, { id: 'html-gen' });
                } else {
                  toast.error(`Generation failed after ${finalSlides.length} slide(s).`, { id: 'html-gen' });
                }
                onUsageRefresh?.();
                setTimeout(() => { setGenerating(false); setCurrentStep(5); }, 50);
              } else if (Date.now() - lastProgressAt > NO_PROGRESS_LIMIT_MS) {
                // The job died without a terminal status (deploy/worker
                // recycle). Don't spin forever — surface what was recovered.
                done = true;
                setActiveGeneratingIndices([]);
                toast.error(polled.length > 0
                  ? `Generation stalled — keeping the ${polled.length} slide(s) that finished.`
                  : 'Generation stalled on the server. Please try again.', { id: 'html-gen' });
                setTimeout(() => { setGenerating(false); if (polled.length > 0) setCurrentStep(5); }, 50);
              }
            } catch (_) { /* transient Supabase hiccup — keep polling */ }
          }
        } else if (streamErr.isJobNotFound) {
          // No project id to poll (rare) — last resort, regenerate directly.
          console.warn('[CreatePPT] Job not found and no project id; falling back to direct stream');
          await readStream(withBackend('/create-html-slides-stream'), false);
        } else {
          throw streamErr;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || cancelRequestedRef.current) {
        // r137: user-initiated local stop (no background job to cancel) —
        // mirror the 'cancelled' event: keep finished slides, no error.
        setActiveGeneratingIndices([]);
        setCancelRequested(false);
        setGeneratedSlides(prev => {
          const cleaned = prev.filter(sl => sl !== null && sl !== undefined);
          toast.success(cleaned.length > 0 ? `Stopped — ${cleaned.length} slide(s) kept.` : 'Stopped.', { id: 'html-gen' });
          setTimeout(() => { setGenerating(false); setCurrentStep(cleaned.length > 0 ? 5 : 2); }, 50);
          return cleaned;
        });
        return;
      }
      console.error('HTML generation error:', error);
      const isNetworkError = error.message?.includes('network') || error.name === 'TypeError' || error.message?.includes('Failed to fetch');
      // If we already generated some slides, stay on step 4 and show partial results
      setGeneratedSlides(prev => {
        const validSlides = prev.filter(Boolean);
        if (validSlides.length > 0) {
          toast.error(`Connection lost after ${validSlides.length} slides. You can continue with what was generated.`, { id: 'html-gen' });
          return prev;
        }
        toast.error(isNetworkError ? 'Network error — please check your connection and try again' : (error.message || 'Failed to generate HTML slides'), { id: 'html-gen' });
        setCurrentStep(2);
        return prev;
      });
    } finally {
      setGenerating(false);
      // r137b #2: only an in-flight HTML run owns a live abort handle — the
      // cancel handler uses this to tell runs it can stop from runs it can't
      streamAbortRef.current = null;
    }
  };

  // AI Style presets for Nano Banana Pro mode
  const aiStylePresets = {
    'ai-create': {
      name: 'Let AI Create',
      description: 'AI analyzes your content and creates a unique theme',
      colors: null, // No preset colors - AI decides
      style: null, // No preset style - AI analyzes content and creates appropriate theme
      isAiGenerated: true
    },
    'brand-reference': {
      name: 'Brand Reference',
      description: 'Match the style of your uploaded slides',
      colors: null,
      style: null,
      isBrandReference: true
    },
    'modern-dark': {
      name: 'Modern Dark',
      description: 'Sleek dark theme with vibrant accents',
      colors: { primary: '#a855f7', secondary: '#6366f1', background: '#1a1a2e', text: '#ffffff' },
      style: 'dark gradient background, purple/indigo accents, white text, modern minimalist'
    },
    'clean-light': {
      name: 'Clean Light',
      description: 'Professional light theme',
      colors: { primary: '#3b82f6', secondary: '#1e40af', background: '#ffffff', text: '#1e293b' },
      style: 'clean white background, blue accents, dark text, corporate professional'
    },
    'bold-gradient': {
      name: 'Bold Gradient',
      description: 'Eye-catching gradient backgrounds',
      colors: { primary: '#f97316', secondary: '#ea580c', background: '#7c3aed', text: '#ffffff' },
      style: 'vibrant purple-to-orange gradient background, orange accents, white text, bold creative'
    },
    'nature-green': {
      name: 'Nature Green',
      description: 'Fresh and eco-friendly theme',
      colors: { primary: '#10b981', secondary: '#059669', background: '#ecfdf5', text: '#064e3b' },
      style: 'soft green tones, emerald accents, nature-inspired, fresh and sustainable'
    },
    'corporate-blue': {
      name: 'Corporate Blue',
      description: 'Traditional business style',
      colors: { primary: '#1e40af', secondary: '#1e3a8a', background: '#f8fafc', text: '#1e293b' },
      style: 'navy blue accents, light gray background, professional corporate, trustworthy'
    },
    'sunset-warm': {
      name: 'Sunset Warm',
      description: 'Warm and inviting tones',
      colors: { primary: '#f59e0b', secondary: '#d97706', background: '#1c1917', text: '#fef3c7' },
      style: 'warm amber and gold accents, dark background, sunset inspired, welcoming'
    }
  };

  // Generate presentation with AI
  const handleGenerate = async () => {
    if (!presentationTitle.trim()) {
      toast.error('Please enter a presentation title');
      return;
    }

    // Validate based on input mode
    if (inputMode === 'raw') {
      if (!rawContent.trim()) {
        toast.error('Please enter some content');
        return;
      }
    } else {
      const hasContent = slides.some(s => s.title.trim() || s.content.trim() || s.bulletPoints.some(b => b.trim()));
      if (!hasContent) {
        toast.error('Please add some content to at least one slide');
        return;
      }
    }

    // Check if using template mode
    if (generationMode === 'templates') {
      return handleGenerateWithTemplates();
    }

    // Check if using PPTX template mode
    if (generationMode === 'pptx') {
      return handleGenerateWithPPTXTemplate();
    }

    // Check if using video mode
    if (generationMode === 'video') {
      return handleGenerateVideo();
    }

    // Check if using HTML slides mode
    if (generationMode === 'html') {
      return handleGenerateWithHTML();
    }

    // If enhance text is enabled, show outline review first
    if (enhanceText && inputMode !== 'prompt') {
      return handleEnhanceAndReview();
    }

    setGenerating(true);
    setGenerationProgress(0);
    setGeneratedSlides([]);
    setGenerationStatusMessage('Analyzing content...');
    setCurrentStep(4); // Go to generating step — shows progressive slide grid

    // Reset background extraction state for new generation
    extractionQueueRef.current = [];
    extractionInProgressRef.current = 0;
    setBgExtractionProgress({ current: 0, total: 0, isActive: false });

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('presentation_title', presentationTitle);
      formData.append('input_mode', inputMode);
      
      if (inputMode === 'raw') {
        formData.append('raw_content', rawContent);
      } else {
        formData.append('slides_content', JSON.stringify(slides));
      }
      
      formData.append('slide_count', slideCount.toString());
      formData.append('user_id', userId || '');
      formData.append('enhance_text', enhanceText.toString());

      if (brandGuidelines && useBrandGuidelines) {
        formData.append('brand_guidelines', JSON.stringify(brandGuidelines));
      }

      // Add brand references if any
      if (brandReferences.images.length > 0) {
        formData.append('brand_references', JSON.stringify(brandReferences.images.map(img => img.base64)));
        formData.append('reference_weight', brandReferences.weight);
      }

      if (templateFile) {
        formData.append('template', templateFile);
      }

      // Add AI style preset for Nano Banana Pro
      if (aiStylePreset && aiStylePresets[aiStylePreset]) {
        const preset = aiStylePresets[aiStylePreset];
        if (preset.isAiGenerated) {
          // Let AI analyze content and create a unique theme
          formData.append('ai_create_theme', 'true');
        } else {
          formData.append('style_preset', JSON.stringify(preset));
        }
      }

      // Add custom theme request if provided
      if (questionnaireAnswers?.customThemeRequest) {
        formData.append('custom_theme_request', questionnaireAnswers.customThemeRequest);
      }

      // Add questionnaire answers for design context
      if (questionnaireAnswers?.audience) formData.append('questionnaire_audience', questionnaireAnswers.audience);
      if (questionnaireAnswers?.presentationType) formData.append('questionnaire_type', questionnaireAnswers.presentationType);
      if (questionnaireAnswers?.visualStyle) formData.append('questionnaire_visual_style', questionnaireAnswers.visualStyle);
      if (questionnaireAnswers?.includeImages) formData.append('questionnaire_include_images', questionnaireAnswers.includeImages);

      // Add brand identity ID if selected
      if (brandIdentityId || brandIdentityIdRef.current) formData.append('brand_identity_id', brandIdentityId || brandIdentityIdRef.current);
      if (deckTemplateIdRef.current) formData.append('deck_template_id', deckTemplateIdRef.current);

      // Add variation direction for style context (from variation preview selection)
      if (variationDirection) formData.append('variation_direction', JSON.stringify(variationDirection));

      // Pass user's original prompt and custom instructions so the imaginative
      // generator knows the intent beyond the structured slide list.
      if (topicPrompt) formData.append('user_prompt', topicPrompt);
      if (aiInstructions) formData.append('ai_instructions', aiInstructions);

      // Use streaming endpoint for progressive slide display
      const response = await fetch(withBackend('/create-ppt-stream'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to start slide generation');
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));

              switch (event.type) {
                case 'start':
                  toast.loading(`Generating ${event.total} slides...`, { id: 'generation' });
                  onChatMessage?.(`Generating ${event.total} slides...`);
                  break;
                case 'progress': {
                  const pct = event.total > 0 ? Math.round((event.current / event.total) * 100) : 0;
                  setGenerationProgress(isNaN(pct) ? 0 : pct);
                  setGenerationStatusMessage(event.status || `Designing slide ${event.current || '?'} of ${event.total || '?'}...`);
                  toast.loading(event.status, { id: 'generation' });
                  // Track which slides are generating in parallel
                  if (event.generating_indices) {
                    setActiveGeneratingIndices(event.generating_indices);
                  }
                  break;
                }
                case 'slide': {
                  const slideIndex = event.index ?? event.slide?.slide_index;
                  // Place slide at its correct index (supports out-of-order arrival)
                  setGeneratedSlides(prev => {
                    const updated = [...prev];
                    if (slideIndex !== undefined && slideIndex !== null) {
                      // Expand array if needed
                      while (updated.length <= slideIndex) updated.push(null);
                      updated[slideIndex] = event.slide;
                    } else {
                      // Fallback: append if no index provided
                      updated.push(event.slide);
                    }
                    return updated;
                  });
                  // Remove from active generating list
                  if (slideIndex !== undefined && slideIndex !== null) {
                    setActiveGeneratingIndices(prev => prev.filter(i => i !== slideIndex));
                  }
                  setCurrentSlideIndex(slideIndex ?? 0);
                  // Queue this slide for background extraction immediately
                  queueSlideForExtraction(event.slide);
                  break;
                }
                case 'error':
                  toast.error(`Slide ${event.slide_index + 1} failed: ${event.error}`);
                  // Remove from active generating list
                  setActiveGeneratingIndices(prev => prev.filter(i => i !== event.slide_index));
                  break;
                case 'complete':
                  toast.success(`Generated ${event.total} slides!`, { id: 'generation' });
                  onChatMessage?.(`All ${event.total} slides generated! You can now edit them.`);
                  setActiveGeneratingIndices([]);
                  // Clean up any null slots from out-of-order generation, then transition to review
                  setGeneratedSlides(prev => {
                    const cleaned = prev.filter(s => s !== null && s !== undefined);
                    // Use setTimeout to ensure the cleaned slides state is committed before step change
                    setTimeout(() => {
                      setGenerating(false);
                      setCurrentStep(5);

                      // After Nano Banana generation completes, overlay images on slides that need them
                      if (questionnaireAnswers?.includeImages === 'yes') {
                        overlayNanaBananaImages(cleaned);
                      }
                    }, 50);
                    return cleaned;
                  });
                  break;
                case 'fatal_error':
                  throw new Error(event.error);
                default:
                  break;
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE event:', parseError);
            }
          }
        }
      }

    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.message || 'Failed to generate presentation', { id: 'generation' });
      setCurrentStep(2);
    } finally {
      setGenerating(false);
    }
  };

  // Overlay AI-generated images on Nano Banana slides that need them
  const overlayNanaBananaImages = async (slides) => {
    // Build list of slides needing images with their array indices
    const imageTasks = [];
    slides.forEach((s, arrayIdx) => {
      if (s && s.needsImage && s.suggestedImage) {
        imageTasks.push({ slide: s, arrayIdx });
      }
    });
    if (imageTasks.length === 0) return;

    toast.loading(`Generating ${imageTasks.length} image${imageTasks.length > 1 ? 's' : ''}...`, { id: 'nano-images' });

    const loadImg = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

    const overlayImageOnSlide = async (slidePreviewUrl, imageUrl) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1920;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');

      // Draw original slide
      const slideImg = await loadImg(slidePreviewUrl);
      ctx.drawImage(slideImg, 0, 0, 1920, 1080);

      // Image area on right side (matching text-with-image: x:60%, y:5%, w:36%, h:90%)
      const imgX = Math.round(0.60 * 1920);
      const imgY = Math.round(0.05 * 1080);
      const imgW = Math.round(0.36 * 1920);
      const imgH = Math.round(0.90 * 1080);
      const radius = 16;

      // Rounded rect clip path
      const photo = await loadImg(imageUrl);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(imgX + radius, imgY);
      ctx.lineTo(imgX + imgW - radius, imgY);
      ctx.quadraticCurveTo(imgX + imgW, imgY, imgX + imgW, imgY + radius);
      ctx.lineTo(imgX + imgW, imgY + imgH - radius);
      ctx.quadraticCurveTo(imgX + imgW, imgY + imgH, imgX + imgW - radius, imgY + imgH);
      ctx.lineTo(imgX + radius, imgY + imgH);
      ctx.quadraticCurveTo(imgX, imgY + imgH, imgX, imgY + imgH - radius);
      ctx.lineTo(imgX, imgY + radius);
      ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
      ctx.closePath();
      ctx.clip();

      // Cover-fit the photo
      const pRatio = photo.naturalWidth / photo.naturalHeight;
      const bRatio = imgW / imgH;
      let sx, sy, sw, sh;
      if (pRatio > bRatio) {
        sh = photo.naturalHeight; sw = sh * bRatio;
        sx = (photo.naturalWidth - sw) / 2; sy = 0;
      } else {
        sw = photo.naturalWidth; sh = sw / bRatio;
        sx = 0; sy = (photo.naturalHeight - sh) / 2;
      }
      ctx.drawImage(photo, sx, sy, sw, sh, imgX, imgY, imgW, imgH);
      ctx.restore();

      return canvas.toDataURL('image/png');
    };

    // Generate images in parallel
    const results = await Promise.allSettled(
      imageTasks.map(async ({ slide, arrayIdx }) => {
        try {
          const response = await fetch(withBackend('/api/generate-slide-image'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image_description: slide.suggestedImage, slide_title: slide.title || '' }),
          });
          if (!response.ok) return null;
          const data = await response.json();
          const imageUrl = data.image_url;
          if (!imageUrl || !slide.preview_url) return null;

          const composited = await overlayImageOnSlide(slide.preview_url, imageUrl);
          return { arrayIdx, composited };
        } catch (err) {
          console.warn(`Failed to generate/overlay image for slide ${arrayIdx + 1}:`, err);
          return null;
        }
      })
    );

    // Update slides with composited images
    const successes = results.filter(r => r.status === 'fulfilled' && r.value).map(r => r.value);
    if (successes.length > 0) {
      setGeneratedSlides(prev => {
        const updated = [...prev];
        for (const { arrayIdx, composited } of successes) {
          if (updated[arrayIdx]) {
            updated[arrayIdx] = {
              ...updated[arrayIdx],
              preview_url: composited,
              extractedLayers: updated[arrayIdx].extractedLayers ? {
                ...updated[arrayIdx].extractedLayers,
                background: composited,
                background_image: composited,
              } : undefined,
            };
          }
        }
        return updated;
      });
      toast.success(`${successes.length} image${successes.length > 1 ? 's' : ''} added!`, { id: 'nano-images' });
    } else {
      toast.dismiss('nano-images');
    }
  };


  // --- Region highlighting helpers for Edit Dialog ---
  const getRegionPercentCoords = (e) => {
    const container = editSlidePreviewRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handleRegionMouseDown = (e) => {
    e.preventDefault();
    const coords = getRegionPercentCoords(e);
    if (!coords) return;
    setIsDrawingRegion(true);
    setDrawStart(coords);
    setDrawCurrent(coords);
    setHighlightRegion(null);
  };

  const handleRegionMouseMove = (e) => {
    if (!isDrawingRegion) return;
    const coords = getRegionPercentCoords(e);
    if (coords) setDrawCurrent(coords);
  };

  const handleRegionMouseUp = () => {
    if (!isDrawingRegion || !drawStart || !drawCurrent) {
      setIsDrawingRegion(false);
      return;
    }
    setIsDrawingRegion(false);
    const rx = Math.min(drawStart.x, drawCurrent.x);
    const ry = Math.min(drawStart.y, drawCurrent.y);
    const rw = Math.abs(drawCurrent.x - drawStart.x);
    const rh = Math.abs(drawCurrent.y - drawStart.y);
    if (rw > 2 && rh > 2) {
      setHighlightRegion({ x: rx, y: ry, width: rw, height: rh });
    }
    setDrawStart(null);
    setDrawCurrent(null);
  };

  const getRegionStyle = () => {
    if (highlightRegion) {
      return { left: `${highlightRegion.x}%`, top: `${highlightRegion.y}%`, width: `${highlightRegion.width}%`, height: `${highlightRegion.height}%` };
    }
    if (isDrawingRegion && drawStart && drawCurrent) {
      const lx = Math.min(drawStart.x, drawCurrent.x);
      const ly = Math.min(drawStart.y, drawCurrent.y);
      return { left: `${lx}%`, top: `${ly}%`, width: `${Math.abs(drawCurrent.x - drawStart.x)}%`, height: `${Math.abs(drawCurrent.y - drawStart.y)}%` };
    }
    return {};
  };

  const toggleReferenceSlide = (idx) => {
    setReferenceSlideIndices(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Edit generated slides
  const handleEditSlides = async () => {
    if (!editPrompt.trim()) {
      toast.error('Please enter an edit instruction');
      return;
    }

    setEditingSlides(true);
    setShowEditDialog(false);

    // Determine which slides to edit based on scope
    const slidesToEdit = editScope === 'current'
      ? [generatedSlides[currentSlideIndex]]
      : generatedSlides;

    const toastMessage = editScope === 'current'
      ? `Editing slide ${currentSlideIndex + 1}...`
      : `Editing ${generatedSlides.length} slides...`;
    toast.loading(toastMessage, { id: 'edit-slides' });

    try {
      // Build reference slides payload
      const referenceSlides = referenceSlideIndices.map(idx => ({
        index: idx,
        preview_url: generatedSlides[idx]?.preview_url || null,
        title: generatedSlides[idx]?.title || `Slide ${idx + 1}`,
      })).filter(s => s.preview_url);

      const response = await fetch(withBackend('/edit-ppt-slides'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slides: slidesToEdit,
          edit_prompt: editPrompt,
          brand_guidelines: useBrandGuidelines ? brandGuidelines : null,
          user_id: userId || '',
          edit_scope: editScope,
          current_slide_index: currentSlideIndex,
          highlight_region: highlightRegion,
          reference_slides: referenceSlides,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to edit slides');
      }

      const data = await response.json();

      // Update slides based on scope
      if (editScope === 'current') {
        setGeneratedSlides(prev => prev.map((slide, idx) =>
          idx === currentSlideIndex ? data.slides[0] : slide
        ));
      } else {
        setGeneratedSlides(data.slides);
      }

      setEditPrompt('');
      setHighlightRegion(null);
      setReferenceSlideIndices([]);
      toast.success(editScope === 'current' ? 'Slide updated!' : 'All slides updated!', { id: 'edit-slides' });

    } catch (error) {
      console.error('Edit error:', error);
      toast.error(error.message || 'Failed to edit slides', { id: 'edit-slides' });
    } finally {
      setEditingSlides(false);
    }
  };

  // Detect if the user wants to apply an edit to all slides
  const isApplyToAllSlides = (msg) => {
    const lower = msg.toLowerCase();
    return [
      /\ball\s+(?:of\s+)?(?:the\s+)?slides?\b/,
      /\bevery\s+(?:single\s+)?slide\b/,
      /\beach\s+(?:and\s+every\s+)?slide\b/,
      /\bacross\s+(?:all\s+)?(?:the\s+)?slides?\b/,
      /\bwhole\s+presentation\b/,
      /\bentire\s+(?:presentation|deck)\b/,
      /\ball\s+(?:of\s+)?(?:the\s+)?pages?\b/,
      /\bevery\s+(?:single\s+)?page\b/,
      /\bon\s+(?:all|every)\s+(?:of\s+)?(?:the\s+)?slides?\b/,
      /\bfrom\s+(?:all|every)\s+(?:of\s+)?(?:the\s+)?slides?\b/,
    ].some(p => p.test(lower));
  };

  // Detect specific slide reference in the message (e.g., "slide 3", "slide #5", "page 2")
  const parseSlideReference = (msg) => {
    const patterns = [
      /\bslide\s*#?\s*(\d+)\b/i,
      /\bpage\s*#?\s*(\d+)\b/i,
      /\bslide\s+number\s*(\d+)\b/i,
      /\b(?:on|in|for|edit|update|change|modify|fix)\s+(?:slide|page)\s*#?\s*(\d+)\b/i,
      /\bسلايد\s*(\d+)\b/i, // Arabic: سلايد 3
      /\bصفحة\s*(\d+)\b/i, // Arabic: صفحة 3
    ];
    for (const p of patterns) {
      const m = msg.match(p);
      if (m) {
        const slideNum = parseInt(m[1], 10);
        if (slideNum >= 1 && slideNum <= generatedSlides.length) {
          return slideNum - 1; // Convert to 0-based index
        }
      }
    }
    return null; // No specific slide referenced
  };

  // Structural edits: add / delete / duplicate / reorder entire slides.
  // Called from UnifiedWorkspace after /api/chat/route classifies intent as "structural".
  // op: { message, operation, position, target_slide, content_brief }
  const handleStructuralEdit = async (op) => {
    if (externalFeedRef.current) {
      // r140b P1: a splice during the feed shifts every later adopt index
      toast.info('Still generating — structural edits unlock when the deck finishes.');
      return;
    }
    const { message = '', operation, position, target_slide, target_uid, to_position, content_brief, reference_images: opRefImages } = op || {};
    const htmlSlides = generatedSlides.filter(s => s?.html_source);
    if (htmlSlides.length === 0) {
      toast.error('No slides to modify');
      return { ok: false };
    }
    // r145: resolve by STABLE ID first — the planner numbers slides against
    // the pre-plan deck, and earlier ops splice the live array ("delete
    // slides 3 and 5" used to delete 3 then the original 6).
    const resolveIdx = (fallbackIdx) => {
      if (target_uid) {
        const byUid = generatedSlidesRef.current.findIndex(s => s?._uid === target_uid);
        if (byUid !== -1) return byUid;
      }
      return fallbackIdx;
    };

    // Log user message
    setHtmlChatHistory(prev => [...prev, {
      role: 'user', content: message, timestamp: Date.now(),
    }]);

    try {
      if (operation === 'delete') {
        // 1-indexed target → 0-indexed; fall back to current slide
        const idx = resolveIdx(target_slide != null ? (target_slide - 1) : currentSlideIndex);
        if (idx < 0 || idx >= generatedSlides.length) {
          toast.error(`Slide ${target_slide} not found`);
          return { ok: false };
        }
        // r145: chat deletes had NO undo and autosave made them permanent —
        // keep the slide and offer a 12s Undo that re-inserts it.
        const deletedSlide = generatedSlidesRef.current[idx];
        setGeneratedSlides(prev => renumberSlideCounters(prev.filter((_, i) => i !== idx), prev.length));
        setCurrentSlideIndex(Math.max(0, Math.min(idx, generatedSlides.length - 2)));
        const msg = `Deleted slide ${idx + 1}`;
        setHtmlChatHistory(prev => [...prev, { role: 'bot', content: msg, timestamp: nextMsgId() }]);
        onChatMessage?.(msg);
        toast.success(msg, {
          duration: 12000,
          action: {
            label: 'Undo',
            onClick: () => setGeneratedSlides(prev => {
              const next = [...prev];
              next.splice(Math.min(idx, next.length), 0, deletedSlide);
              return renumberSlideCounters(next, prev.length);
            }),
          },
        });
        return { ok: true };
      }

      if (operation === 'reorder') {
        // r145: the planner advertised reorder but no executor existed —
        // "move slide 7 before slide 2" planned successfully and did nothing.
        const fromIdx = resolveIdx(target_slide != null ? (target_slide - 1) : currentSlideIndex);
        let toIdx = typeof to_position === 'number' ? to_position - 1 : null;
        if (to_position === 'start') toIdx = 0;
        if (to_position === 'end') toIdx = generatedSlides.length - 1;
        if (fromIdx < 0 || fromIdx >= generatedSlides.length || toIdx == null) {
          toast.error('Reorder needs a valid slide and destination');
          return { ok: false };
        }
        toIdx = Math.max(0, Math.min(toIdx, generatedSlides.length - 1));
        setGeneratedSlides(prev => {
          const next = [...prev];
          const [moved] = next.splice(fromIdx, 1);
          next.splice(toIdx, 0, moved);
          return renumberSlideCounters(next, next.length);
        });
        setCurrentSlideIndex(toIdx);
        const msg = `Moved slide ${fromIdx + 1} to position ${toIdx + 1}`;
        setHtmlChatHistory(prev => [...prev, { role: 'bot', content: msg, timestamp: nextMsgId() }]);
        onChatMessage?.(msg);
        toast.success(msg);
        return { ok: true };
      }

      if (operation === 'duplicate') {
        const srcIdx = resolveIdx(target_slide != null ? (target_slide - 1) : currentSlideIndex);
        if (srcIdx < 0 || srcIdx >= generatedSlides.length) {
          toast.error(`Slide ${target_slide} not found`);
          return { ok: false };
        }
        const src = generatedSlides[srcIdx];
        // Insert duplicate immediately after the source slide
        const insertAt = srcIdx + 1;
        const clone = { ...src, _duplicated: true, _uid: genUid() }; // r145: fresh identity
        setGeneratedSlides(prev => {
          const next = [...prev];
          next.splice(insertAt, 0, clone);
          return renumberSlideCounters(next, prev.length);
        });
        setCurrentSlideIndex(insertAt);
        const msg = `Duplicated slide ${srcIdx + 1}`;
        setHtmlChatHistory(prev => [...prev, { role: 'bot', content: msg, timestamp: Date.now() }]);
        onChatMessage?.(msg);
        toast.success(msg);
        return;
      }

      if (operation === 'add') {
        // Determine insert position (0-indexed)
        let insertAt;
        if (position === 'start') {
          insertAt = 0;
        } else if (position === 'end' || position == null) {
          insertAt = generatedSlides.length;
        } else if (typeof position === 'number') {
          // position is 1-indexed "the new slide becomes slide N"
          insertAt = Math.max(0, Math.min(position - 1, generatedSlides.length));
        } else {
          insertAt = generatedSlides.length;
        }

        // Pick a style reference: prefer slide 1 (usually most representative of the deck's style),
        // or the current slide if slide 1 isn't an HTML slide
        const styleRef = generatedSlides.find(s => s?.html_source) || generatedSlides[currentSlideIndex];
        if (!styleRef?.html_source) {
          toast.error('No existing HTML slide to use as style reference');
          return { ok: false };
        }

        // Add progress message
        const progressId = Date.now();
        setHtmlChatHistory(prev => [...prev, {
          role: 'bot', content: 'Generating new slide...', timestamp: progressId, isProgress: true,
        }]);

        const totalAfter = generatedSlides.length + 1;

        // Build deck context so the LLM can generate meaningful content
        // when the user doesn't specify what to add
        const slideTitles = generatedSlides
          .map((s, i) => `Slide ${i + 1}: ${s.title || '(untitled)'}`)
          .join('\n');
        const deckContext = [
          topicPrompt ? `Original prompt: "${topicPrompt}"` : '',
          `Existing slides:\n${slideTitles}`,
        ].filter(Boolean).join('\n\n');

        const orchCtxAdd = getOrchestrationCtx();
        const addSlidePayload = {
          style_reference_html: styleRef.html_source,
          brief: content_brief || message,
          slide_number: insertAt + 1,
          total_slides: totalAfter,
          brand_context: '',
          html_model: htmlEditModel,
          deck_context: deckContext,
          user_id: userId || '',
          ...orchCtxAdd,
        };
        // Pass attached chat images so the LLM can place them on the new slide
        // ("create a new slide with these images", "add these to slide X").
        const combinedRefImages = [...(opRefImages || []), ...htmlChatImages];
        if (combinedRefImages.length > 0) {
          addSlidePayload.reference_images = combinedRefImages;
        }
        const resp = await fetch(withBackend('/api/chat/add-slide'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify(addSlidePayload),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          if (resp.status === 403 && err.error === 'usage_limit_exceeded') {
            toast.error(err.details?.reason === 'trial_expired'
              ? 'Your 7-day trial has ended. Upgrade to keep creating.'
              : 'You\'ve reached your monthly slide limit for this model tier. Upgrade for more.');
            if (onUsageRefresh) onUsageRefresh(userId);
            return { ok: false, quota: true }; // r146b: a quota add is a FAILURE and stops the plan
          }
          throw new Error(err.error || 'Failed to generate new slide');
        }

        // Refresh usage after successful add
        if (onUsageRefresh) onUsageRefresh(userId);

        const result = await resp.json();
        const newSlide = {
          title: content_brief || `New slide ${insertAt + 1}`,
          html_source: result.html_source,
          preview_url: result.preview_url,
        };

        setGeneratedSlides(prev => {
          const next = [...prev];
          next.splice(insertAt, 0, { ...newSlide, _uid: genUid() });
          return renumberSlideCounters(next, prev.length);
        });
        setCurrentSlideIndex(insertAt);

        const finalMsg = `Added new slide at position ${insertAt + 1}`;
        setHtmlChatHistory(prev => prev.map(m =>
          m.timestamp === progressId
            ? { ...m, content: finalMsg, isProgress: false }
            : m
        ));
        onChatMessage?.(finalMsg);
        toast.success(finalMsg);
        return { ok: true };
      }

      toast.error(`Unsupported structural operation: ${operation}`);
      return { ok: false };
    } catch (err) {
      console.error('[STRUCTURAL]', err);
      toast.error(err.message || 'Structural edit failed');
      setHtmlChatHistory(prev => prev.map(m =>
        m.isProgress ? { ...m, content: `Failed: ${err.message}`, isProgress: false, isError: true } : m
      ));
      return { ok: false, error: err.message };
    }
  };

  // r145: deterministic renumbering of OUR OWN counter artifact after any
  // structural change (the deterministic-handling carve-out of the
  // no-regex-for-content law: this pattern only ever matches the counter
  // text we injected or its model-mirrored twin, whole-element, both digit
  // scripts). Cheap (pure DOM/string work), so it runs automatically.
  const COUNTER_TEXT_RE = /^[\s\u200e\u200f]*[0-9\u0660-\u0669]{1,3}\s*\/\s*[0-9\u0660-\u0669]{1,3}[\s\u200e\u200f]*$/;
  const renumberSlideCounters = (slides, oldTotal = null) => {
    try {
      const total = slides.length;
      const _toWestern = (t) => t.replace(/[\u0660-\u0669]/g, d => String('\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669'.indexOf(d)));
      const toDigits = (n, arabic) => {
        const s = String(n).padStart(2, '0');
        return arabic ? s.replace(/[0-9]/g, d => '\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669'[d]) : s;
      };
      return slides.map((s, i) => {
        if (!s?.html_source) return s;
        const doc = new DOMParser().parseFromString(s.html_source, 'text/html');
        let els = [...doc.querySelectorAll('.wiz-counter')];
        if (els.length === 0) {
          for (const el of doc.querySelectorAll('div,span,p')) {
            const t = (el.textContent || '').trim();
            if (el.children.length !== 0 || !COUNTER_TEXT_RE.test(t)) continue;
            // r146b: without .wiz-counter the whole-element NN/NN match can
            // hit a KPI ("7 / 10 score") — only rewrite when the SECOND
            // number equals the deck total (old or new), which only the
            // counter satisfies.
            const second = parseInt(_toWestern(t).split('/')[1], 10);
            if (second === total || (oldTotal != null && second === oldTotal)) { els = [el]; break; }
          }
        }
        let changed = false;
        for (const el of els) {
          const txt = (el.textContent || '').trim();
          if (!COUNTER_TEXT_RE.test(txt)) continue;
          const arabic = /[\u0660-\u0669]/.test(txt);
          const next = `${toDigits(i + 1, arabic)} / ${toDigits(total, arabic)}`;
          if (txt !== next) { el.textContent = next; changed = true; }
        }
        if (!changed) return s;
        // r146b: do NOT flag _needsRescreenshot — after add/delete EVERY
        // slide's total changes, and flagging all of them mounted a live
        // iframe per slide (the exact lag r120 removed). A briefly-stale
        // counter in a thumbnail beats a whole-deck iframe storm; the live
        // cards render the fresh HTML regardless.
        return { ...s, html_source: `<!DOCTYPE html>\n${doc.documentElement.outerHTML}` };
      });
    } catch {
      return slides; // renumbering is a courtesy — never break the splice
    }
  };

  // HTML slide chat: send edit message
  // options: { applyAll?: boolean, targetSlide?: number } — from LLM intent router
  const handleHtmlChatSend = async (messageOverride, options) => {
    const message = (typeof messageOverride === 'string' ? messageOverride : '') || htmlChatInput.trim();
    if (!message) return { ok: false };
    if (htmlChatLoading) {
      // r145: this silent return was the P0 — every op after the first in a
      // multi-op plan vanished while the UI reported success. The plan loop
      // now AWAITS, so hitting this means a genuine double-send.
      toast.info('An edit is already running — try again when it finishes.');
      return { ok: false, busy: true };
    }
    if (externalFeedRef.current) {
      toast.info('Still generating — edits unlock when the deck finishes.'); // r140b P1
      return { ok: false };
    }

    // r145: when the PLANNER routed this call (options != null), its scope
    // and target are final — the regex re-detectors used to flip a
    // single-slide brief mentioning "all the slides" into a deck-wide
    // rewrite (a regex overriding the LLM router).
    const applyToAll = options
      ? !!options.applyAll
      : (htmlApplyAll || isApplyToAllSlides(message));
    // r160: a deck-wide sweep is the dangerous moment — checkpoint the
    // saved state first (fire-and-forget, fail-open).
    if (applyToAll && projectId) snapshotVersion('before apply-all edit');

    let targetSlideIndex = currentSlideIndex;
    if (!applyToAll) {
      if (options?.targetUid) {
        // r146b: resolve by IDENTITY first — the plan numbered this slide
        // before earlier structural ops spliced the array
        const byUid = generatedSlides.findIndex(s => s?._uid === options.targetUid);
        targetSlideIndex = byUid !== -1 ? byUid : (options.targetSlide ?? currentSlideIndex);
      } else if (options?.targetSlide != null) {
        targetSlideIndex = options.targetSlide;
      } else if (htmlHighlightRegion?.slideIdx != null) {
        // r145: a drawn region owns the target — it is bound to the slide it
        // was drawn on, never wherever the user navigated to since.
        targetSlideIndex = htmlHighlightRegion.slideIdx;
      } else if (!options) {
        const referencedIndex = parseSlideReference(message);
        if (referencedIndex !== null) targetSlideIndex = referencedIndex;
      }
      targetSlideIndex = Math.max(0, Math.min(targetSlideIndex, generatedSlides.length - 1));
      if (targetSlideIndex !== currentSlideIndex) setCurrentSlideIndex(targetSlideIndex);
    }

    const slide = generatedSlides[targetSlideIndex];
    if (!slide?.html_source) {
      toast.error('No HTML slide selected');
      return { ok: false };
    }
    const targetUid = slide._uid || null; // r145: results write by IDENTITY

    // Merge any reference images passed via options (e.g., from UnifiedWorkspace chat attachment)
    const effectiveImages = [...(options?.referenceImages || []), ...htmlChatImages];

    // Add user message to chat
    const userMsg = {
      role: 'user',
      content: message,
      slideIndex: targetSlideIndex,
      timestamp: nextMsgId(),
      // r145: only a region drawn on THIS target rides the edit
      region: (htmlHighlightRegion && (htmlHighlightRegion.slideIdx == null || htmlHighlightRegion.slideIdx === targetSlideIndex))
        ? htmlHighlightRegion : null,
      images: effectiveImages.length > 0 ? [...effectiveImages] : null,
    };
    setHtmlChatHistory(prev => [...prev, userMsg]);
    setHtmlChatInput('');
    setHtmlChatLoading(true);
    setHtmlHighlightRegion(null);
    setHtmlRegionMode(false);
    // r145: cancel + timeout plumbing — an edit had NO abort path and a
    // hung request wedged the whole chat surface for up to 10 minutes.
    editCancelRef.current = false;
    editAbortRef.current = new AbortController();
    // r173: 600s — the full edit pipeline (edit + QA retry + agentic
    // refinement, all on Opus) legitimately runs past 5 minutes; the 300s
    // timer abandoned (and still billed) a completing edit on 01/09. The
    // server allows 900s; the backend also time-budgets the refinement now.
    const _abortTimer = setTimeout(() => { try { editAbortRef.current?.abort(); } catch { /* noop */ } }, 600000);
    let _editOk = false;
    let _editDiff = null; // r169: before/after payload for the chat diff card (single-slide edits)

    try {
      if (applyToAll) {
        // --- Apply to all HTML slides ---
        const htmlSlideIndices = generatedSlides
          .map((s, i) => (s?.html_source ? i : -1))
          .filter(i => i !== -1);
        const total = htmlSlideIndices.length;

        // Add progress message
        const progressMsgId = nextMsgId();
        setHtmlChatHistory(prev => [...prev, {
          role: 'bot',
          content: `Applying to all slides (0/${total})...`,
          timestamp: progressMsgId,
          isProgress: true,
        }]);

        let succeeded = 0;
        let failed = 0;

        const orchCtxAll = getOrchestrationCtx();
        let quotaStopped = false;
        // r146b: ONE 300s timer used to span the WHOLE sweep — with QA +
        // agentic passes a 5-slide sweep exceeded it and every remaining
        // fetch instantly rejected on the shared aborted controller.
        clearTimeout(_abortTimer);
        for (const slideIdx of htmlSlideIndices) {
          if (editCancelRef.current) break; // r145: user hit Stop
          const targetSlide = generatedSlides[slideIdx];
          const loopUid = targetSlide?._uid || null;
          setActiveEditIdx(slideIdx); // r145: the overlay follows the WORKED slide
          editAbortRef.current = new AbortController();
          const _iterTimer = setTimeout(() => { try { editAbortRef.current?.abort(); } catch { /* noop */ } }, 600000); // r173: match the single-edit ceiling
          try {
            const payload = {
              html_source: targetSlide.html_source,
              edit_prompt: message,
              slide_index: slideIdx,
              brand_context: '',
              current_screenshot: targetSlide.preview_url || '',
              html_model: options?.modelOverride || htmlEditModel,
              user_id: userId || '',
              run_qa: questionnaireAnswers?.runQa !== 'no',
              // r146: the agentic intent judge is per-request value — on an
              // N-slide sweep it would run N times for one mechanical
              // instruction. QA still runs; intent verification is single-edit.
              agentic: false,
              ...orchCtxAll,
            };
            if (effectiveImages.length > 0) {
              payload.embed_images = effectiveImages.map((url, i) => ({ slot_id: i, data_url: url, name: `attached image ${i + 1}` }));
            }

            const response = await fetch(withBackend('/edit-html-slide'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
              body: JSON.stringify({ ...payload, proposal_mode: !!questionnaireAnswers?.proposalMode, request_id: crypto.randomUUID() }),
              signal: editAbortRef.current?.signal,
            });

            if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              if (response.status === 402 || response.status === 403) {
                // r145: out of quota — STOP the sweep instead of burning
                // through the remaining slides for N identical failures.
                quotaStopped = true;
                toast.error(errData.message || 'Usage limit reached — the remaining slides were not edited.');
                if (onUsageRefresh) onUsageRefresh(userId);
                break;
              }
              throw new Error(errData.message || errData.error || 'Failed');
            }

            const result = await response.json();
            succeeded++;

            // Snapshot the pre-edit state so the user can undo this change.
            recordSlideHistory(slideIdx, null, null, loopUid);

            // Update this slide — by IDENTITY, so a concurrent structural
            // splice can't land the result on the wrong slide (r145).
            setGeneratedSlides(prev => prev.map((s, i) =>
              (loopUid ? s?._uid === loopUid : i === slideIdx)
                ? { ...s, html_source: result.html_source, preview_url: result.preview_url || s.preview_url, _needsRescreenshot: !result.preview_url }
                : s
            ));

            // Update progress message in-place
            setHtmlChatHistory(prev => prev.map(m =>
              m.timestamp === progressMsgId
                ? { ...m, content: `Applying to all slides (${succeeded}/${total})...` }
                : m
            ));
          } catch (err) {
            console.error(`HTML chat edit error (slide ${slideIdx + 1}):`, err);
            failed++;
          } finally {
            clearTimeout(_iterTimer);
          }
        }

        // Final status message (replace the progress message)
        const finalMsg = editCancelRef.current
          ? `Stopped — ${succeeded}/${total} slides updated`
          : quotaStopped
            ? `Stopped at the usage limit — ${succeeded}/${total} slides updated`
            : failed === 0
              ? `Updated all ${succeeded} slides`
              : `Updated ${succeeded}/${total} slides (${failed} failed)`;
        _editOk = failed === 0 && !quotaStopped && !editCancelRef.current;
        if (succeeded > 0 && onUsageRefresh) onUsageRefresh(userId); // r145: meter was stale after edits
        setHtmlChatHistory(prev => prev.map(m =>
          m.timestamp === progressMsgId
            ? { ...m, content: finalMsg, isProgress: false }
            : m
        ));
        onChatMessage?.(finalMsg);

        setHtmlChatImages([]);
        setHtmlApplyAll(false);
      } else {
        // --- Check if user wants to generate/insert an image ---
        const imageKeywords = /\b(logo|image|photo|picture|icon|illustration|صورة|شعار|لوجو)\b/i;
        const insertKeywords = /\b(add|insert|place|put|ضع|أضف|ضيف)\b/i;
        // r145: never hijack a PLANNER-routed edit (options != null) — the
        // regex pair used to reroute "place the icon next to the title"
        // into AI image generation against the router's decision.
        const wantsImageGen = !options && imageKeywords.test(message) && insertKeywords.test(message) && userMsg.region && effectiveImages.length === 0;

        let imageGenFellThrough = false;
        if (wantsImageGen) {
          // Generate image first, then preview for confirmation
          setHtmlChatHistory(prev => [...prev, {
            role: 'bot', content: 'Generating image...', timestamp: Date.now(), isProgress: true,
          }]);

          try {
            const imgResp = await fetch(withBackend('/api/generate-image-for-slide'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ description: message, style: 'inline' }),
            });
            if (!imgResp.ok) throw new Error('Image generation failed');
            const { image_url } = await imgResp.json();

            // Replace progress with image preview
            setHtmlChatHistory(prev => {
              const filtered = prev.filter(m => !m.isProgress);
              return [...filtered, {
                role: 'bot',
                type: 'image_preview',
                image_url,
                edit_prompt: message,
                region: userMsg.region,
                slideIndex: targetSlideIndex,
                timestamp: Date.now(),
              }];
            });
          } catch (imgErr) {
            setHtmlChatHistory(prev => {
              const filtered = prev.filter(m => !m.isProgress);
              return [...filtered, {
                role: 'bot', content: `Image generation failed: ${imgErr.message}. Proceeding with text edit...`,
                timestamp: nextMsgId(), isError: true,
              }];
            });
            imageGenFellThrough = true; // r145: actually DO the promised text edit
          }
          if (!imageGenFellThrough) {
            setHtmlChatLoading(false);
            setHtmlChatImages([]);
            clearTimeout(_abortTimer);
            return { ok: true, imagePreview: true };
          }
        }

        // --- Single slide edit ---
        setActiveEditIdx(targetSlideIndex); // r145: overlay on the WORKED slide
        const orchCtx = getOrchestrationCtx();
        const payload = {
          html_source: slide.html_source,
          edit_prompt: message,
          slide_index: targetSlideIndex,
          brand_context: '',
          current_screenshot: slide.preview_url || '',
          html_model: options?.modelOverride || htmlEditModel,
          user_id: userId || '',
          run_qa: questionnaireAnswers?.runQa !== 'no',
          ...orchCtx,
        };
        if (userMsg.region) {
          payload.highlight_region = userMsg.region;
        }
        // r146: cross-slide context from the planner
        if (options?.contextSlides?.length) {
          payload.context_slides = options.contextSlides;
        }
        // Attached images are PLACED VERBATIM on the slide (embed), not used as
        // style references. The backend stores each and fills a data-user-asset
        // slot, so the exact image lands on the slide (in the region if drawn).
        if (effectiveImages.length > 0) {
          payload.embed_images = effectiveImages.map((url, i) => ({ slot_id: i, data_url: url, name: `attached image ${i + 1}` }));
        }

        const response = await fetch(withBackend('/edit-html-slide'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ ...payload, proposal_mode: !!questionnaireAnswers?.proposalMode, request_id: crypto.randomUUID() }),
          signal: editAbortRef.current?.signal,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || errData.error || 'Failed to edit slide');
        }

        const result = await response.json();

        // Snapshot pre-edit state for undo.
        recordSlideHistory(targetSlideIndex, null, null, targetUid);

        // Update the slide — by IDENTITY (a structural splice mid-flight
        // used to land the result on whatever slide now held the index).
        setGeneratedSlides(prev => prev.map((s, i) =>
          (targetUid ? s?._uid === targetUid : i === targetSlideIndex)
            ? { ...s, html_source: result.html_source, preview_url: result.preview_url || s.preview_url, _needsRescreenshot: !result.preview_url }
            : s
        ));

        // Add bot response
        const botMsg = `Updated slide ${targetSlideIndex + 1}`;
        setHtmlChatHistory(prev => [...prev, {
          role: 'bot',
          content: botMsg,
          slideIndex: targetSlideIndex,
          timestamp: nextMsgId(),
        }]);
        // r146: the agentic loop tells the user when it self-corrected —
        // r146b: mirrored to the UW chat (the CPW panel is hidden there)
        if (result.agentic_note) {
          setHtmlChatHistory(prev => [...prev, {
            role: 'bot',
            content: `🔁 ${result.agentic_note}`,
            timestamp: nextMsgId(),
            slideIndex: targetSlideIndex,
          }]);
          onChatMessage?.(`🔁 ${result.agentic_note}`);
        }
        // r145: the backend runs QA on every edit and returned residual
        // issues that were thrown away — surface them as the existing QA
        // card (with its Apply-fixes wiring) instead of shipping silently.
        if (Array.isArray(result.qa_issues) && result.qa_issues.length > 0) {
          const qaHeadline = `QA found ${result.qa_issues.length} remaining issue${result.qa_issues.length > 1 ? 's' : ''} on this slide`;
          setHtmlChatHistory(prev => [...prev, {
            role: 'bot',
            content: qaHeadline,
            timestamp: nextMsgId(),
            slideIndex: targetSlideIndex,
            qaIssues: result.qa_issues,
          }]);
          // r146b: the UW chat renders this shape with Apply-fixes wiring
          onChatMessage?.({ text: qaHeadline, isQa: true, qaIssues: result.qa_issues, slideIndex: targetSlideIndex });
        }
        onChatMessage?.(botMsg);
        if (onUsageRefresh) onUsageRefresh(userId); // r145: refresh the credits meter
        _editOk = true;
        // r169: hand the chat a before/after pair so it can render a diff
        // card with per-change revert/redo. `slide` is the pre-edit state
        // this branch read the payload from.
        _editDiff = {
          slideIdx: targetSlideIndex,
          uid: targetUid || slide._uid || null,
          before: { html_source: slide.html_source, preview_url: slide.preview_url || '' },
          after: { html_source: result.html_source, preview_url: result.preview_url || '' },
        };

        setHtmlChatImages([]);
      }
    } catch (error) {
      const cancelled = error?.name === 'AbortError' || editCancelRef.current;
      console.error('HTML chat edit error:', error);
      const errMsg = cancelled ? 'Edit stopped.' : `Error: ${error.message}`;
      setHtmlChatHistory(prev => [...prev, {
        role: 'bot',
        content: errMsg,
        slideIndex: targetSlideIndex,
        timestamp: nextMsgId(),
        isError: !cancelled,
      }]);
      onChatMessage?.(cancelled ? errMsg : { text: errMsg, isError: true });
      if (!cancelled) toast.error(error.message || 'Failed to edit slide');
    } finally {
      clearTimeout(_abortTimer);
      editAbortRef.current = null;
      setActiveEditIdx(null);
      setHtmlChatLoading(false);
    }
    return { ok: _editOk, diff: _editDiff };
  };

  // Handle image preview confirmation — insert the generated image into the slide
  const handleConfirmImage = async (msg) => {
    setHtmlChatLoading(true);
    try {
      const slide = generatedSlides[msg.slideIndex];
      if (!slide?.html_source) throw new Error('No slide found');

      const orchCtxImg = getOrchestrationCtx();
      const payload = {
        html_source: slide.html_source,
        edit_prompt: `Insert this provided image into the highlighted region. Make it fit naturally within the slide design. The original request was: ${msg.edit_prompt}`,
        slide_index: msg.slideIndex,
        brand_context: '',
        current_screenshot: slide.preview_url || '',
        html_model: htmlEditModel,
        highlight_region: msg.region,
        reference_images: [msg.image_url],
        user_id: userId || '',
        run_qa: questionnaireAnswers?.runQa !== 'no',
        ...orchCtxImg,
      };

      const response = await fetch(withBackend('/edit-html-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ ...payload, proposal_mode: !!questionnaireAnswers?.proposalMode, request_id: crypto.randomUUID() }),
      });
      if (!response.ok) throw new Error('Failed to insert image');
      const result = await response.json();

      setGeneratedSlides(prev => prev.map((s, i) =>
        i === msg.slideIndex ? { ...s, html_source: result.html_source, preview_url: result.preview_url, _needsRescreenshot: false } : s
      ));

      // Remove the preview message and add success
      setHtmlChatHistory(prev => [
        ...prev.filter(m => m.timestamp !== msg.timestamp),
        { role: 'bot', content: `Image inserted into slide ${msg.slideIndex + 1}`, timestamp: Date.now() },
      ]);
    } catch (err) {
      toast.error(err.message);
      setHtmlChatHistory(prev => [...prev, { role: 'bot', content: `Error: ${err.message}`, timestamp: Date.now(), isError: true }]);
    } finally {
      setHtmlChatLoading(false);
    }
  };

  // Handle image regeneration
  const handleRegenerateImage = async (msg) => {
    setHtmlChatLoading(true);
    try {
      const imgResp = await fetch(withBackend('/api/generate-image-for-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: msg.edit_prompt, style: 'inline' }),
      });
      if (!imgResp.ok) throw new Error('Regeneration failed');
      const { image_url } = await imgResp.json();

      // Replace the preview message with new image
      setHtmlChatHistory(prev => prev.map(m =>
        m.timestamp === msg.timestamp ? { ...m, image_url } : m
      ));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setHtmlChatLoading(false);
    }
  };

  // HTML chat: handle image upload
  const [uploadingImages, setUploadingImages] = useState(false);

  const processImageFiles = (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploadingImages(true);
    let remaining = imageFiles.length;
    imageFiles.forEach(file => {
      if (file.size > 4 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 4MB limit`);
        remaining--;
        if (remaining === 0) setUploadingImages(false);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setHtmlChatImages(prev => [...prev.slice(-2), reader.result]); // Max 3 images
        remaining--;
        if (remaining === 0) setUploadingImages(false);
      };
      reader.onerror = () => {
        remaining--;
        if (remaining === 0) setUploadingImages(false);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleHtmlChatImageUpload = (e) => {
    processImageFiles(Array.from(e.target.files || []));
    if (htmlChatFileRef.current) htmlChatFileRef.current.value = '';
  };

  // Drag & drop handler for chat area
  const handleChatDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setChatDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    processImageFiles(files);
  };

  // Paste image handler for chat textarea
  const handleChatPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (imageItems.length > 0) {
      e.preventDefault();
      const files = imageItems.map(item => item.getAsFile()).filter(Boolean);
      processImageFiles(files);
    }
  };

  const [chatDragOver, setChatDragOver] = useState(false);

  // r130: teach-the-brand from the generation page — capture a correction at
  // the moment the user SEES the problem, with the offending slide attached
  // as evidence automatically. Rules land on the brand (r127 endpoints) and
  // apply to every future generation and the QA judge.
  const [teachBrandOpen, setTeachBrandOpen] = useState(false);
  const [teachText, setTeachText] = useState('');
  const [teachScope, setTeachScope] = useState('');
  const [teachIncludeShot, setTeachIncludeShot] = useState(true);
  const [teachBusy, setTeachBusy] = useState(false);
  // r155: the modal silently attached the invisible "current" slide — the
  // user now PICKS the example (defaults to the slide they were on).
  const [teachSlideIdx, setTeachSlideIdx] = useState(0);
  useEffect(() => {
    if (teachBrandOpen) setTeachSlideIdx(Math.max(0, Math.min(currentSlideIndex, generatedSlides.length - 1)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachBrandOpen]);

  // Downscale the slide preview to a data URL the feedback endpoint accepts
  // (≤1280px JPEG stays far under its 4MB cap). Fail-open: no screenshot
  // means a text-only rule, never a blocked save.
  const slideShotDataUrl = async (slide) => {
    try {
      const src = slide?.preview_url;
      if (!src) return null;
      // preview_url can be a bare base64 blob or a relative backend path
      // (documented pathologies) — normalize like every other consumer does
      const u = normalizeImageUrl(src);
      if (!u) return null;
      const resp = await fetch(u.startsWith('/') ? withBackend(u) : u);
      const blob = await resp.blob();
      const bmp = await createImageBitmap(blob);
      const scale = Math.min(1, 1280 / (bmp.width || 1280));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bmp.width * scale));
      canvas.height = Math.max(1, Math.round(bmp.height * scale));
      canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch { return null; }
  };

  const submitTeachBrand = async () => {
    const brandId = brandIdentityId || brandIdentityIdRef.current;
    if (!teachText.trim() || !brandId || teachBusy) return;
    setTeachBusy(true);
    try {
      const shot = teachIncludeShot
        ? await slideShotDataUrl(generatedSlides[teachSlideIdx]) : null;
      const res = await fetch(withBackend(`/api/brand-identities/${brandId}/feedback`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          instruction: teachText.trim(),
          scope: teachScope.trim(),
          image_data_url: shot,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save the rule');
      setTeachBrandOpen(false); setTeachText(''); setTeachScope('');
      toast.success('Rule saved — every future generation with this brand will follow it.');
    } catch (err) {
      toast.error(err.message);
    } finally { setTeachBusy(false); }
  };

  // ── r160: VERSION HISTORY ──────────────────────────────────────────────
  // Every accepted save snapshots the deck server-side (slide-level dedupe).
  // Restore loads a snapshot into the editor and lets the normal save
  // machinery persist it — a restore is a NEW version, never an overwrite.
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionList, setVersionList] = useState(null);
  const [versionBusy, setVersionBusy] = useState('');
  const snapshotVersion = async (label) => {
    if (!projectId) return;
    try {
      await fetch(withBackend(`/api/projects/${projectId}/versions/snapshot`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ user_id: userId || '', label: label || 'checkpoint' }),
      });
    } catch { /* fail-open — a checkpoint must never block an edit */ }
  };
  const openVersions = async () => {
    setVersionsOpen(true); setVersionList(null);
    try {
      const r = await fetch(withBackend(`/api/projects/${projectId}/versions?user_id=${encodeURIComponent(userId || '')}`), {
        headers: { ...(await authHeaders()) },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load versions');
      setVersionList(d.versions || []);
    } catch (err) { toast.error(err.message); setVersionList([]); }
  };
  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore the version from ${new Date(v.created_at).toLocaleString()}? Your current deck stays saved as its own version.`)) return;
    setVersionBusy(v.id);
    try {
      // r160b (review P2-2): the confirm's promise is only true for SAVED
      // state — flush unsaved edits into a version first.
      if (hasUnsavedEdits) {
        try { await handleSaveProject(); } catch { /* the snapshot below still has the last save */ }
      }
      const r = await fetch(withBackend(`/api/projects/${projectId}/versions/${v.id}?user_id=${encodeURIComponent(userId || '')}`), {
        headers: { ...(await authHeaders()) },
      });
      const d = await r.json();
      if (!r.ok || !Array.isArray(d.slides) || d.slides.length === 0) throw new Error(d.error || 'Could not load that version');
      if (d.missing > 0) {
        toast.warning(`${d.missing} slide${d.missing > 1 ? 's' : ''} of that version could not be recovered — restoring the rest.`);
      }
      const restored = d.slides.map(s => ({ html_source: s.html_source, title: s.title || '', preview_url: '' }));
      // r160b (review P1-2): blank previews must be re-screenshot in-session —
      // reset the recovery once-guard; the recovery effect (now keyed on
      // generatedSlides) refills them, and the deferred save then persists
      // real previews instead of blanking the Recents card.
      screenshotRecoveryDone.current = false;
      setGeneratedSlides(restored);
      if (currentStep !== 5) setCurrentStep(5);
      setBaselineSaveDue(true); // persist the restore as its own new version
      setVersionsOpen(false);
      toast.success(`Restored ${restored.length} slides — saving as a new version.`);
    } catch (err) { toast.error(err.message); }
    finally { setVersionBusy(''); }
  };

  // r130: drag-and-drop onto the content-import box (it always LOOKED like a
  // drop zone — dashed border — but only accepted clicks).
  const importDrop = useFileDrop((files) => {
    if (!importing && files[0]) handleContentImport(files[0], null);
  });

  // Make current slide editable (single slide)
  const handleMakeCurrentSlideEditable = async () => {
    const currentSlide = generatedSlides[currentSlideIndex];

    // Check if already extracted (with actual layer data)
    if (currentSlide?.extractedLayers?.layers?.length > 0) {
      setExtractedLayers(currentSlide.extractedLayers);
      setSelectedLayerIndex(0);
      setShowLayersModal(true);
      toast.success('Slide is already editable!');
      return;
    }

    if (!currentSlide?.preview_url) {
      toast.error('No slide to make editable');
      return;
    }

    setMakingEditable(true);
    setEditableProgress({ current: 0, total: 1 });
    toast.loading('Making current slide editable...', { id: 'make-editable' });

    try {
      const response = await fetch(withBackend('/extract-slide-elements'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_data: currentSlide.preview_url,
          classes: ['text', 'icons', 'shapes', 'logos']
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedSlides(prev => prev.map((s, idx) =>
          idx === currentSlideIndex ? { ...s, extractedLayers: data } : s
        ));
        setExtractedLayers(data);
        setSelectedLayerIndex(0);
        setShowLayersModal(true);
        toast.success('Slide is now editable!', { id: 'make-editable' });
      } else {
        throw new Error('Failed to extract elements');
      }
    } catch (error) {
      console.error('Make editable error:', error);
      toast.error('Failed to make slide editable', { id: 'make-editable' });
    } finally {
      setMakingEditable(false);
      setEditableProgress({ current: 0, total: 0 });
    }
  };

  // Make all slides editable
  const handleMakeAllSlidesEditable = async () => {
    // Check if all slides already have extracted layers (with actual layer data)
    const allExtracted = generatedSlides.every(slide => slide.extractedLayers?.layers?.length > 0);
    if (allExtracted) {
      // All slides already editable, just show the editor for current slide
      const currentSlide = generatedSlides[currentSlideIndex];
      setExtractedLayers(currentSlide.extractedLayers);
      setSelectedLayerIndex(0);
      setShowLayersModal(true);
      toast.success('All slides are already editable!');
      return;
    }

    setMakingEditable(true);
    const slidesToExtract = generatedSlides.filter(slide => slide && slide.preview_url && !(slide.extractedLayers?.layers?.length > 0));
    setEditableProgress({ current: 0, total: slidesToExtract.length });
    toast.loading(`Making slides editable (0/${slidesToExtract.length})...`, { id: 'make-editable' });

    try {
      for (let i = 0; i < generatedSlides.length; i++) {
        const slide = generatedSlides[i];
        if (!slide) continue;

        // Skip if already extracted (with real layers) or no preview
        if (slide.extractedLayers?.layers?.length > 0 || !slide.preview_url) continue;

        const response = await fetch(withBackend('/extract-slide-elements'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_data: slide.preview_url,
            classes: ['text', 'icons', 'shapes', 'logos']
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setGeneratedSlides(prev => prev.map((s, idx) =>
            idx === i ? { ...s, extractedLayers: data } : s
          ));
        }

        const completed = generatedSlides.slice(0, i + 1).filter(s => s.extractedLayers || s === slide).length;
        setEditableProgress({ current: completed, total: slidesToExtract.length });
        toast.loading(`Making slides editable (${completed}/${slidesToExtract.length})...`, { id: 'make-editable' });
      }

      toast.success('All slides are now editable!', { id: 'make-editable' });

      // Show the layer editor for current slide
      const currentSlide = generatedSlides[currentSlideIndex];
      if (currentSlide?.extractedLayers) {
        setExtractedLayers(currentSlide.extractedLayers);
        setSelectedLayerIndex(0);
        setShowLayersModal(true);
      }
    } catch (error) {
      console.error('Make editable error:', error);
      toast.error('Failed to make some slides editable', { id: 'make-editable' });
    } finally {
      setMakingEditable(false);
      setEditableProgress({ current: 0, total: 0 });
    }
  };

  // Legacy wrapper function (kept for compatibility)
  const handleMakeEditable = async () => {
    await handleMakeAllSlidesEditable();
  };

  // Open layer editor for current slide (if already extracted with real layers)
  const handleOpenLayerEditor = () => {
    const currentSlide = generatedSlides[currentSlideIndex];
    if (currentSlide?.extractedLayers?.layers?.length > 0) {
      setExtractedLayers(currentSlide.extractedLayers);
      setSelectedLayerIndex(0);
      setShowLayersModal(true);
    } else {
      toast.error('Please make slides editable first');
    }
  };

  // Save layer edits back to the slide
  const handleSaveLayerEdits = (updatedLayers) => {
    setGeneratedSlides(prev => prev.map((slide, idx) =>
      idx === currentSlideIndex ? { ...slide, extractedLayers: { ...slide.extractedLayers, layers: updatedLayers } } : slide
    ));
    setHasUnsavedEdits(false);
    toast.success('Edits saved!');
  };

  // Legacy single-slide extract (kept for backwards compatibility)
  const handleExtractElements = async () => {
    handleMakeEditable();
  };

  // Process extraction queue - called when a new slide is added or when an extraction completes
  const processExtractionQueue = async () => {
    // Check if we can start more extractions
    while (extractionInProgressRef.current < maxConcurrentExtractions && extractionQueueRef.current.length > 0) {
      const slideToExtract = extractionQueueRef.current.shift();
      if (!slideToExtract) continue;

      extractionInProgressRef.current++;

      // Extract this slide (don't await - let it run in parallel)
      extractSingleSlideInBackground(slideToExtract).finally(() => {
        extractionInProgressRef.current--;
        // Process more from queue when this one completes
        processExtractionQueue();
      });
    }
  };

  // Extract a single slide in background (called per-slide as they generate)
  const extractSingleSlideInBackground = async (slide) => {
    if (!slide?.preview_url) return;

    try {
      const response = await fetch(withBackend('/extract-slide-elements'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_data: slide.preview_url,
          classes: ['text', 'icons', 'shapes', 'logos']
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Update the slide with extracted layers using functional update
        setGeneratedSlides(prev => prev.map(s =>
          s && s.index === slide.index ? { ...s, extractedLayers: data } : s
        ));
      }
    } catch (error) {
      console.warn(`Background extraction failed for slide ${slide.index + 1}:`, error);
    }

    // Update progress
    setBgExtractionProgress(prev => {
      const newCurrent = prev.current + 1;
      const isComplete = newCurrent >= prev.total;
      return {
        current: newCurrent,
        total: prev.total,
        isActive: !isComplete
      };
    });
  };

  // Queue a slide for background extraction
  const queueSlideForExtraction = (slide) => {
    if (!slide?.preview_url) return;

    extractionQueueRef.current.push(slide);

    // Update total count and mark as active
    setBgExtractionProgress(prev => ({
      current: prev.current,
      total: prev.total + 1,
      isActive: true
    }));

    // Start processing queue
    processExtractionQueue();
  };

  // Extract elements for all slides in background (called after generation completes)
  // Processes slides in parallel with concurrency limit for better performance
  const extractAllElementsInBackground = async () => {
    // Build list of {slide, arrayIndex} for slides that need extraction
    const toExtract = [];
    generatedSlides.forEach((slide, idx) => {
      if (slide && slide.preview_url && !slide.extractedLayers) {
        toExtract.push({ slide, arrayIndex: idx });
      }
    });

    if (toExtract.length === 0) return;

    setMakingEditable(true);
    setEditableProgress({ current: 0, total: toExtract.length });
    toast.loading(`Making slides editable (0/${toExtract.length})...`, { id: 'bg-extract' });

    // Process slides in parallel with concurrency limit of 3
    const concurrencyLimit = 3;
    let completed = 0;

    const extractSlide = async ({ slide, arrayIndex }) => {
      try {
        const response = await fetch(withBackend('/extract-slide-elements'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_data: slide.preview_url,
            classes: ['text', 'icons', 'shapes', 'logos']
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Update the slide with extracted layers using array index
          setGeneratedSlides(prev => prev.map((s, idx) =>
            idx === arrayIndex ? { ...s, extractedLayers: data } : s
          ));
        }
      } catch (error) {
        console.warn(`Background extraction failed for slide ${arrayIndex + 1}:`, error);
      }

      completed++;
      setEditableProgress({ current: completed, total: toExtract.length });
      toast.loading(`Making slides editable (${completed}/${toExtract.length})...`, { id: 'bg-extract' });
    };

    // Process in batches with concurrency limit
    for (let i = 0; i < toExtract.length; i += concurrencyLimit) {
      const batch = toExtract.slice(i, i + concurrencyLimit);
      await Promise.all(batch.map(extractSlide));
    }

    setMakingEditable(false);
    toast.success('All slides ready for editing!', { id: 'bg-extract', duration: 3000 });
  };

  // Handle inline slide update (from InlineSlideEditor)
  const handleInlineSlideUpdate = async (slideIndex, updatedSlide) => {
    // r118 (audit P0): manual editor output never entered slide history, so
    // the view-mode Undo button reverted to the last CHAT snapshot and threw
    // away every hand adjustment made since — no confirm, no redo, and the
    // in-editor stack is wiped on Done. Snapshot the PRE-edit state (the ref
    // still holds it at this point) so hand edits are undoable at deck level
    // too. Tagged 'manual' so a whole editing session collapses to one entry.
    recordSlideHistory(slideIndex, null, 'manual');
    setGeneratedSlides(prev => prev.map((s, i) => i === slideIndex ? updatedSlide : s));
    setHasUnsavedEdits(true);
    schedulePerSlideSave(slideIndex);

    // Re-screenshot HTML slides after inline text edits
    if (updatedSlide._needsRescreenshot && updatedSlide.html_source) {
      try {
        const resp = await fetch(withBackend('/screenshot-html'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html_source: updatedSlide.html_source }),
        });
        if (resp.ok) {
          const { preview_url } = await resp.json();
          setGeneratedSlides(prev => prev.map((s, i) =>
            i === slideIndex ? { ...s, preview_url, _needsRescreenshot: false } : s
          ));
          // The fresh screenshot should ride the per-slide save too
          schedulePerSlideSave(slideIndex);
        }
      } catch (err) {
        console.error('Re-screenshot failed:', err);
      }
    }
  };

  // Handle slide layout type change from sidebar
  const [changingSlideType, setChangingSlideType] = useState(false);
  const handleChangeSlideType = async (newType) => {
    const slide = generatedSlides[currentSlideIndex];
    if (!slide || slide.extractedLayers?.source !== 'template') return;

    setChangingSlideType(true);
    try {
      // Preserve existing image URL from the slide
      const existingImageUrl = slide._slideImageUrl
        || slide.extractedLayers?.layers?.find(l => l.type === 'image' && l.content)?.content
        || '';
      const hasImage = !!existingImageUrl;
      const updatedSlide = { ...slide, slideType: newType, needsImage: hasImage };
      const colorScheme = customColorScheme || selectedTheme?.colorScheme;
      const content = {
        title: slide.title,
        content: slide.content || '',
        bullet_points: slide.bullet_points || [],
        bulletPoints: slide.bullet_points || [],
        slideType: newType,
        _slideImageUrl: existingImageUrl,
      };

      const template = getTemplateForSlide(
        { ...selectedTheme, colorScheme },
        updatedSlide,
        currentSlideIndex,
        generatedSlides.length
      );

      const newImage = await renderTemplateToImage(template, content, colorScheme);
      const bgOnly = await renderTemplateBackgroundOnly(template, content, colorScheme);
      const newLayers = generateEditableLayers(template, content, colorScheme);

      setGeneratedSlides(prev => prev.map((s, i) =>
        i === currentSlideIndex
          ? { ...s, slideType: newType, preview_url: newImage, _slideImageUrl: existingImageUrl,
              template_id: template.id, layout: template.layout,
              extractedLayers: { ...s.extractedLayers, layers: newLayers, background: newImage, background_image: newImage, backgroundOnly: bgOnly } }
          : s
      ));
      setHasUnsavedEdits(true);
    } catch (err) {
      console.error('Failed to change slide type:', err);
    } finally {
      setChangingSlideType(false);
    }
  };

  // Layout options for sidebar
  const layoutOptions = [
    { id: 'title', name: 'Title', desc: 'Centered title + subtitle' },
    { id: 'section', name: 'Section', desc: 'Section divider' },
    { id: 'bullets', name: 'Bullets', desc: 'Simple bullet list' },
    { id: 'icon-bullets', name: 'Icon Bullets', desc: 'Bullets with icons' },
    { id: 'cards', name: 'Cards', desc: 'Card grid layout' },
    { id: 'icon-grid', name: 'Icon Grid', desc: 'Grid with icons' },
    { id: 'feature-showcase', name: 'Features', desc: 'Feature cards' },
    { id: 'timeline', name: 'Timeline', desc: 'Horizontal timeline' },
    { id: 'process', name: 'Process', desc: 'Step-by-step flow' },
    { id: 'numbered-list', name: 'Numbered', desc: 'Numbered list' },
    { id: 'text-with-image', name: 'Split Layout', desc: 'Text + image block' },
    { id: 'three-column', name: 'Three Columns', desc: '3-column blocks' },
    { id: 'quote-card', name: 'Quote', desc: 'Styled quote' },
    { id: 'big-statement', name: 'Statement', desc: 'Large centered text' },
    { id: 'closing', name: 'Closing', desc: 'Closing slide' },
  ];

  // Layout thumbnail previews - rendered at small size for sidebar
  const [layoutThumbnails, setLayoutThumbnails] = useState({});
  const layoutThumbCacheKeyRef = useRef('');

  useEffect(() => {
    const colorScheme = customColorScheme || selectedTheme?.colorScheme;
    if (!selectedTheme || !colorScheme || generatedSlides.length === 0) return;

    // Cache key based on theme + color scheme to avoid re-rendering
    const cacheKey = `${selectedTheme.id || 'default'}-${colorScheme.primary}-${colorScheme.secondary}`;
    if (cacheKey === layoutThumbCacheKeyRef.current) return;
    layoutThumbCacheKeyRef.current = cacheKey;

    const sampleContent = {
      title: 'Sample Title',
      content: 'Sample subtitle text here',
      bullet_points: ['First point', 'Second point', 'Third point', 'Fourth point'],
      bulletPoints: ['First point', 'Second point', 'Third point', 'Fourth point'],
    };

    // Generate thumbnails in parallel
    const generateThumbs = async () => {
      const thumbs = {};
      const promises = layoutOptions.map(async (opt) => {
        try {
          const fakeSlide = { slideType: opt.id, title: sampleContent.title };
          const template = getTemplateForSlide(
            { ...selectedTheme, colorScheme },
            fakeSlide, 1, 5
          );
          if (template) {
            thumbs[opt.id] = await renderTemplateToImage(template, sampleContent, colorScheme, { width: 384, height: 216 });
          }
        } catch (e) {
          // Silently skip failed thumbnails
        }
      });
      await Promise.all(promises);
      setLayoutThumbnails(thumbs);
    };

    generateThumbs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTheme, customColorScheme, generatedSlides.length > 0]);

  // Handle color scheme change from ThemeCustomizer
  const handleColorSchemeChange = async (newColorScheme) => {
    setCustomColorScheme(newColorScheme);

    // Re-render current slide with new colors
    const currentSlide = generatedSlides[currentSlideIndex];
    if (!currentSlide || currentSlide.extractedLayers?.source !== 'template') return;

    const content = {
      title: currentSlide.title,
      content: currentSlide.content || '',
      bullet_points: currentSlide.bullet_points || currentSlide.bulletPoints || [],
    };

    const template = getTemplateForSlide(selectedTheme, currentSlide, currentSlideIndex, generatedSlides.length);
    if (!template) return;

    try {
      const newImage = await renderTemplateToImage(template, content, newColorScheme);
      const bgOnly = await renderTemplateBackgroundOnly(template, content, newColorScheme);
      const newLayers = generateEditableLayers(template, content, newColorScheme);
      setGeneratedSlides(prev => prev.map((s, i) =>
        i === currentSlideIndex
          ? { ...s, preview_url: newImage, extractedLayers: { ...s.extractedLayers, layers: newLayers, background: newImage, backgroundOnly: bgOnly } }
          : s
      ));
    } catch (err) {
      console.error('Failed to re-render with new colors:', err);
    }
  };

  // Apply custom color scheme to all template slides
  const handleApplyThemeToAll = async () => {
    const colorScheme = customColorScheme || selectedTheme?.colorScheme;
    if (!colorScheme || !selectedTheme) return;

    toast.loading('Applying theme to all slides...', { id: 'apply-theme' });

    let updated = [...generatedSlides];
    for (let i = 0; i < updated.length; i++) {
      const slide = updated[i];
      if (slide.extractedLayers?.source !== 'template') continue;

      const content = {
        title: slide.title,
        content: slide.content || '',
        bullet_points: slide.bullet_points || slide.bulletPoints || [],
      };

      const template = getTemplateForSlide(selectedTheme, slide, i, updated.length);
      if (!template) continue;

      try {
        const newImage = await renderTemplateToImage(template, content, colorScheme);
        const newLayers = generateEditableLayers(template, content, colorScheme);
        updated[i] = {
          ...slide,
          preview_url: newImage,
          extractedLayers: { ...slide.extractedLayers, layers: newLayers, background: newImage },
        };
      } catch (err) {
        console.error(`Failed to re-render slide ${i + 1}:`, err);
      }
    }

    setGeneratedSlides(updated);
    setHasUnsavedEdits(true);
    toast.success('Theme applied to all slides!', { id: 'apply-theme', duration: 3000 });
  };

  // Extract text element positions from rendered HTML using DOM getBoundingClientRect
  // This replaces Gemini-based CSS guessing with pixel-perfect positions
  const extractTextLayoutFromHtml = (html) => {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:1280px;height:720px;border:none;visibility:hidden;';
      iframe.sandbox = 'allow-same-origin';
      document.body.appendChild(iframe);

      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const timeout = setTimeout(() => { cleanup(); resolve({ elements: [] }); }, 8000);

      iframe.onload = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) { clearTimeout(timeout); cleanup(); resolve({ elements: [] }); return; }

          // Wait for fonts to load
          const fontsReady = doc.fonts?.ready || Promise.resolve();
          fontsReady.then(() => {
            // Small delay for layout to settle
            setTimeout(() => {
              try {
                const elements = [];
                const seen = new Set();
                const SLIDE_W = 1280, SLIDE_H = 720;

                // Walk text nodes to get leaf-level text (avoids parent/child duplication)
                const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
                  acceptNode: (node) => {
                    const text = node.textContent.trim();
                    if (!text || text.length < 1) return NodeFilter.FILTER_REJECT;
                    const parent = node.parentElement;
                    if (!parent) return NodeFilter.FILTER_REJECT;
                    const style = doc.defaultView.getComputedStyle(parent);
                    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return NodeFilter.FILTER_REJECT;
                    if (parent.offsetHeight === 0 || parent.offsetWidth === 0) return NodeFilter.FILTER_REJECT;
                    return NodeFilter.FILTER_ACCEPT;
                  }
                });

                // Group text nodes by their nearest block-level parent
                const blockMap = new Map();
                while (walker.nextNode()) {
                  const textNode = walker.currentNode;
                  // Find the nearest block-level or significant parent
                  let el = textNode.parentElement;
                  while (el && el !== doc.body) {
                    const display = doc.defaultView.getComputedStyle(el).display;
                    if (display === 'block' || display === 'flex' || display === 'grid' ||
                        display === 'list-item' || display === 'table-cell' ||
                        el.tagName.match(/^(H[1-6]|P|LI|TD|TH|DIV|SECTION|ARTICLE|FIGCAPTION|LABEL|BLOCKQUOTE)$/i)) {
                      break;
                    }
                    el = el.parentElement;
                  }
                  if (!el || el === doc.body) continue;

                  if (!blockMap.has(el)) blockMap.set(el, []);
                  blockMap.get(el).push(textNode);
                }

                // Extract layout for each block element
                for (const [el, textNodes] of blockMap) {
                  const text = el.innerText?.trim();
                  if (!text || text.length < 1) continue;

                  // Deduplicate by element reference
                  const key = `${el.tagName}-${el.offsetTop}-${el.offsetLeft}-${text.substring(0, 30)}`;
                  if (seen.has(key)) continue;
                  seen.add(key);

                  const rect = el.getBoundingClientRect();
                  if (rect.width < 5 || rect.height < 5) continue;
                  // Skip elements completely outside viewport
                  if (rect.right < 0 || rect.bottom < 0 || rect.left > SLIDE_W || rect.top > SLIDE_H + 50) continue;

                  const style = doc.defaultView.getComputedStyle(el);
                  const fontSize = parseFloat(style.fontSize) || 16;

                  // Convert rgb(r,g,b) to hex
                  let color = '#333333';
                  const rgb = style.color.match(/\d+/g);
                  if (rgb && rgb.length >= 3) {
                    color = '#' + rgb.slice(0, 3).map(c => parseInt(c).toString(16).padStart(2, '0')).join('');
                  }

                  elements.push({
                    text: text,
                    x_percent: (rect.left / SLIDE_W) * 100,
                    y_percent: (rect.top / SLIDE_H) * 100,
                    width_percent: (rect.width / SLIDE_W) * 100,
                    height_percent: (rect.height / SLIDE_H) * 100,
                    font_size_pt: Math.round(fontSize * 0.75), // px to pt
                    bold: parseInt(style.fontWeight) >= 600 || style.fontWeight === 'bold',
                    color: color,
                    align: style.textAlign === 'start' ? 'left' : style.textAlign,
                  });
                }

                clearTimeout(timeout);
                cleanup();
                resolve({ elements });
              } catch (e) {
                console.warn('DOM text extraction error:', e);
                clearTimeout(timeout);
                cleanup();
                resolve({ elements: [] });
              }
            }, 300);
          }).catch(() => { clearTimeout(timeout); cleanup(); resolve({ elements: [] }); });
        } catch (e) {
          clearTimeout(timeout);
          cleanup();
          resolve({ elements: [] });
        }
      };

      // Write the HTML
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
      } else {
        clearTimeout(timeout);
        cleanup();
        resolve({ elements: [] });
      }
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Native PPTX export — Phase 2: structural DOM extractor
  //
  // Walks the rendered iframe DOM and produces a tree capturing every
  // visible element with its computed styles, bounding box, inline
  // SVG markup, image src, and direct text content. Feeds the native
  // PPTX pipeline that builds real shapes / text boxes / pictures
  // instead of the screenshot + invisible-text-overlay hack.
  //
  // Runs in parallel with the legacy text-only extractor above during
  // the migration; the two coexist until the native pipeline is
  // confident enough to delete the legacy path.
  // ──────────────────────────────────────────────────────────────
  const extractStructuredDOM = (html) => {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:1280px;height:720px;border:none;visibility:hidden;';
      // allow-scripts so Chart.js (charts, geo maps) actually draws — the
      // preview iframe runs scripts too, so this matches what the user sees.
      // Without it every <canvas> is blank at extraction time and the export
      // drops chart content entirely.
      iframe.sandbox = 'allow-scripts allow-same-origin';
      document.body.appendChild(iframe);

      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const timeout = setTimeout(() => { cleanup(); resolve({ tree: null }); }, 10000);

      iframe.onload = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) { clearTimeout(timeout); cleanup(); resolve({ tree: null }); return; }

          // Wait for every canvas to actually PAINT before extracting. A
          // fixed delay was a guess: a slow Chart.js render serialized to a
          // transparent PNG and the deck exported with an empty chart area,
          // silently (user report, 11/08). Mirrors the server screenshot
          // path's alpha poll; capped so a canvas that never paints cannot
          // hang the export.
          const canvasesPainted = () => new Promise((res) => {
            const cs = Array.from(doc.querySelectorAll('canvas'));
            if (!cs.length) return res();
            const started = Date.now();
            const painted = (c) => {
              try {
                if (!c.width || !c.height) return false;
                const ctx = c.getContext('2d');
                if (!ctx) return true;   // non-2d context: assume painted
                const d = ctx.getImageData(0, 0, Math.min(c.width, 300), Math.min(c.height, 300)).data;
                for (let i = 3; i < d.length; i += 4) if (d[i] !== 0) return true;
                return false;
              } catch { return true; }  // tainted: cannot inspect, assume painted
            };
            const tick = () => {
              if (cs.every(painted)) return res();
              if (Date.now() - started > 6000) {
                console.warn('[export] canvas still blank after 6s — exporting anyway');
                return res();
              }
              setTimeout(tick, 120);
            };
            tick();
          });

          const fontsReady = doc.fonts?.ready || Promise.resolve();
          Promise.all([fontsReady, canvasesPainted()]).then(() => {
            // Layout-settle delay — matches the legacy extractor so
            // both see the same rendered state.
            setTimeout(() => {
              try {
                const SLIDE_W = 1280, SLIDE_H = 720;
                const win = doc.defaultView;

                // Skip entirely — these never contribute to visual output
                const SKIP_TAGS = new Set(['script', 'style', 'link', 'meta', 'noscript', 'title', 'head']);

                // Inline-display tags for the runs heuristic. A block element
                // whose children are all inline-like is treated as a text leaf
                // and emitted as one text box with styled runs, instead of
                // recursing (which would cause child-span positioning to clash
                // with the parent's text box).
                const INLINE_TAGS = new Set([
                  'span', 'em', 'strong', 'b', 'i', 'u', 'a', 'small', 'mark',
                  'sub', 'sup', 'code', 'time', 'abbr', 's', 'del', 'ins', 'br',
                  'cite', 'q', 'dfn', 'samp', 'kbd', 'var', 'wbr', 'ruby', 'rt',
                  'font', 'label',
                ]);
                const INLINE_DISPLAYS = new Set([
                  'inline', 'inline-block', 'inline-flex', 'inline-grid', 'contents',
                ]);

                // Convert CSS color (rgb/rgba) to #RRGGBB or null when fully transparent
                const rgbToHex = (v) => {
                  if (!v) return null;
                  if (v === 'transparent') return null;
                  const rgba = v.match(/rgba?\(([^)]+)\)/);
                  if (!rgba) return null;
                  const parts = rgba[1].split(',').map(s => parseFloat(s.trim()));
                  if (parts.length < 3) return null;
                  if (parts.length === 4 && parts[3] === 0) return null; // fully transparent
                  const [r, g, b] = parts.slice(0, 3).map(n => Math.max(0, Math.min(255, Math.round(n))));
                  return '#' + [r, g, b].map(n => n.toString(16).padStart(2, '0')).join('');
                };

                // Parse "1px solid rgb(…)" into { width, style, color } or null
                const parseBorder = (b) => {
                  if (!b) return null;
                  if (b === 'none' || b.startsWith('0px')) return null;
                  const m = b.match(/^([\d.]+)px\s+(\w+)\s+(.+)$/);
                  if (!m) return null;
                  const width = parseFloat(m[1]);
                  if (width < 0.5) return null;
                  return { width, style: m[2], color: rgbToHex(m[3]) };
                };

                // Decompose CSS matrix(a,b,c,d,tx,ty) into rotate + scale
                const parseTransform = (t) => {
                  if (!t || t === 'none') return null;
                  const m = t.match(/matrix\(([-\d., ]+)\)/);
                  if (!m) return null;
                  const parts = m[1].split(',').map(s => parseFloat(s.trim()));
                  if (parts.length < 4) return null;
                  const [a, b, c, d] = parts;
                  const scaleX = Math.hypot(a, b);
                  const scaleY = Math.hypot(c, d);
                  const rotate = (Math.atan2(b, a) * 180) / Math.PI;
                  // Ignore imperceptible transforms
                  if (Math.abs(rotate) < 0.01 && Math.abs(scaleX - 1) < 0.01 && Math.abs(scaleY - 1) < 0.01) return null;
                  return { rotate, scaleX, scaleY };
                };

                const isVisible = (el, style, rect) => {
                  if (style.display === 'none') return false;
                  if (style.visibility === 'hidden') return false;
                  if (parseFloat(style.opacity) === 0) return false;
                  if (rect.width < 1 || rect.height < 1) return false;
                  // Element completely outside the slide viewport — ignore
                  if (rect.right < 0 || rect.bottom < 0) return false;
                  if (rect.left > SLIDE_W + 100 || rect.top > SLIDE_H + 100) return false;
                  return true;
                };

                // Concatenate only direct text-node children (no recursion)
                const directText = (el) => {
                  let t = '';
                  for (const child of el.childNodes) {
                    if (child.nodeType === Node.TEXT_NODE) t += child.nodeValue;
                  }
                  return t.replace(/\s+/g, ' ').trim();
                };

                // Full text INCLUDING descendants, whitespace-collapsed.
                // The converter uses this when the node has only inline children
                // so "The Future <span>Designing</span> the New Era" renders as
                // one text box with the correct content order instead of three
                // overlapping boxes.
                const fullText = (el) => {
                  const raw = el.innerText || el.textContent || '';
                  return raw.replace(/\s+/g, ' ').trim();
                };

                // Whether a direct child counts as inline for text-leaf detection.
                // Text nodes are always inline. Element nodes are inline when
                // their tag is in INLINE_TAGS OR computed display is inline-ish.
                //
                // IMPORTANT: visual-content tags (svg/img/canvas/video/iframe)
                // are NEVER inline for this purpose even though their default
                // display is 'inline' — if a parent swallowed them into a text
                // leaf, the converter would skip recursion and their pictures
                // would never render. This was why wavy-underline SVGs next to
                // text were disappearing in the last export.
                const VISUAL_CONTENT_TAGS = new Set([
                  'svg', 'img', 'canvas', 'video', 'iframe', 'picture', 'object', 'embed',
                ]);
                const isInlineChild = (child) => {
                  if (child.nodeType === Node.TEXT_NODE) return true;
                  if (child.nodeType !== Node.ELEMENT_NODE) return false;
                  const t = (child.tagName || '').toLowerCase();
                  if (VISUAL_CONTENT_TAGS.has(t)) return false;
                  if (INLINE_TAGS.has(t)) return true;
                  try {
                    return INLINE_DISPLAYS.has(win.getComputedStyle(child).display);
                  } catch {
                    return false;
                  }
                };

                // True if all element children of el are inline-like. Text-node
                // children are inline by nature. Used to decide whether this
                // element is a text leaf (render as one text box with runs)
                // vs. a structural container (recurse into children).
                const allChildrenInline = (el) => {
                  for (const child of el.childNodes) {
                    if (child.nodeType === Node.ELEMENT_NODE && !isInlineChild(child)) {
                      return false;
                    }
                  }
                  return true;
                };

                // Extract ordered runs: each child node becomes one run with
                // its own color/weight/etc. Preserves inline-styled accents
                // like "The Future of <span style='color:purple'>Creativity.</span>"
                // which the converter renders as a single text box with two runs.
                // Whitespace in text nodes is preserved so word separators survive.
                // <br> elements become an explicit '\n' run — the backend splits
                // on those into multiple paragraphs, so "The Paradigm Shift<br>
                // in Visual Workflows" doesn't collapse to "Shiftin Visual".
                const extractRuns = (el, injectSpaces = false) => {
                  const runs = [];
                  for (const child of el.childNodes) {
                    if (child.nodeType === Node.TEXT_NODE) {
                      const t = child.nodeValue;
                      if (!t) continue;
                      runs.push({ text: t });  // inherits parent style
                    } else if (child.nodeType === Node.ELEMENT_NODE) {
                      const tag = (child.tagName || '').toLowerCase();
                      // <br> is a line-break with no text — emit an explicit newline
                      if (tag === 'br') {
                        runs.push({ text: '\n', linebreak: true });
                        continue;
                      }
                      const t = child.textContent || '';
                      if (!t) continue;
                      // Flex rows separate children visually via `gap`, not
                      // whitespace text nodes — inject a space so adjacent
                      // spans don't fuse ("So what:Broadcasters"). Skip when
                      // either side already carries whitespace or a linebreak.
                      if (injectSpaces && runs.length > 0
                          && !runs[runs.length - 1].linebreak
                          && !/\s$/.test(runs[runs.length - 1].text || '')
                          && !/^\s/.test(t)) {
                        runs.push({ text: ' ' });
                      }
                      let cs;
                      try { cs = win.getComputedStyle(child); } catch { cs = null; }
                      const run = { text: t };
                      if (cs) {
                        const col = rgbToHex(cs.color);
                        if (col) run.color = col;
                        if (cs.fontWeight && (cs.fontWeight === 'bold' || parseInt(cs.fontWeight, 10) >= 600)) run.bold = true;
                        if (cs.fontStyle === 'italic') run.italic = true;
                        const td = cs.textDecorationLine && cs.textDecorationLine !== 'none' ? cs.textDecorationLine : '';
                        if (td.includes('underline')) run.underline = true;
                        const fs = parseFloat(cs.fontSize);
                        if (fs && Number.isFinite(fs)) run.fontSize = fs;
                        const ff = cs.fontFamily;
                        if (ff) run.fontFamily = ff;
                      }
                      runs.push(run);
                    }
                  }
                  return runs;
                };

                const walk = (el, depth) => {
                  if (depth > 40) return null; // runaway safety
                  const tag = (el.tagName || '').toLowerCase();
                  if (SKIP_TAGS.has(tag)) return null;

                  const style = win.getComputedStyle(el);
                  const rect = el.getBoundingClientRect();
                  if (!isVisible(el, style, rect)) return null;

                  const node = {
                    tag,
                    // Bounding box as percentages of the 1280×720 slide
                    bbox: {
                      x: (rect.left / SLIDE_W) * 100,
                      y: (rect.top / SLIDE_H) * 100,
                      w: (rect.width / SLIDE_W) * 100,
                      h: (rect.height / SLIDE_H) * 100,
                    },
                    style: {
                      // Layout mode — converter uses this to decide text-leaf
                      // vs structural container. A flex/grid parent with inline
                      // children is NOT a text leaf (children are positioned
                      // intentionally), so we must recurse not collapse.
                      display: style.display || null,
                      // Typography
                      fontFamily: style.fontFamily || null,
                      fontSize: parseFloat(style.fontSize) || null,     // px
                      fontWeight: style.fontWeight || null,
                      fontStyle: style.fontStyle || null,
                      color: rgbToHex(style.color),
                      textAlign: style.textAlign === 'start' ? 'left' : style.textAlign,
                      lineHeight: style.lineHeight || null,
                      letterSpacing: style.letterSpacing || null,
                      textDecoration: style.textDecorationLine && style.textDecorationLine !== 'none' ? style.textDecorationLine : null,
                      textTransform: style.textTransform && style.textTransform !== 'none' ? style.textTransform : null,
                      // Box
                      backgroundColor: rgbToHex(style.backgroundColor),
                      // backgroundImage captures linear-gradients, url()
                      // references, and CSS patterns. Backend derives fill
                      // color from it when backgroundColor is transparent
                      // (common pattern for flat fills implemented via
                      // linear-gradient for theming flexibility).
                      backgroundImage: (style.backgroundImage && style.backgroundImage !== 'none') ? style.backgroundImage : null,
                      // backgroundSize rides along for url() photos: CSS
                      // 'cover' crops in the browser but the PPTX converter
                      // stretches pictures into their box — same distortion
                      // class as <img> object-fit, different property.
                      backgroundSize: (style.backgroundSize && style.backgroundSize !== 'auto') ? style.backgroundSize : null,
                      // backgroundClip tells the renderer whether the
                      // element's backgroundImage visually fills the box
                      // ('border-box'/default) or only the text glyphs
                      // ('text' — used for CSS text-gradient effects). For
                      // 'text' we must NOT render a rectangle, or the
                      // gradient paints over the text itself.
                      backgroundClip: style.backgroundClip || style.webkitBackgroundClip || null,
                      borderRadius: style.borderRadius || null,
                      border: {
                        top: parseBorder(style.borderTop),
                        right: parseBorder(style.borderRight),
                        bottom: parseBorder(style.borderBottom),
                        left: parseBorder(style.borderLeft),
                      },
                      boxShadow: style.boxShadow && style.boxShadow !== 'none' ? style.boxShadow : null,
                      padding: {
                        top: parseFloat(style.paddingTop) || 0,
                        right: parseFloat(style.paddingRight) || 0,
                        bottom: parseFloat(style.paddingBottom) || 0,
                        left: parseFloat(style.paddingLeft) || 0,
                      },
                      // Layering / transforms
                      opacity: parseFloat(style.opacity),
                      zIndex: style.zIndex === 'auto' ? null : parseInt(style.zIndex, 10),
                      transform: parseTransform(style.transform),
                      overflow: style.overflow || null,
                      // CSS features PPTX can't natively represent — flag for
                      // per-element rasterization in Phase 5
                      backdropFilter: style.backdropFilter && style.backdropFilter !== 'none' ? style.backdropFilter : null,
                      mixBlendMode: style.mixBlendMode && style.mixBlendMode !== 'normal' ? style.mixBlendMode : null,
                      filter: style.filter && style.filter !== 'none' ? style.filter : null,
                    },
                    text: null,
                    src: null,
                    svgMarkup: null,
                    children: [],
                  };

                  // SVG: capture full inline markup (cairosvg will convert → EMF
                  // in Phase 3); don't recurse into SVG internals.
                  // Bake COMPUTED fill/stroke into the markup: icon colors come
                  // from CSS outside the element (currentColor, var(--brand-*),
                  // classes), which raw outerHTML doesn't carry — the rasterizer
                  // then painted stroke-less, fill="none" skeletons, i.e.
                  // invisible icons in the PPTX export.
                  if (tag === 'svg') {
                    try {
                      const clone = el.cloneNode(true);
                      const srcEls = [el, ...el.querySelectorAll('*')];
                      const dstEls = [clone, ...clone.querySelectorAll('*')];
                      for (let k = 0; k < srcEls.length && k < dstEls.length; k++) {
                        if (!dstEls[k] || !dstEls[k].setAttribute) continue;
                        const cs2 = win.getComputedStyle(srcEls[k]);
                        if (cs2.fill) dstEls[k].setAttribute('fill', cs2.fill);
                        if (cs2.stroke) dstEls[k].setAttribute('stroke', cs2.stroke);
                        if (cs2.strokeWidth) dstEls[k].setAttribute('stroke-width', cs2.strokeWidth);
                        if (cs2.opacity && cs2.opacity !== '1') dstEls[k].setAttribute('opacity', cs2.opacity);
                      }
                      node.svgMarkup = clone.outerHTML;
                    } catch {
                      try { node.svgMarkup = el.outerHTML; } catch {}
                    }
                    return node;
                  }

                  // <img>: capture src; no children to walk. objectFit rides
                  // along because PPTX pictures STRETCH to their box by
                  // default (CSS 'fill') while generated slides use 'cover'
                  // (crop) for photos — without it every photo whose natural
                  // aspect differs from its box exports visibly distorted.
                  if (tag === 'img') {
                    node.src = el.currentSrc || el.src || el.getAttribute('src') || null;
                    try { node.objectFit = (style.objectFit || '').toLowerCase() || null; } catch {}
                    return node;
                  }

                  // <canvas>: freeze the rendered pixels (Chart.js charts,
                  // geo maps) to a PNG data URL so the converter can place a
                  // picture. Without this, canvas content is DROPPED entirely
                  // from the export — the single worst fidelity loss. Charts
                  // draw from data (no cross-origin bitmaps), so toDataURL is
                  // safe; a tainted canvas just falls through with src=null.
                  if (tag === 'canvas') {
                    // A canvas that is BLANK at capture time serializes to a
                    // fully transparent PNG, which the backend embeds happily —
                    // the user sees an empty chart area and nothing anywhere
                    // reports a problem. Verify real pixels before trusting it,
                    // and mark the node when they are missing so the backend
                    // can fall back instead of silently dropping the chart.
                    try {
                      const _cw = el.width, _ch = el.height;
                      let _drawn = false;
                      try {
                        const _ctx = el.getContext('2d');
                        if (_ctx && _cw && _ch) {
                          const _d = _ctx.getImageData(0, 0, Math.min(_cw, 300), Math.min(_ch, 300)).data;
                          for (let _i = 3; _i < _d.length; _i += 4) {
                            if (_d[_i] !== 0) { _drawn = true; break; }
                          }
                        } else if (_cw && _ch) {
                          _drawn = true;   // webgl/other context — assume painted
                        }
                      } catch { _drawn = true; }   // tainted read — trust toDataURL
                      node.src = el.toDataURL('image/png');
                      node.canvasBlank = !_drawn;
                      if (!_drawn) console.warn('[export] canvas captured BLANK — chart had not painted');
                    } catch (e) {
                      node.canvasBlank = true;
                      console.warn('[export] canvas capture failed (tainted?):', e && e.message);
                    }
                    return node;
                  }

                  // Capture text in three complementary forms so the backend
                  // can pick the best rendering strategy per node:
                  //   - text:      direct-child text nodes only
                  //   - innerText: full text including descendants (fallback)
                  //   - runs:      ordered sequence of styled text runs,
                  //                present only when this element is a text
                  //                leaf (all children are inline). Lets the
                  //                converter emit ONE text box with the
                  //                original inline color/weight accents
                  //                preserved (e.g. "Creativity." in purple).
                  const direct = directText(el);
                  if (direct) node.text = direct;
                  const full = fullText(el);
                  if (full) node.innerText = full;

                  // Only capture runs when this block's content is a flow
                  // of inline children — otherwise flex/grid containers like
                  // <header style="display:flex"> would collapse their
                  // space-between children into a single concatenated text box
                  // at the header's bbox (causing the "2025 · VOL.01" + logo
                  // stacking we've been seeing).
                  const display = style.display || '';
                  const isBlockish = !INLINE_DISPLAYS.has(display)
                    && display !== 'flex' && display !== 'grid'
                    && display !== 'inline-flex' && display !== 'inline-grid';
                  // Flex/grid containers whose children form ONE single-row
                  // text flow (a "So what:" label + span with a small gap)
                  // also deserve the one-box runs path — otherwise the parent
                  // text and each span render as SEPARATE overlapping boxes
                  // ("to ItLargest") and inter-span spaces vanish. The guards
                  // keep genuine layout rows excluded: space-between headers
                  // fail the gap check, stacked columns fail the same-row
                  // check.
                  const isSingleRowTextFlow = () => {
                    if ((style.flexDirection || '').startsWith('column')) return false;
                    const kids = Array.from(el.children);
                    if (kids.length === 0) return false;
                    // A child that draws its OWN box (badge-pill background,
                    // border) or is purely decorative (no text) must keep its
                    // individual shape — collapsing it into a text run erases
                    // the pill and can leave white-on-white run text.
                    for (const k of kids) {
                      if (!(k.textContent || '').trim()) return false;
                      let kcs;
                      try { kcs = win.getComputedStyle(k); } catch { return false; }
                      if (rgbToHex(kcs.backgroundColor)) return false;
                      if (parseBorder(kcs.borderTop) || parseBorder(kcs.borderRight)
                        || parseBorder(kcs.borderBottom) || parseBorder(kcs.borderLeft)) return false;
                    }
                    const rects = kids.map(k => k.getBoundingClientRect());
                    const tops = rects.map(r => r.top);
                    if (Math.max(...tops) - Math.min(...tops) > 12) return false;
                    const sorted = rects.slice().sort((a, b) => a.left - b.left);
                    for (let i = 1; i < sorted.length; i++) {
                      if (sorted[i].left - sorted[i - 1].right > 60) return false;
                    }
                    return true;
                  };
                  const isFlexTextRow = !isBlockish && !INLINE_DISPLAYS.has(display)
                    && (display === 'flex' || display === 'grid') && isSingleRowTextFlow();
                  if ((isBlockish || isFlexTextRow) && full && allChildrenInline(el)) {
                    const runs = extractRuns(el, isFlexTextRow).filter(r => (r.text || '').length > 0);
                    if (runs.length > 0) node.runs = runs;
                  }

                  for (const child of el.children) {
                    const cn = walk(child, depth + 1);
                    if (cn) node.children.push(cn);
                  }

                  return node;
                };

                const tree = walk(doc.body, 0);
                clearTimeout(timeout);
                cleanup();
                resolve({ tree });
              } catch (err) {
                console.warn('[DOM-STRUCT] extraction error:', err);
                clearTimeout(timeout);
                cleanup();
                resolve({ tree: null });
              }
              // Canvas slides need Chart.js to finish drawing before
              // toDataURL captures the pixels. A FIXED delay is a guess: a
              // slow chart still serializes blank and the export ships an
              // empty rectangle. Poll for real pixels instead (the server's
              // screenshot path already does exactly this), capped so a
              // never-painting canvas cannot hang the export.
            }, (doc.querySelector('canvas') ? 0 : 300));
          }).catch(() => { clearTimeout(timeout); cleanup(); resolve({ tree: null }); });
        } catch (err) {
          clearTimeout(timeout);
          cleanup();
          resolve({ tree: null });
        }
      };

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        // Kill Chart.js animation in the extraction copy: hidden iframes
        // throttle requestAnimationFrame, so an animated chart may still be
        // mid-draw when toDataURL fires. The defaults switch must run right
        // AFTER the Chart.js CDN tag and BEFORE the inline init scripts at
        // the end of body (which execute during parsing, ahead of
        // DOMContentLoaded). With animation off the first paint is the final
        // frame and the settle delay isn't load-bearing.
        doc.write(html.replace(
          /(<script[^>]*src=[^>]*chart[^>]*><\/script>)/i,
          '$1<script>try{if(window.Chart&&Chart.defaults){Chart.defaults.animation=false;}}catch(e){}</script>'
        ));
        doc.close();
      } else {
        clearTimeout(timeout);
        cleanup();
        resolve({ tree: null });
      }
    });
  };

  // Batched structural-DOM extraction across all slides
  const extractAllSlideDOMs = async (slides) => {
    const doms = {};
    const batchSize = 3;
    for (let i = 0; i < slides.length; i += batchSize) {
      const batch = slides.slice(i, i + batchSize);
      const results = await Promise.all(
        batch.map((slide, batchIdx) => {
          const idx = i + batchIdx;
          if (!slide?.html_source) return Promise.resolve(null);
          return extractStructuredDOM(stripAnimationsFromHtml(slide.html_source))
            .then(dom => ({ idx, dom }));
        })
      );
      results.forEach(r => { if (r?.dom?.tree) doms[r.idx] = r.dom; });
    }
    return doms;
  };

  // Extract text layouts from all slides (parallel, with concurrency limit)
  const extractAllSlideLayouts = async (slides) => {
    const layouts = {};
    // Process 3 at a time to avoid DOM overload
    const batchSize = 3;
    for (let i = 0; i < slides.length; i += batchSize) {
      const batch = slides.slice(i, i + batchSize);
      const results = await Promise.all(
        batch.map((slide, batchIdx) => {
          const idx = i + batchIdx;
          if (!slide?.html_source) return Promise.resolve(null);
          return extractTextLayoutFromHtml(stripAnimationsFromHtml(slide.html_source))
            .then(layout => ({ idx, layout }));
        })
      );
      results.forEach(r => { if (r?.layout?.elements?.length > 0) layouts[r.idx] = r.layout; });
    }
    return layouts;
  };

  // Strip CSS animations and transitions from HTML for clean PPTX export
  const stripAnimationsFromHtml = (html) => {
    if (!html) return html;
    return html
      // Remove @keyframes blocks
      .replace(/@keyframes\s+[\w-]+\s*\{[^}]*(?:\{[^}]*\}[^}]*)*\}/gi, '')
      // Remove animation properties
      .replace(/animation\s*:[^;}"']+[;]/gi, '')
      .replace(/animation-[\w-]+\s*:[^;}"']+[;]/gi, '')
      // Remove transition properties
      .replace(/transition\s*:[^;}"']+[;]/gi, '')
      .replace(/transition-[\w-]+\s*:[^;}"']+[;]/gi, '')
      // Force opacity to 1 and transform to none (common animation targets that leave elements invisible)
      .replace(/(opacity\s*:\s*)0(\s*[;}"'])/gi, '$11$2');
  };

  // Export presentation (mode param overrides state to avoid stale-state issues)
  const handleExport = async (mode) => {
    if (generatedSlides.length === 0) {
      toast.error('No slides to export');
      return;
    }

    // Handle video export
    if (generatedVideoUrl) {
      setExporting(true);
      toast.loading('Downloading video...', { id: 'export-ppt' });
      try {
        // If it's a data URL, download directly
        if (generatedVideoUrl.startsWith('data:')) {
          const a = document.createElement('a');
          a.href = generatedVideoUrl;
          a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}.mp4`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else if (generatedVideoUrl.startsWith('/api/')) {
          // Local download endpoint
          const response = await fetch(withBackend(generatedVideoUrl));
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}.mp4`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          // External URL (Supabase)
          window.open(generatedVideoUrl, '_blank');
        }
        toast.success('Video downloaded!', { id: 'export-ppt' });
      } catch (error) {
        console.error('Video export error:', error);
        toast.error('Failed to download video', { id: 'export-ppt' });
      } finally {
        setExporting(false);
      }
      return;
    }

    setExporting(true);
    const isEditable = (mode || exportMode) === 'editable';
    const hasHtmlSlides = generatedSlides.some(s => s?.html_source);
    const toastMessage = isEditable
      ? hasHtmlSlides ? 'Creating editable PowerPoint from HTML slides...' : 'Creating editable PowerPoint (analyzing layouts)...'
      : 'Generating PowerPoint file...';
    toast.loading(toastMessage, { id: 'export-ppt' });

    try {
      // Route HTML slides to the specialized endpoint for true editable export
      const endpoint = (isEditable && hasHtmlSlides)
        ? '/export-html-pptx'
        : isEditable ? '/export-editable-pptx' : '/export-created-pptx';

      // Prepare slides data — include html_source for HTML export, strip animations for clean export
      const slidesPayload = generatedSlides.filter(s => s != null).map(slide => ({
        ...slide,
        html_source: slide.html_source ? stripAnimationsFromHtml(slide.html_source) : undefined,
        preview_base64: slide.preview || slide.preview_base64 || null,
        bullet_points: slide.bullet_points || slide.bulletPoints || []
      }));

      // For editable HTML export: run both extractors in parallel.
      // - extractedLayouts feeds the legacy screenshot+overlay pipeline (text positions only)
      // - domStructures feeds the Phase 2+ native pipeline (full DOM tree with styles)
      // Both are sent to the backend; the dispatcher picks which to use.
      let extractedLayouts = null;
      let domStructures = null;
      if (isEditable && hasHtmlSlides) {
        try {
          toast.loading('Extracting slide structure...', { id: 'export-ppt' });
          const [layouts, doms] = await Promise.all([
            extractAllSlideLayouts(slidesPayload),
            extractAllSlideDOMs(slidesPayload),
          ]);
          extractedLayouts = layouts;
          domStructures = doms;
          const layoutCount = Object.keys(extractedLayouts || {}).length;
          const domCount = Object.keys(domStructures || {}).length;
          console.log(`[PPTX Export] Client extraction — layouts: ${layoutCount}/${slidesPayload.length}, DOM trees: ${domCount}/${slidesPayload.length}`);
          if (layoutCount > 0 || domCount > 0) {
            toast.loading('Creating editable PowerPoint...', { id: 'export-ppt' });
          }
        } catch (extractErr) {
          console.warn('[PPTX Export] Client-side extraction failed, backend will use fallbacks:', extractErr);
          extractedLayouts = null;
          domStructures = null;
        }
      }

      const response = await fetch(withBackend(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          presentation_title: presentationTitle,
          title: presentationTitle,
          slides: isEditable ? slidesPayload : generatedSlides.map(s => s?.html_source ? { ...s, html_source: stripAnimationsFromHtml(s.html_source) } : s),
          brand_colors: (useBrandGuidelines && brandGuidelines?.colors) || [],
          // brand_identity_id lets the backend fetch brand.custom_fonts
          // for native PPTX font embedding (phase 3.3+). user_id is a
          // fallback for reloaded projects whose brand id wasn't persisted —
          // backend resolves the user's default brand.
          brand_identity_id: brandIdentityId || brandIdentityIdRef.current || '',
          user_id: userId || '',
          ...(extractedLayouts && Object.keys(extractedLayouts).length > 0 ? { extracted_layouts: extractedLayouts } : {}),
          ...(domStructures && Object.keys(domStructures).length > 0 ? { dom_structures: domStructures } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export presentation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = isEditable ? '_editable' : '';
      a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}${suffix}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(isEditable ? 'Editable presentation exported!' : 'Presentation exported!', { id: 'export-ppt' });

    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export', { id: 'export-ppt' });
    } finally {
      setExporting(false);
    }
  };

  // Export as images (ZIP)
  const handleExportImages = async () => {
    if (generatedSlides.length === 0) return;
    setExporting(true);
    toast.loading('Exporting slides as images...', { id: 'export-ppt' });
    try {
      const response = await fetch(withBackend('/export-slides-as-images'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presentation_title: presentationTitle,
          slides: generatedSlides.filter(s => s != null),
        }),
      });
      if (!response.ok) throw new Error('Failed to export images');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}_images.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Images exported!', { id: 'export-ppt' });
    } catch (error) {
      console.error('Image export error:', error);
      toast.error(error.message || 'Failed to export images', { id: 'export-ppt' });
    } finally {
      setExporting(false);
    }
  };

  // Export as PDF
  const handleExportPDF = async () => {
    if (generatedSlides.length === 0) return;
    setExporting(true);
    toast.loading('Exporting as PDF...', { id: 'export-ppt' });
    try {
      const response = await fetch(withBackend('/export-slides-as-pdf'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presentation_title: presentationTitle,
          slides: generatedSlides.filter(s => s != null),
        }),
      });
      if (!response.ok) throw new Error('Failed to export PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exported!', { id: 'export-ppt' });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(error.message || 'Failed to export PDF', { id: 'export-ppt' });
    } finally {
      setExporting(false);
    }
  };

  // PHASE 1 TEST: Generate the currently-viewed slide as a structured JSON model
  // This is purely a diagnostic to validate whether Gemini Flash can produce
  // reliable coordinates. No HTML/PPTX rendering happens — we just look at the JSON.
  // Production SVG-based PPTX export. Posts every slide's HTML to the
  // new /export-pptx-svg endpoint, which renders each one in a headless
  // browser, serializes the DOM to SVG, and embeds the SVG+PNG pair in
  // the final .pptx. Users right-click any slide in PowerPoint 2016+
  // and pick "Convert to Shape" to edit individual elements.
  const handleExportPptxSvg = async () => {
    if (generatedSlides.length === 0) {
      toast.error('No slides to export');
      return;
    }
    const htmlSlides = generatedSlides.filter(s => s?.html_source);
    if (htmlSlides.length === 0) {
      toast.error('No HTML slides to export');
      return;
    }

    setExporting(true);
    toast.loading(`Rendering ${htmlSlides.length} slide${htmlSlides.length > 1 ? 's' : ''}…`, { id: 'export-ppt' });
    try {
      const payload = {
        presentation_title: presentationTitle,
        title: presentationTitle,
        slides: htmlSlides.map(s => ({
          html_source: stripAnimationsFromHtml(s.html_source),
          title: s.title,
        })),
        brand_identity_id: brandIdentityId || brandIdentityIdRef.current || '',
        user_id: userId || '',
      };
      const resp = await fetch(withBackend('/export-pptx-svg'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${presentationTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(
        `${htmlSlides.length} slides exported. Right-click any slide → "Convert to Shape" to edit.`,
        { id: 'export-ppt', duration: 7000 }
      );
      // Degraded font embedding used to fail silently (deck opens in Calibri
      // on the client's machine). The backend now reports it via this header.
      const exportWarnings = resp.headers.get('X-Export-Warnings');
      if (exportWarnings) {
        toast.warning(exportWarnings, { duration: 12000 });
      }
    } catch (e) {
      console.error('[EXPORT-SVG] failed', e);
      toast.error(`Export failed: ${e.message}`, { id: 'export-ppt' });
    } finally {
      setExporting(false);
    }
  };

  // [SPIKE] Test the SVG → PPTX + PowerPoint Convert-to-Shape pipeline
  // on the currently-selected slide. Throwaway endpoint; existence is
  // entirely for a go/no-go decision, not for end users.
  const handleSpikeSvgExport = async () => {
    const slide = generatedSlides[currentSlideIndex];
    if (!slide?.html_source) {
      toast.error('Current slide has no html_source to spike');
      return;
    }
    toast.loading('Rendering SVG via Playwright…', { id: 'spike-svg' });
    try {
      const resp = await fetch(withBackend('/spike-svg-export'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: stripAnimationsFromHtml(slide.html_source) }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const svgSize = resp.headers.get('X-Spike-Svg-Size');
      const hasFobj = resp.headers.get('X-Spike-Has-ForeignObject');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spike_slide${currentSlideIndex + 1}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(
        `Spike .pptx downloaded (SVG ${svgSize} chars, foreignObject=${hasFobj === '1' ? 'YES — bad' : 'no'})`,
        { id: 'spike-svg', duration: 8000 }
      );
      console.log('[SPIKE-SVG] svg size:', svgSize, 'hasForeignObject:', hasFobj);
    } catch (e) {
      console.error('[SPIKE-SVG] failed', e);
      toast.error(`Spike failed: ${e.message}`, { id: 'spike-svg' });
    }
  };

  const handleTestStructuredSlide = async () => {
    const slide = generatedSlides[currentSlideIndex];
    if (!slide) {
      toast.error('No slide selected');
      return;
    }
    setStructuredTesting(true);
    setStructuredError(null);
    setStructuredModel(null);
    setShowStructuredModal(true);
    try {
      const slideContent = {
        title: slide.title || `Slide ${currentSlideIndex + 1}`,
        content: slide.content || '',
        bulletPoints: slide.bullet_points || slide.bulletPoints || [],
      };
      const resp = await fetch(withBackend('/api/slides/generate-structured'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slide_content: slideContent,
          brand_identity_id: brandIdentityId || brandIdentityIdRef.current || null,
          slide_index: currentSlideIndex,
          total_slides: generatedSlides.length || 1,
          slide_type: slide.slide_type || '',
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setStructuredError(data.error || `HTTP ${resp.status}`);
        if (data.raw_response) {
          setStructuredModel({ _raw: data.raw_response });
        }
      } else {
        setStructuredModel(data.model);
      }
    } catch (e) {
      setStructuredError(e.message || 'Network error');
    } finally {
      setStructuredTesting(false);
    }
  };

  // Export as editable PPTX using dom-to-pptx (client-side)
  const handleExportEditablePptx = async () => {
    const htmlSlides = generatedSlides.filter(s => s?.html_source);
    if (htmlSlides.length === 0) {
      toast.error('No HTML slides to export');
      return;
    }

    setExporting(true);
    toast.loading(`Exporting ${htmlSlides.length} slides as editable PPTX...`, { id: 'export-ppt' });

    try {
      const { exportToPptx } = await import('dom-to-pptx');

      // --- PPTX Export Helpers ---

      // Fix 1: Fully resolve SVG styles from iframe context onto the clone
      // Two issues: (a) SVG attributes like stroke="var(--brand-accent)" are XML attributes
      // that getComputedStyle doesn't touch, and (b) class-based SVG styles (e.g. .icon { fill: red })
      // are lost when the clone moves from the iframe to the main document because the iframe's
      // stylesheets don't exist in the main document. dom-to-pptx's own inlineSvgStyles reads
      // getComputedStyle from the main document window — which returns defaults for the clone.
      // Fix: read computed styles from the IFRAME's window while it's still alive, write as
      // inline attributes on the clone.
      const inlineSvgStylesFromIframe = (wrapperEl, iframeBody, iframeWin) => {
        // Read CSS custom properties from :root for var() resolution
        const rootStyles = iframeWin.getComputedStyle(iframeWin.document.documentElement);
        const resolveVar = (val) => {
          if (!val || !val.includes('var(')) return val;
          return val.replace(/var\(\s*(--[\w-]+)\s*(?:,\s*([^)]+))?\)/g, (_, name, fallback) => {
            const resolved = rootStyles.getPropertyValue(name)?.trim();
            return resolved || fallback?.trim() || '';
          });
        };

        // Match SVG elements in iframe (originals) with their clones in wrapper
        const iframeSvgs = iframeBody.querySelectorAll('svg');
        const cloneSvgs = wrapperEl.querySelectorAll('svg');
        const len = Math.min(iframeSvgs.length, cloneSvgs.length);

        const svgProps = ['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
          'stroke-opacity', 'fill-opacity', 'opacity', 'color', 'stop-color', 'stop-opacity',
          'stroke-dasharray', 'stroke-dashoffset', 'stroke-miterlimit', 'paint-order'];

        for (let s = 0; s < len; s++) {
          const origAll = iframeSvgs[s].querySelectorAll('*');
          const cloneAll = cloneSvgs[s].querySelectorAll('*');
          // Also process the <svg> root itself
          const origNodes = [iframeSvgs[s], ...origAll];
          const cloneNodes = [cloneSvgs[s], ...cloneAll];
          const nodeLen = Math.min(origNodes.length, cloneNodes.length);

          for (let n = 0; n < nodeLen; n++) {
            const computed = iframeWin.getComputedStyle(origNodes[n]);
            const clone = cloneNodes[n];
            for (const prop of svgProps) {
              const val = computed.getPropertyValue(prop);
              if (val && val !== '' && val !== 'none' && val !== 'auto') {
                clone.style.setProperty(prop, val);
              }
            }
            // Also resolve var() in XML attributes (stroke="var(--brand-accent)")
            for (const attr of ['stroke', 'fill', 'stop-color', 'color']) {
              const attrVal = clone.getAttribute(attr);
              if (attrVal?.includes('var(')) clone.setAttribute(attr, resolveVar(attrVal));
            }
          }
        }
      };

      // Fix 2: Convert clip-path elements to native shapes/SVGs for editable PPTX export
      // Strategy:
      //   circle()     → border-radius:50% (dom-to-pptx maps to native rounded shape, fully editable)
      //   polygon()    → inline <svg><polygon> (svgAsVector preserves as vector, "Convert to Shape" in PPT)
      //   complex/path → rasterize as PNG fallback

      const parseClipPathValue = (cp) => {
        if (!cp || cp === 'none') return null;
        // circle(50% at 50% 50%) or circle(50%)
        if (cp.startsWith('circle(')) return { type: 'circle' };
        // ellipse(50% 40% at 50% 50%)
        if (cp.startsWith('ellipse(')) return { type: 'ellipse' };
        // polygon(50% 0%, 100% 25%, ...)
        const polyMatch = cp.match(/polygon\((.+)\)/);
        if (polyMatch) {
          const points = polyMatch[1].split(',').map(p => {
            const parts = p.trim().split(/\s+/);
            return { x: parseFloat(parts[0]) / 100, y: parseFloat(parts[1]) / 100 };
          });
          if (points.length >= 3 && points.every(p => !isNaN(p.x) && !isNaN(p.y))) {
            return { type: 'polygon', points };
          }
        }
        return null; // unsupported — will rasterize
      };

      const convertClipPaths = async (wrapperEl) => {
        const clipped = [...wrapperEl.querySelectorAll('*')].filter(el =>
          el.style.clipPath && el.style.clipPath !== 'none' && el.style.clipPath !== ''
        );
        for (const el of clipped) {
          try {
            const rect = el.getBoundingClientRect();
            if (rect.width < 2 || rect.height < 2) continue;
            const parsed = parseClipPathValue(el.style.clipPath);

            if (parsed?.type === 'circle' || parsed?.type === 'ellipse') {
              // Circle/ellipse: strip clip-path, use border-radius instead
              // dom-to-pptx maps border-radius:50% to a native rounded shape (editable!)
              el.style.clipPath = 'none';
              el.style.borderRadius = parsed.type === 'circle' ? '50%' : '50%';
              el.style.overflow = 'hidden';
              continue;
            }

            if (parsed?.type === 'polygon' && parsed.points.length >= 3) {
              // Polygon: inject an SVG inside the element instead of replacing it.
              // Replacing the element would lose flex/grid positioning (the new
              // SVG has no inline left/top because flex children are positioned
              // by their parent, not by their own style).
              const w = rect.width;
              const h = rect.height;
              const bg = el.style.backgroundColor || getComputedStyle(el).backgroundColor || 'transparent';
              const border = el.style.borderColor || 'none';
              const borderW = parseFloat(el.style.borderWidth) || 0;

              // Build SVG polygon points string
              const svgPoints = parsed.points
                .map(p => `${(p.x * w).toFixed(1)},${(p.y * h).toFixed(1)}`)
                .join(' ');

              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('width', w);
              svg.setAttribute('height', h);
              svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
              svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              svg.style.cssText = `position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:0;`;

              const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
              polygon.setAttribute('points', svgPoints);
              polygon.setAttribute('fill', bg);
              if (borderW > 0 && border !== 'none') {
                polygon.setAttribute('stroke', border);
                polygon.setAttribute('stroke-width', borderW.toString());
              }
              svg.appendChild(polygon);

              // Strip clip-path and background — the SVG provides the visible shape now.
              // Keep the element in place so its flex/grid positioning and text children
              // remain intact.
              el.style.clipPath = 'none';
              el.style.background = 'transparent';
              el.style.backgroundColor = 'transparent';
              if (!el.style.position || el.style.position === 'static') {
                el.style.position = 'relative';
              }
              // Insert SVG as first child so existing text/children render on top
              el.insertBefore(svg, el.firstChild);
              continue;
            }

            // Fallback: rasterize complex clip-paths (path(), url(), etc.)
            // Inject as a background-style child img so the element keeps its
            // flex/grid positioning.
            const { default: html2canvas } = await import('html2canvas');
            const captured = await html2canvas(el, {
              backgroundColor: null, scale: 2, logging: false, useCORS: true,
              width: Math.ceil(rect.width), height: Math.ceil(rect.height),
            });
            const dataUrl = captured.toDataURL('image/png');
            const img = document.createElement('img');
            img.src = dataUrl;
            img.style.cssText = `position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:0;`;
            el.style.clipPath = 'none';
            el.style.background = 'transparent';
            el.style.backgroundColor = 'transparent';
            if (!el.style.position || el.style.position === 'static') {
              el.style.position = 'relative';
            }
            el.insertBefore(img, el.firstChild);
          } catch (e) {
            el.style.clipPath = 'none';
          }
        }
      };

      // Fix 7: Convert CSS-drawn decorative shapes to inline SVGs.
      // dom-to-pptx can faithfully export native <svg> elements as editable vector
      // shapes (with svgAsVector:true), but it has no idea what to do with a div
      // styled into a circle via border-radius:50%, a 1px-wide div used as a vertical
      // line, or an empty div with only a border. These end up as ugly rectangles or
      // get lost. Detect those CSS-drawn shapes and inject an equivalent SVG inside
      // the original element (same child-injection pattern as convertClipPaths so we
      // never disturb flex/grid positioning).
      const convertCssShapesToSvg = (wrapperEl) => {
        const escapeAttr = (v) => String(v ?? '').replace(/"/g, '&quot;');
        const isTransparent = (c) => !c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)' || c === 'rgba(0,0,0,0)';

        // Build dasharray for SVG from CSS border-style
        const dashArrayFor = (style, w) => {
          const sw = Math.max(1, w);
          if (style === 'dashed') return `${sw * 4} ${sw * 2}`;
          if (style === 'dotted') return `${sw} ${sw * 2}`;
          return null;
        };

        const injectShapeSvg = (el, svgString) => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = svgString;
          const svg = tempDiv.firstElementChild;
          if (!svg) return;
          // Position absolutely INSIDE the original element so flex/grid placement
          // of the original element is preserved (same pattern as convertClipPaths).
          svg.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:0;overflow:visible;');
          // Strip the original element's visual styling — the SVG provides them now.
          el.style.background = 'none';
          el.style.backgroundColor = 'transparent';
          el.style.border = 'none';
          el.style.borderRadius = '0';
          el.style.boxShadow = 'none';
          if (!el.style.position || el.style.position === 'static') {
            el.style.position = 'relative';
          }
          el.insertBefore(svg, el.firstChild);
        };

        const buildEllipseSvg = (w, h, fill, stroke, strokeWidth, dashArray, opacity) => {
          const sw = strokeWidth || 0;
          const rx = Math.max(0, (w - sw) / 2);
          const ry = Math.max(0, (h - sw) / 2);
          const dashAttr = dashArray ? ` stroke-dasharray="${dashArray}"` : '';
          const opacityAttr = opacity && opacity !== '1' ? ` opacity="${escapeAttr(opacity)}"` : '';
          return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><ellipse cx="${w / 2}" cy="${h / 2}" rx="${rx}" ry="${ry}" fill="${escapeAttr(fill)}" stroke="${escapeAttr(stroke)}" stroke-width="${sw}"${dashAttr}${opacityAttr}/></svg>`;
        };

        const buildLineSvg = (w, h, color, opacity) => {
          const opacityAttr = opacity && opacity !== '1' ? ` opacity="${escapeAttr(opacity)}"` : '';
          if (w >= h) {
            // horizontal line
            return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><line x1="0" y1="${h / 2}" x2="${w}" y2="${h / 2}" stroke="${escapeAttr(color)}" stroke-width="${h}" stroke-linecap="round"${opacityAttr}/></svg>`;
          }
          // vertical line
          return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><line x1="${w / 2}" y1="0" x2="${w / 2}" y2="${h}" stroke="${escapeAttr(color)}" stroke-width="${w}" stroke-linecap="round"${opacityAttr}/></svg>`;
        };

        const buildBorderedRectSvg = (w, h, fill, stroke, strokeWidth, borderRadius, dashArray, opacity) => {
          const sw = strokeWidth || 0;
          const inset = sw / 2;
          const rectW = Math.max(0, w - sw);
          const rectH = Math.max(0, h - sw);
          const r = Math.max(0, Math.min(borderRadius || 0, Math.min(rectW, rectH) / 2));
          const dashAttr = dashArray ? ` stroke-dasharray="${dashArray}"` : '';
          const opacityAttr = opacity && opacity !== '1' ? ` opacity="${escapeAttr(opacity)}"` : '';
          return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect x="${inset}" y="${inset}" width="${rectW}" height="${rectH}" rx="${r}" ry="${r}" fill="${escapeAttr(fill)}" stroke="${escapeAttr(stroke)}" stroke-width="${sw}"${dashAttr}${opacityAttr}/></svg>`;
        };

        // Snapshot the element list now — we'll be inserting children, so don't
        // re-walk after each modification.
        const candidates = [...wrapperEl.querySelectorAll('*')];
        for (const el of candidates) {
          // Skip SVGs and anything inside an SVG (already handled by Fix 1)
          if (el.tagName === 'SVG' || el.tagName === 'svg') continue;
          if (el.closest('svg')) continue;
          // Skip elements with children — we'd hide them
          if (el.children.length > 0) continue;
          // Skip elements with text content — we'd hide it
          const text = el.textContent?.trim();
          if (text && text.length > 0) continue;
          // Skip <img> and other replaced elements — they have their own visuals
          const tag = el.tagName.toLowerCase();
          if (tag === 'img' || tag === 'video' || tag === 'canvas' || tag === 'iframe' || tag === 'input' || tag === 'textarea' || tag === 'button') continue;

          const rect = el.getBoundingClientRect();
          const w = rect.width;
          const h = rect.height;
          // Skip invisible / too-small / slide-sized
          if (w < 2 || h < 2) continue;
          if (w > 1200 && h > 700) continue; // probably the slide background

          // Read computed styles from the live DOM (the wrapper is on the stage now)
          const cs = window.getComputedStyle(el);
          const bg = cs.backgroundColor;
          const hasBg = !isTransparent(bg);
          // Use border-top-* as the canonical border (most CSS sets a uniform border)
          const borderColor = cs.borderTopColor || cs.borderColor || '';
          const borderWidth = parseFloat(cs.borderTopWidth || cs.borderWidth) || 0;
          const borderStyle = cs.borderTopStyle || cs.borderStyle || 'none';
          const hasBorder = borderWidth > 0 && !isTransparent(borderColor) && borderStyle !== 'none';
          const opacity = cs.opacity || '1';
          const borderRadius = parseFloat(cs.borderTopLeftRadius || cs.borderRadius) || 0;

          // Nothing visible to draw
          if (!hasBg && !hasBorder) continue;

          // 1. Circle/ellipse: border-radius covers ≥ ~50% of the smaller dimension
          //    AND aspect ratio is roughly square-to-2:1 (lets capsules through too)
          const isRound = borderRadius >= Math.min(w, h) / 2 - 1;
          if (isRound) {
            const fill = hasBg ? bg : 'none';
            const stroke = hasBorder ? borderColor : 'none';
            const dash = dashArrayFor(borderStyle, borderWidth);
            injectShapeSvg(el, buildEllipseSvg(w, h, fill, stroke, borderWidth, dash, opacity));
            continue;
          }

          // 2. Thin line: one dimension < 4px and the other > 16px
          const isHLine = h < 4 && w > 16;
          const isVLine = w < 4 && h > 16;
          if (isHLine || isVLine) {
            const color = hasBg ? bg : borderColor;
            injectShapeSvg(el, buildLineSvg(w, h, color, opacity));
            continue;
          }

          // 3. Empty bordered rectangle (only border, no background) → outlined rect
          //    Or a filled rect with border (background AND border) → both
          if (hasBorder || hasBg) {
            // Skip plain filled rectangles with no border and no border-radius —
            // dom-to-pptx already handles those fine via background-color.
            if (hasBg && !hasBorder && borderRadius === 0) continue;
            const fill = hasBg ? bg : 'none';
            const stroke = hasBorder ? borderColor : 'none';
            const dash = dashArrayFor(borderStyle, borderWidth);
            injectShapeSvg(el, buildBorderedRectSvg(w, h, fill, stroke, borderWidth, borderRadius, dash, opacity));
            continue;
          }
        }
      };

      // Fix 3 REMOVED: verifyPositions caused layout cascades by setting position:absolute
      // on flex children, removing them from flow and reordering siblings.
      // The cure was worse than the disease for slides using flex/grid layouts.

      // Fix 4: Normalize font-weight — 600 (semibold) renders as regular in PPTX
      const normalizeFontWeights = (wrapperEl) => {
        wrapperEl.querySelectorAll('*').forEach(el => {
          const fw = el.style.fontWeight;
          if (fw && parseInt(fw) >= 600 && parseInt(fw) < 700) {
            el.style.fontWeight = '700'; // Promote semibold to bold for PPTX
          }
        });
      };

      // Fix 5 REMOVED: compensateTextMetrics added height to text elements,
      // which broke flex/grid layouts. The added buffer cascaded through
      // parent containers and clipped sibling content (e.g. body text in cards).

      // Fix 6: Inline table cell styles from iframe computed styles
      // Our inlineAllStyles covers this recursively, but table elements have
      // presentation-specific properties (header bg fills, alternating row colors)
      // that are often set via CSS selectors like th { background: #1e40af }
      // which get lost in the clone. Explicitly inline key table styles.
      const inlineTableStyles = (iframeBody, wrapperEl, iframeWin) => {
        // Inline <table> container styles (border-collapse, layout, width)
        const origTables = iframeBody.querySelectorAll('table');
        const cloneTables = wrapperEl.querySelectorAll('table');
        for (let i = 0; i < Math.min(origTables.length, cloneTables.length); i++) {
          const tc = iframeWin.getComputedStyle(origTables[i]);
          const ct = cloneTables[i];
          ct.style.borderCollapse = tc.borderCollapse;
          ct.style.borderSpacing = tc.borderSpacing;
          ct.style.tableLayout = tc.tableLayout;
          ct.style.width = tc.width;
          const tbg = tc.backgroundColor;
          if (tbg && tbg !== 'rgba(0, 0, 0, 0)' && tbg !== 'transparent') ct.style.backgroundColor = tbg;
        }

        // Inline <tr> row styles (background-color for alternating rows, borders)
        const origRows = iframeBody.querySelectorAll('tr');
        const cloneRows = wrapperEl.querySelectorAll('tr');
        for (let i = 0; i < Math.min(origRows.length, cloneRows.length); i++) {
          const rc = iframeWin.getComputedStyle(origRows[i]);
          const cr = cloneRows[i];
          const rbg = rc.backgroundColor;
          if (rbg && rbg !== 'rgba(0, 0, 0, 0)' && rbg !== 'transparent') cr.style.backgroundColor = rbg;
          cr.style.borderBottom = rc.borderBottom;
        }

        // Inline <th>/<td> cell styles
        const origCells = iframeBody.querySelectorAll('th, td');
        const cloneCells = wrapperEl.querySelectorAll('th, td');
        const len = Math.min(origCells.length, cloneCells.length);
        for (let i = 0; i < len; i++) {
          const computed = iframeWin.getComputedStyle(origCells[i]);
          const clone = cloneCells[i];
          // Force-inline the properties that matter most for table appearance.
          // Use the full `background` shorthand so gradient fills are preserved,
          // then override `background-color` individually for solid fills.
          const bgShorthand = computed.background;
          if (bgShorthand) clone.style.background = bgShorthand;
          const bg = computed.backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
            clone.style.backgroundColor = bg;
          }
          clone.style.color = computed.color;
          clone.style.fontWeight = computed.fontWeight;
          clone.style.fontSize = computed.fontSize;
          clone.style.fontFamily = computed.fontFamily;
          clone.style.textAlign = computed.textAlign;
          clone.style.padding = computed.padding;
          clone.style.borderTop = computed.borderTop;
          clone.style.borderRight = computed.borderRight;
          clone.style.borderBottom = computed.borderBottom;
          clone.style.borderLeft = computed.borderLeft;
          clone.style.verticalAlign = computed.verticalAlign;
          clone.style.whiteSpace = computed.whiteSpace;
          clone.style.lineHeight = computed.lineHeight;
          clone.style.letterSpacing = computed.letterSpacing;
        }
      };

      // Helper: inline all computed styles onto every element so dom-to-pptx
      // can read them without needing CSS class rules
      const inlineAllStyles = (sourceEl, clonedEl, win) => {
        const computed = win.getComputedStyle(sourceEl);
        // Key properties that affect layout and appearance in PowerPoint
        const props = [
          'position', 'top', 'left', 'right', 'bottom', 'width', 'height',
          'min-width', 'min-height', 'max-width', 'max-height',
          'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
          'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
          'display', 'flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'align-self',
          'gap', 'row-gap', 'column-gap', 'flex', 'flex-grow', 'flex-shrink', 'flex-basis', 'order',
          'grid-template-columns', 'grid-template-rows', 'grid-column', 'grid-row',
          'background-color', 'background-image', 'background-size', 'background-position', 'background-repeat',
          'color', 'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant',
          'line-height', 'letter-spacing', 'text-align', 'text-transform', 'text-decoration',
          'white-space', 'word-break', 'overflow-wrap', 'text-overflow',
          'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
          'border-radius', 'border-top-left-radius', 'border-top-right-radius', 'border-bottom-left-radius', 'border-bottom-right-radius',
          'border-color', 'border-width', 'border-style',
          'box-shadow', 'opacity', 'overflow', 'overflow-x', 'overflow-y',
          'z-index', 'direction', 'unicode-bidi', 'text-indent',
          'vertical-align', 'box-sizing', 'transform', 'filter',
        ];
        for (const prop of props) {
          const val = computed.getPropertyValue(prop);
          if (val && val !== '' && val !== 'none' && val !== 'normal' && val !== 'auto' && val !== '0px' && val !== 'rgba(0, 0, 0, 0)') {
            clonedEl.style.setProperty(prop, val);
          }
        }
        // Also copy position-related values that might be 0px (they matter for absolute positioning)
        for (const prop of ['top', 'left', 'right', 'bottom']) {
          if (computed.getPropertyValue('position') === 'absolute' || computed.getPropertyValue('position') === 'fixed') {
            clonedEl.style.setProperty(prop, computed.getPropertyValue(prop));
          }
        }
        // Recurse into children
        const sourceChildren = sourceEl.children;
        const clonedChildren = clonedEl.children;
        for (let i = 0; i < sourceChildren.length && i < clonedChildren.length; i++) {
          inlineAllStyles(sourceChildren[i], clonedChildren[i], win);
        }
      };

      // Create a hidden staging area
      const stage = document.createElement('div');
      stage.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;';
      document.body.appendChild(stage);

      const slideElements = [];

      for (let i = 0; i < htmlSlides.length; i++) {
        const slide = htmlSlides[i];
        toast.loading(`Rendering slide ${i + 1} of ${htmlSlides.length}...`, { id: 'export-ppt' });

        // Create an iframe to render the full HTML document
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'width:1280px;height:720px;border:none;visibility:hidden;';
        stage.appendChild(iframe);

        // Write the slide HTML into the iframe (strip animations for clean export)
        // IIFE-wrap inline scripts to prevent `const` redeclaration across Chart.js blocks
        const cleanHtml = stripAnimationsFromHtml(slide.html_source).replace(
          /<script(?![^>]*\bsrc\b)([^>]*)>([\s\S]*?)<\/script>/gi,
          (m, attrs, body) => body.trim() ? `<script${attrs}>(function(){${body}})();</script>` : m
        );
        await new Promise((resolve) => {
          iframe.onload = resolve;
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          doc.open();
          doc.write(cleanHtml);
          doc.close();
          setTimeout(resolve, 2000);
        });

        // Wait for fonts/images to load
        try {
          await iframe.contentDocument.fonts.ready;
        } catch (e) { /* ignore */ }

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const iframeBody = iframeDoc.body;
        const iframeWin = iframe.contentWindow;

        // Extra wait for Chart.js canvas animations (default animation ~1s)
        let canvasEls = iframeBody.querySelectorAll('canvas');
        if (canvasEls.length > 0) {
          await new Promise(r => setTimeout(r, 3000));
          // Safety net: if CDN loaded slowly and inline scripts failed on first
          // pass, re-execute them now that the library is available.
          // Only re-execute if some canvases DON'T have a Chart instance yet.
          const ChartClass = iframeWin.Chart;
          const needsReinit = !ChartClass?.getChart ||
            !Array.from(canvasEls).every(c => ChartClass.getChart(c));
          if (needsReinit) {
            // Destroy partial instances before re-init
            if (ChartClass?.getChart) {
              canvasEls.forEach(c => { try { ChartClass.getChart(c)?.destroy(); } catch (_) {} });
            }
            const inlineScripts = iframeBody.querySelectorAll('script:not([src])');
            inlineScripts.forEach(old => {
              try {
                const fresh = iframeDoc.createElement('script');
                fresh.textContent = old.textContent;
                old.parentNode.replaceChild(fresh, old);
              } catch (_) {}
            });
            // Wait a bit more for re-executed scripts to render
            await new Promise(r => setTimeout(r, 1500));
          }
          // Re-query canvases (some scripts may have created new ones)
          canvasEls = iframeBody.querySelectorAll('canvas');
        } else {
          await new Promise(r => setTimeout(r, 500));
        }

        // Rasterize <canvas> elements (e.g. Chart.js charts) BEFORE cloning.
        // cloneNode(true) copies the DOM node but not the canvas pixel data, so
        // charts would export as blank rectangles. Replace each canvas with an
        // <img> snapshot so the clone preserves the rendered chart.
        for (const canvas of canvasEls) {
          try {
            const dataUrl = canvas.toDataURL('image/png');
            const img = iframeDoc.createElement('img');
            img.src = dataUrl;
            // Preserve the canvas dimensions
            const cs = iframeWin.getComputedStyle(canvas);
            img.style.width = cs.width;
            img.style.height = cs.height;
            img.style.display = cs.display || 'block';
            img.style.maxWidth = '100%';
            canvas.parentNode.replaceChild(img, canvas);
          } catch (canvasErr) {
            console.warn('Failed to rasterize canvas for PPTX export:', canvasErr);
          }
        }

        // Create wrapper at slide dimensions
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'width:1280px;height:720px;position:relative;overflow:hidden;box-sizing:border-box;';

        // Apply body computed styles to wrapper
        const bodyComputed = iframeWin.getComputedStyle(iframeBody);
        wrapper.style.background = bodyComputed.background || bodyComputed.backgroundColor || '';
        wrapper.style.fontFamily = bodyComputed.fontFamily || '';
        wrapper.style.color = bodyComputed.color || '';
        wrapper.style.direction = bodyComputed.direction || '';

        // Also check html element for background
        const htmlEl = iframeDoc.documentElement;
        if (htmlEl) {
          const htmlComputed = iframeWin.getComputedStyle(htmlEl);
          if (htmlComputed.backgroundColor && htmlComputed.backgroundColor !== 'rgba(0, 0, 0, 0)' && !wrapper.style.backgroundColor) {
            wrapper.style.backgroundColor = htmlComputed.backgroundColor;
          }
        }

        // Clone body and inline ALL computed styles from the live iframe DOM
        const clonedBody = iframeBody.cloneNode(true);
        inlineAllStyles(iframeBody, clonedBody, iframeWin);

        // Move children from cloned body into wrapper
        while (clonedBody.firstChild) {
          wrapper.appendChild(clonedBody.firstChild);
        }

        // Neutralize CSS effects that have no PPTX equivalent
        wrapper.querySelectorAll('*').forEach(el => {
          const s = el.style;

          // filter — PPTX has no CSS filter equivalent.
          // blur overlays become solid blobs; drop-shadow/brightness/contrast
          // cause colors to shift dramatically when dom-to-pptx rasterises.
          if (s.filter && s.filter !== 'none') {
            const filterValue = s.filter;
            const blurMatch = filterValue.match(/blur\(\s*(\d+(?:\.\d+)?)px\s*\)/i);
            s.filter = 'none';

            if (blurMatch) {
              const blurRadius = parseFloat(blurMatch[1]);
              // textContent includes descendants — if anything textual lives inside
              // the blurred element, we can't safely hide it.
              const hasText = el.textContent?.trim().length > 0;

              // Heavy blur (>= 20px) on a non-text element is almost always a
              // decorative atmospheric glow — e.g. a solid circle blurred to 80px
              // that's supposed to look like a soft gradient halo. Without the
              // blur it becomes a hard, opaque shape that ruins the design (those
              // visible teal/blue circles in exported decks). Removing it entirely
              // is closer to the original look than showing it as a hard disc.
              if (blurRadius >= 20 && !hasText) {
                s.display = 'none';
              } else if (!s.opacity || parseFloat(s.opacity) > 0.5) {
                // Small blur or text-containing element — keep it but fade so
                // the lost blur doesn't overwhelm the layout.
                s.opacity = '0.25';
              }
            }
          }

          // backdrop-filter has no PPTX equivalent
          if (s.backdropFilter) s.backdropFilter = 'none';

          // mix-blend-mode causes color artifacts
          if (s.mixBlendMode && s.mixBlendMode !== 'normal') s.mixBlendMode = 'normal';

          // Heavy box-shadows with large spread render as dark rectangles in PPTX.
          // Keep subtle shadows but strip heavy ones.
          if (s.boxShadow && s.boxShadow !== 'none') {
            // If shadow spread+blur > 30px total, it'll create noticeable dark blobs
            const shadowParts = s.boxShadow.match(/(\d+)px/g);
            if (shadowParts) {
              const totalPx = shadowParts.reduce((sum, p) => sum + parseInt(p), 0);
              if (totalPx > 40) s.boxShadow = 'none';
            }
          }
          // Opacity: dom-to-pptx often ignores or misapplies CSS opacity on elements,
          // causing faint decorative elements (gridlines, background patterns at opacity:0.05)
          // to render at full darkness. Bake low opacity into background-color alpha instead.
          const opVal = parseFloat(s.opacity);
          if (!isNaN(opVal) && opVal < 0.3 && opVal > 0) {
            const hasText = el.textContent?.trim().length > 0;
            if (!hasText) {
              // Purely decorative element — hide it entirely if barely visible
              if (opVal < 0.1) {
                s.display = 'none';
              } else {
                // Bake opacity into background-color alpha so dom-to-pptx renders it correctly
                const bg = s.backgroundColor;
                if (bg && bg.startsWith('rgb')) {
                  const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  if (match) {
                    const [, r, g, b] = match;
                    s.backgroundColor = `rgba(${r}, ${g}, ${b}, ${opVal})`;
                    s.opacity = '1';
                  }
                }
              }
            }
          }

          // Note: clip-path is handled by convertClipPaths below (converted to native shapes/SVGs)
        });

        // Fix 1: Inline SVG computed styles from iframe (class-based + var() resolution)
        inlineSvgStylesFromIframe(wrapper, iframeBody, iframeWin);

        // Fix 4: Normalize semibold (600) → bold (700) for PPTX
        normalizeFontWeights(wrapper);

        // Fix 6: Explicitly inline table cell styles from iframe
        inlineTableStyles(iframeBody, wrapper, iframeWin);

        // Append to stage so getBoundingClientRect works for shape measurement
        stage.appendChild(wrapper);

        // r161: liberate empty inline decorations (accent dashes, underline
        // blocks, icons) OUT of text blocks — one such child made dom-to-pptx
        // reject the whole block as a text container and explode it into one
        // text box per node ("pre-italic / italic / post-italic"). Must run
        // on-stage (measures rects) and BEFORE the shape pass so hoisted
        // dashes get drawn natively.
        hoistDecorativeInlines(wrapper, window);

        // Fix 7: Convert CSS-drawn shapes (border-radius circles, thin-div lines,
        // empty bordered rects) to inline SVGs so dom-to-pptx exports them as
        // editable vectors instead of fumbling them as plain rectangles.
        convertCssShapesToSvg(wrapper);

        // Fix 2: Convert clip-path elements to native shapes/SVGs (must be in DOM)
        await convertClipPaths(wrapper);

        slideElements.push(wrapper);

        iframe.remove();
      }

      // Wait for fonts to finish loading in the main document
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 500));

      toast.loading('Building PowerPoint file...', { id: 'export-ppt' });

      // Export all slide elements to PPTX. skipDownload: we inject the brand
      // theme XML into the blob first, then trigger the download ourselves.
      // autoEmbedFonts disabled: Google Fonts serves woff2 which dom-to-pptx parses as TTF → corrupts file
      const rawBlob = await exportToPptx(slideElements, {
        fileName: `${presentationTitle.replace(/[^a-zA-Z0-9\s]/g, '_')}.pptx`,
        autoEmbedFonts: false,
        svgAsVector: true,
        skipDownload: true,
      });

      // Inject brand theme (color scheme + font scheme) so the exported file
      // behaves like a template: PowerPoint shows the brand palette under
      // Design → Variants → Colors and the brand fonts under Fonts.
      toast.loading('Applying brand theme...', { id: 'export-ppt' });
      const themedBlob = await applyThemeToExportedPptx({
        pptxBlob: rawBlob,
        brandIdentityId: brandIdentityId || brandIdentityIdRef.current || null,
        slideRoots: slideElements,
        presentationTitle,
      });

      // r147: dom-to-pptx has no RTL support — Arabic paragraphs export
      // LTR-flagged (scrambled word order in PowerPoint). For Arabic decks,
      // a deterministic server pass stamps rtl on Arabic paragraphs.
      // Fail-open: any hiccup ships the un-fixed export.
      let finalBlob = themedBlob;
      const _isArabicDeck = (() => {
        let ar = 0, lat = 0;
        for (const s of htmlSlides) {
          const t = (s.html_source || '')
            .replace(/<style[\s\S]*?<\/style>/gi, ' ')
            .replace(/<script[\s\S]*?<\/script>/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .slice(0, 20000);
          for (const ch of t) {
            if (ch >= '\u0600' && ch <= '\u06FF') ar++;
            else if (/[a-zA-Z]/.test(ch)) lat++;
          }
        }
        return ar >= 40 && ar > lat;
      })();
      if (_isArabicDeck) {
        try {
          toast.loading('Fixing right-to-left text direction...', { id: 'export-ppt' });
          const fd = new FormData();
          fd.append('file', themedBlob, 'deck.pptx');
          const rtlResp = await fetch(withBackend('/api/pptx/rtl-fix'), { method: 'POST', body: fd, signal: AbortSignal.timeout(60000) });
          if (rtlResp.ok) finalBlob = await rtlResp.blob();
        } catch { /* fail-open */ }
      }

      // Trigger download of the themed blob.
      const fileName = `${presentationTitle.replace(/[^a-zA-Z0-9\s]/g, '_')}.pptx`;
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Cleanup
      stage.remove();
      toast.success('Editable PPTX exported!', { id: 'export-ppt' });

    } catch (error) {
      console.error('dom-to-pptx export error:', error);
      toast.error(error.message || 'Failed to export editable PPTX', { id: 'export-ppt' });
    } finally {
      setExporting(false);
    }
  };

  // Save project to Supabase
  // Presence: tell peers which slide we're on while reviewing/editing.
  useEffect(() => {
    setEditingSlide(currentStep === 5 ? currentSlideIndex : null);
  }, [currentStep, currentSlideIndex, setEditingSlide]);

  const handleSaveProject = async () => {
    if (!userId) {
      toast.error('Please sign in to save projects');
      return;
    }

    if (generatedSlides.length === 0) {
      toast.error('No slides to save');
      return;
    }

    setSaving(true);
    toast.loading('Saving project...', { id: 'save-project' });

    try {
      const pptProjectIdFinal = pptProjectId || `ppt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const metadata = {
        slide_count: generatedSlides.length,
        input_mode: inputMode,
        generation_mode: generationMode,
        theme_id: selectedTheme?.id || null,
        custom_color_scheme: customColorScheme || null,
        created_at: pptProjectId ? undefined : new Date().toISOString(),
        chatHistory: chatMessages || [],
        ppt_project_id: pptProjectIdFinal,
        // Persist brand identity so reloaded projects keep the font +
        // color context for native PPTX export
        brand_identity_id: brandIdentityId || brandIdentityIdRef.current || null,
        // r131b: proposal mode survives reopen — post-reopen regens and chat
        // edits must keep the facts-only contract the badge promises
        proposal_mode: questionnaireAnswers?.proposalMode || false,
        proposal_scope: questionnaireAnswers?.proposalScope || null, // r180
      };

      if (projectId) {
        // ── Versioned collaborative save (owner or shared editor) ──
        // Goes through the backend so shared editors can write (client-side
        // RLS is owner-only) and so the rev check turns silent last-write-
        // wins clobbers into a visible conflict.
        // Job-created decks land here on their FIRST save too (the job set
        // projectId at generation start) — capture that before setPptProjectId
        // so onComplete still fires and App.js swaps the 'generating' skeleton
        // card for the finished project.
        const isFirstSave = !pptProjectId
          && (!existingProject?.projectId
              // r152: feed decks carry the placeholder's projectId from
              // mount, so their finishing save must still count as FIRST —
              // onComplete is what swaps App.js's 'generating' card for the
              // real one (review #8).
              || existingProject?.externalFeed === true);
        const processedSlides = await supabaseHelpers.processSlidesForSave(userId, pptProjectIdFinal, generatedSlides);
        if (revRef.current == null) {
          const { data: row } = await supabaseHelpers.getProjectFull(projectId);
          revRef.current = row?.rev ?? 0;
        }
        // r160b (review P1-2): a previews-less save (fresh restore) must
        // never NULL the Recents card — omit the field instead.
        const _thumb = normalizeImageUrl(processedSlides[0]?.preview_url || null);
        const buildSaveBody = () => JSON.stringify({
          user_id: userId,
          rev: revRef.current,
          analysis_data: { type: 'create-ppt', slides: processedSlides, metadata },
          slide_count: processedSlides.length,
          ...(_thumb ? { thumbnail_url: _thumb } : {}),
          ppt_project: {
            project_id: pptProjectIdFinal,
            title: presentationTitle,
            slides_data: processedSlides,
            brand_guidelines: useBrandGuidelines ? brandGuidelines : null,
            slide_count: processedSlides.length,
            metadata,
          },
        });
        let resp = await fetch(withBackend(`/api/projects/${projectId}/save`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: buildSaveBody(),
        });
        let result = await resp.json().catch(() => ({}));
        if (resp.status === 409 && (result.updated_by_id || null) === userId
            && result.error !== 'generation_in_progress') {
          // Self-conflict: the rev was bumped by this same user — in practice
          // the background generation job's completion write racing the first
          // autosave. Reconcile BEFORE retrying: in a GENERATION context, if
          // the remote row holds MORE slides than this save would write, the
          // bump was the job's own result — adopt it instead of re-clobbering
          // it with a stale snapshot (round 61: the blind retry overwrote 5
          // slides with 1). Outside generation (two-tab editing) the adopt
          // must NOT discard local edits — fall through to the normal flow.
          const { data: row } = await supabaseHelpers.getProjectFull(projectId);
          const remoteSlides = (row?.analysis_data?.slides || []).filter(Boolean);
          const generationContext = liveGenerating || !!currentJobIdRef.current;
          if (generationContext && remoteSlides.length > processedSlides.length) {
            if (row?.rev != null) revRef.current = row.rev;
            isSaveUpdateRef.current = true;
            setGeneratedSlides(prev => remoteSlides.map((dbSlide, idx) => ({
              ...dbSlide,
              extractedLayers: prev[idx]?.extractedLayers || dbSlide.extractedLayers,
            })));
            toast.success('Loaded the completed deck from the server', { id: 'save-project' });
            return;
          }
          if (row?.rev != null) revRef.current = row.rev;
          resp = await fetch(withBackend(`/api/projects/${projectId}/save`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: buildSaveBody(),
          });
          result = await resp.json().catch(() => ({}));
        }
        if (resp.status === 409) {
          if (result.error === 'generation_in_progress') {
            // The background job is still writing this project — saving now
            // would clobber it. Tell the user and queue one retry for when
            // generation finishes (the baseline-save effect fires it).
            console.warn('[Save] deferred — generation in progress on the server');
            // r152: on a FEED deck this 409 means our own finish op failed
            // (there is no server job) — re-send it so the lock clears,
            // instead of 409-looping until the lock times out (review #7).
            if (existingProject?.externalFeed && projectId) {
              try {
                await fetch(withBackend(`/api/projects/direct-feed/${projectId}`), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
                  body: JSON.stringify({ op: 'finish', user_id: userId || '' }),
                });
              } catch (_) { /* fall through to the capped retry */ }
            }
            // r152: cap + space the retries — the effect used to refire the
            // instant `saving` flipped false, a hot loop with a toast per
            // round-trip.
            baselineRetryRef.current += 1;
            if (baselineRetryRef.current <= 3) {
              toast.info('Deck is still generating — your changes will save when it finishes', { id: 'save-project' });
              setTimeout(() => setBaselineSaveDue(true), 5000);
            } else {
              toast.error('Could not save yet — the deck still reports generating. Try Save again in a minute.', { id: 'save-project' });
            }
            return;
          }
          setConflictInfo({ by: result.updated_by || null });
          toast.error('Someone saved a newer version of this deck', { id: 'save-project' });
          return;
        }
        if (!resp.ok) throw new Error(result.error || `HTTP ${resp.status}`);
        if (result.rev != null) revRef.current = result.rev;
        baselineRetryRef.current = 0; // r152: a save landed — reset the cap
        if (!pptProjectId) setPptProjectId(pptProjectIdFinal);

        // Merge processed slides (storage URLs) but keep local extractedLayers
        isSaveUpdateRef.current = true;
        setGeneratedSlides(prev => prev.map((localSlide, idx) => {
          const dbSlide = processedSlides[idx];
          if (!dbSlide) return localSlide;
          return {
            ...dbSlide,
            extractedLayers: localSlide?.extractedLayers || dbSlide.extractedLayers,
          };
        }));
        setLastSaved(new Date());
        setRemoteUpdate(null);
        dirtySlidesRef.current.clear(); // whole-deck save persisted everything
        broadcastDeckUpdated(result.rev);
        toast.success(isFirstSave ? 'Project saved!' : 'Project updated!', { id: 'save-project' });
        if (isFirstSave && onComplete) {
          onComplete({
            preCreatedProjectId: projectId || undefined,
            createPPTData: {
              projectId: pptProjectIdFinal,
              title: presentationTitle,
              slides: processedSlides,
              metadata,
            }
          });
        } else if (onUpdate) {
          onUpdate(projectId, { title: presentationTitle, slides: processedSlides, metadata });
        }
      } else {
        // ── First save: no projects-table row yet, so no concurrent editors
        // are possible — the legacy owner path creates both rows. ──
        const projectData = {
          id: pptProjectId,
          title: presentationTitle,
          slides: generatedSlides,
          brandGuidelines: useBrandGuidelines ? brandGuidelines : null,
          metadata,
        };

        const { data, error, isNew } = await supabaseHelpers.savePPTProject(userId, projectData);
        if (error) throw error;

        if (data?.project_id) setPptProjectId(data.project_id);

        const dbSlides = data?.slides_data;
        if (dbSlides) {
          isSaveUpdateRef.current = true;
          setGeneratedSlides(prev => prev.map((localSlide, idx) => {
            const dbSlide = dbSlides[idx];
            if (!dbSlide) return localSlide;
            return {
              ...dbSlide,
              extractedLayers: localSlide?.extractedLayers || dbSlide.extractedLayers,
            };
          }));
        }
        setLastSaved(new Date());

        const isFirstSave = isNew && !existingProject?.projectId;
        toast.success(isFirstSave ? 'Project saved!' : 'Project updated!', { id: 'save-project' });

        if (isFirstSave && onComplete) {
          onComplete({
            preCreatedProjectId: projectId || undefined,
            createPPTData: {
              projectId: data?.project_id,
              title: presentationTitle,
              slides: dbSlides || generatedSlides,
              metadata,
            }
          });
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save project', { id: 'save-project' });
    } finally {
      setSaving(false);
    }
  };

  // Navigation
  const canProceedToStep2 = presentationTitle.trim() && (inputMode === 'raw' ? (rawContent.trim().length > 0 || excelData) : slides.some(s => s.title.trim() || s.content.trim()));
  // canProceedToStep3 removed — Step 3 no longer exists

  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: currentStep === 1.5 ? 'Preferences' : 'Content' },
      { num: 2, label: 'Outline' },
      { num: 3, label: 'Generate' },
      { num: 4, label: 'Review' },
    ];

    // Map the actual currentStep to the visual step for comparison
    // Internal steps: 1, 1.5, 2, 4, 5 → Display steps: 1, 1, 2, 3, 4
    const getEffectiveStep = () => {
      if (currentStep === 1.5) return 1;
      if (currentStep === 4) return 3;
      if (currentStep === 5) return 4;
      return currentStep;
    };

    const effectiveStep = getEffectiveStep();

    return (
      <div className="flex items-center justify-center space-x-3 mb-8">
        {steps.map((step, idx) => {
          const isActive = effectiveStep >= step.num;
          const isCurrent = effectiveStep === step.num || (currentStep === 1.5 && step.num === 1);
          const isPast = effectiveStep > step.num;

          return (
            <React.Fragment key={step.num}>
              <div className="flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                  isCurrent
                    ? currentStep === 1.5
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                    : isActive
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                }`}>
                  {step.num}
                </div>
                <span className={`ml-1.5 font-medium text-sm ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-8 h-1 rounded ${isPast ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Step 1: Content Input
  const renderContentStep = () => (
    <div className="space-y-6">
      {/* Presentation Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-500" />
            Presentation Details
          </CardTitle>
          <CardDescription>Enter your presentation title and slide count</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Presentation Title *</label>
            <Input
              placeholder="e.g., Q4 Marketing Strategy"
              value={presentationTitle}
              onChange={(e) => setPresentationTitle(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Target Slide Count</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min={1}
                max={50}
                value={slideCount}
                onChange={(e) => setSlideCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">slides</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Slide Content Card - Two tabs only */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="w-5 h-5 text-green-500" />
            Slide Content
          </CardTitle>
          <CardDescription>Choose how you'd like to create your slides</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Input Mode Toggle - Two tabs only */}
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <button
              onClick={() => setInputMode('prompt')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                inputMode === 'prompt'
                  ? 'bg-white dark:bg-slate-700 text-purple-600 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Topic/Prompt
            </button>
            <button
              onClick={() => setInputMode('raw')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                inputMode === 'raw'
                  ? 'bg-white dark:bg-slate-700 text-green-600 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Type className="w-4 h-4 inline mr-2" />
              Paste Content
            </button>
          </div>

          {/* Prompt Mode - AI Research */}
          {inputMode === 'prompt' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  What would you like to create a presentation about?
                </label>
                <Textarea
                  placeholder="e.g., I want to create a presentation about the history of Jordan, covering ancient civilizations to modern times..."
                  value={topicPrompt}
                  onChange={(e) => setTopicPrompt(e.target.value)}
                  rows={4}
                  className="text-base"
                />
              </div>

              {/* AI Instructions */}
              <div>
                <label className="text-sm font-medium mb-2 block text-muted-foreground">
                  Additional instructions (optional)
                </label>
                <Textarea
                  placeholder="Tell AI how to use the content, what to focus on, etc."
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              </div>

              {/* Content Detail Level */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Amount of Text</label>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { value: 'condense', label: 'Minimal' },
                      { value: 'preserve', label: 'Essential' },
                      { value: 'enhance', label: 'Detailed' },
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setTextStyle(option.value)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                          textStyle === option.value
                            ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Tone</label>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { value: 'professional', label: 'Professional' },
                      { value: 'casual', label: 'Casual' },
                      { value: 'academic', label: 'Academic' },
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setContentTone(option.value)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                          contentTone === option.value
                            ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Language and AI Model Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Language</label>
                  <select
                    value={contentLanguage}
                    onChange={(e) => setContentLanguage(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                  >
                    <option value="en">English (US)</option>
                    <option value="ar">Arabic</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                  </select>
                </div>

                <div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                AI will research your topic and create a detailed outline for {slideCount} slides. You'll be able to review and edit before generating.
              </p>
            </div>
          )}

          {/* Paste Content Mode */}
          {inputMode === 'raw' && (
            <div className="space-y-4">
              {/* Excel data preview card */}
              {excelData && (
                <div className="border rounded-lg p-4 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-700 dark:text-emerald-300">
                        {excelData.filename}
                      </span>
                    </div>
                    <button
                      onClick={() => { setExcelData(null); setRawContent(''); }}
                      className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                  {excelData.sheets.map((sheet, i) => (
                    <div key={i} className="mb-2 text-sm">
                      <span className="font-medium text-slate-700 dark:text-slate-300">{sheet.sheet_name}</span>
                      <span className="text-muted-foreground ml-2">
                        {sheet.headers.length} columns · {sheet.total_rows} rows
                      </span>
                      <div className="mt-1 text-xs text-muted-foreground truncate">
                        Columns: {sheet.headers.join(', ')}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-3">
                    AI will analyze this data and create charts automatically
                  </p>
                  <div className="mt-4">
                    <label className="text-sm font-medium mb-2 block text-slate-700 dark:text-slate-300">
                      What's this presentation about? <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Textarea
                      placeholder='e.g. "KPI update meeting with executives" or "Quarterly sales review for the board"'
                      value={rawContent}
                      onChange={(e) => setRawContent(e.target.value)}
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Content textarea */}
              {!excelData && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Paste your content below
                </label>
                <Textarea
                  placeholder="Paste all your presentation content here... Include headings, bullet points, and paragraphs."
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>
              )}

              {/* Import from file/URL - inline */}
              <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-muted-foreground">Or import content from:</label>
                </div>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setImportMode(importMode === 'file' ? null : 'file')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all border ${
                      importMode === 'file'
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 text-muted-foreground'
                    }`}
                  >
                    <File className="w-4 h-4 inline mr-2" />
                    Upload File
                  </button>
                  <button
                    onClick={() => setImportMode(importMode === 'url' ? null : 'url')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all border ${
                      importMode === 'url'
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 text-muted-foreground'
                    }`}
                  >
                    <Globe className="w-4 h-4 inline mr-2" />
                    Paste URL
                  </button>
                </div>

                {/* File upload area */}
                {importMode === 'file' && (
                  <div
                    onClick={() => !importing && contentFileInputRef.current?.click()}
                    {...importDrop.dropProps}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                      importDrop.dragOver
                        ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-900/40'
                        : importing
                        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-slate-300 dark:border-slate-600 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10'
                    }`}
                  >
                    <input
                      ref={contentFileInputRef}
                      type="file"
                      accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleContentImport(file, null);
                      }}
                      className="hidden"
                    />
                    {importing ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Extracting content...</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <FileText className="w-6 h-6 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-600 dark:text-slate-300">Drop a file here</p>
                        <p className="text-xs text-slate-400">supports .pdf, .docx, .doc, .xlsx</p>
                      </div>
                    )}
                  </div>
                )}

                {/* URL input */}
                {importMode === 'url' && (
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="https://example.com/article"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      disabled={importing}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => handleContentImport(null, importUrl)}
                      disabled={importing || !importUrl.trim()}
                      size="sm"
                      className="bg-indigo-500 hover:bg-indigo-600"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Extract'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Text Processing toggle */}
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-3 block">Text Processing</label>
                <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <button
                    onClick={() => setEnhanceText(true)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      enhanceText
                        ? 'bg-white dark:bg-slate-700 text-green-600 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Wand2 className="w-4 h-4 inline mr-2" />
                    Enhance Text
                  </button>
                  <button
                    onClick={() => setEnhanceText(false)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      !enhanceText
                        ? 'bg-white dark:bg-slate-700 text-green-600 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Type className="w-4 h-4 inline mr-2" />
                    Keep As-Is
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {enhanceText
                    ? 'AI will structure and enhance your text into professional slides. You\'ll review the outline before generating.'
                    : 'AI will organize your text into slides without changing the content.'}
                </p>
              </div>

            </div>
          )}
        </CardContent>
      </Card>

      {/* Action button */}
      <div className="flex justify-end">
        {inputMode === 'prompt' ? (
          <Button
            onClick={() => setCurrentStep(1.5)}
            disabled={!topicPrompt.trim()}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : enhanceText ? (
          <Button
            onClick={() => setCurrentStep(1.5)}
            disabled={!rawContent.trim() || !presentationTitle.trim()}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={() => setCurrentStep(1.5)}
            disabled={!canProceedToStep2}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );

  // Handle questionnaire completion — triggers outline generation or skips to theme
  const handleQuestionnaireComplete = (answers) => {
    // Clarifier returns snake_case ids; mirror imagery answers into camelCase for downstream FormData.
    const normalized = {
      ...answers,
      imageryMode: answers.imageryMode || answers.imagery_mode || null,
      artDirection: answers.artDirection || answers.art_direction || null,
    };
    setQuestionnaireAnswers(normalized);

    // Set generation mode from questionnaire
    if (answers.generationMode) {
      setGenerationMode(answers.generationMode);
    }

    // Set theme if Instant mode
    if (answers.selectedTheme) {
      setSelectedTheme(answers.selectedTheme);
    }

    // Set AI style preset from theme question
    if (answers.aiStylePreset) {
      if (answers.aiStylePreset === 'custom') {
        // Custom theme — use ai-create as base, custom text drives the styling
        setAiStylePreset('ai-create');
      } else {
        setAiStylePreset(answers.aiStylePreset);
      }
    }

    // Set custom theme request text
    if (answers.customThemeRequest) {
      setCustomThemeRequest(answers.customThemeRequest);
    }

    // Set HTML edit model from questionnaire
    if (answers.htmlModel) {
      setHtmlEditModel(answers.htmlModel);
    }

    // Set brand references
    if (answers.brandReferences?.images?.length > 0) {
      setBrandReferences(answers.brandReferences);
    }

    // Set logo URL if uploaded in questionnaire
    if (answers.logoUrl) {
      setLogoUrl(answers.logoUrl);
    }

    if (inputMode === 'prompt') {
      // Topic mode → generate outline with answers
      handleGenerateOutline(answers);
    } else if (enhanceText) {
      // Paste + enhance → structure content with answers
      handleStructureContent(answers);
    } else {
      // Paste + no enhance → AI organizes content into slides without rewriting
      handleEnhanceAndReview({ keepAsIs: true });
    }
  };

  const handleQuestionnaireSkipAll = () => {
    // Default to Intelligent (HTML) mode when skipping
    setGenerationMode('html');

    if (inputMode === 'prompt') {
      handleGenerateOutline();
    } else if (enhanceText) {
      handleStructureContent();
    } else {
      // Paste + no enhance → AI organizes content into slides without rewriting
      handleEnhanceAndReview({ keepAsIs: true });
    }
  };

  // Step 1.5: Questionnaire
  const renderQuestionnaireStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Tell us about your presentation
              </CardTitle>
              <CardDescription>
                Help us tailor the content and design to your needs
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => setCurrentStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <PresentationQuestionnaire
            onComplete={handleQuestionnaireComplete}
            onSkipAll={handleQuestionnaireSkipAll}
            inputMode={inputMode}
            userId={userId}
            brandGuidelines={brandGuidelines}
            initialAnswers={questionnaireAnswers}
          />
        </CardContent>
      </Card>
    </div>
  );

  // Step 2: Review & Edit Outline (only for prompt mode)
  const renderOutlineReviewStep = () => (
    <div className="flex gap-6">
      {/* Left: Outline content */}
      <div className="flex-1 min-w-0 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Review & Edit Outline
                {outlineLoading && (
                  <Badge variant="outline" className="ml-2 text-purple-600 border-purple-300 animate-pulse">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    {generatedOutline?.slides?.length || 0} / {slideCount} slides
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {outlineLoading
                  ? 'Slides are appearing as they are generated...'
                  : 'Review and customize your presentation content before generating'}
              </CardDescription>
              {!outlineLoading && (
                <button
                  onClick={() => setCurrentStep(1.5)}
                  className="text-xs text-purple-500 hover:text-purple-700 underline mt-1 inline-flex items-center gap-1"
                >
                  Mode: {generationMode === 'ai' ? 'Imaginative' : generationMode === 'html' ? `Intelligent (${questionnaireAnswers?.htmlModel === 'pro' ? 'Premium' : 'Fast'})` : 'Instant'}
                  <span className="text-muted-foreground">(change)</span>
                </button>
              )}
            </div>
            <Button variant="outline" onClick={() => { setGeneratedOutline(null); setCurrentStep(1); }} disabled={outlineLoading}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Content
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Outline Slides */}
      <div className="space-y-4">
        {generatedOutline?.slides?.map((slide, index) => (
          <Card key={index} className="overflow-hidden">
            <div className="flex">
              {/* Slide Number */}
              <div className="w-16 bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-r">
                <span className="text-2xl font-bold text-muted-foreground">{index + 1}</span>
              </div>

              {/* Slide Content */}
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <Input
                    value={slide.title}
                    onChange={(e) => updateOutlineSlide(index, 'title', e.target.value)}
                    className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
                    placeholder="Slide title..."
                  />
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={slide.slide_type || 'content'}
                      onChange={(e) => updateOutlineSlide(index, 'slide_type', e.target.value)}
                      className="text-xs px-2 py-1 border rounded bg-background"
                    >
                      <optgroup label="Basic">
                        <option value="title">Title</option>
                        <option value="section">Section Divider</option>
                        <option value="closing">Closing</option>
                      </optgroup>
                      <optgroup label="Content">
                        <option value="content">Content</option>
                        <option value="icon-bullets">Icon Bullets</option>
                        <option value="icon-grid">Icon Grid</option>
                        <option value="cards">Cards</option>
                        <option value="feature-showcase">Feature Showcase</option>
                        <option value="three-column">Three Column</option>
                        <option value="numbered-list">Numbered List</option>
                        <option value="text-with-image">Text + Image</option>
                        <option value="big-statement">Big Statement</option>
                      </optgroup>
                      <optgroup label="Data & Stats">
                        <option value="stat-highlight">Stat Highlight</option>
                        <option value="timeline">Timeline</option>
                        <option value="process">Process</option>
                      </optgroup>
                      <optgroup label="Diagrams">
                        <option value="funnel">Funnel</option>
                        <option value="pyramid">Pyramid</option>
                        <option value="cycle">Cycle</option>
                        <option value="staircase">Staircase</option>
                        <option value="venn">Venn</option>
                        <option value="versus">Versus</option>
                        <option value="comparison">Comparison</option>
                      </optgroup>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOutlineSlide(index)}
                      disabled={generatedOutline.slides.length <= 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Content/Subtitle */}
                {slide.content && (
                  <Textarea
                    value={slide.content}
                    onChange={(e) => updateOutlineSlide(index, 'content', e.target.value)}
                    className="text-sm text-muted-foreground mb-3 min-h-[40px]"
                    placeholder="Subtitle or supporting text..."
                    rows={2}
                  />
                )}

                {/* Bullet Points */}
                {slide.bullet_points && slide.bullet_points.length > 0 && (
                  <div className="space-y-2">
                    {slide.bullet_points.map((point, pointIdx) => (
                      <div key={pointIdx} className="flex items-start gap-2">
                        <span className="text-muted-foreground mt-2">•</span>
                        <Input
                          value={point}
                          onChange={(e) => updateOutlineBulletPoint(index, pointIdx, e.target.value)}
                          className="flex-1 text-sm"
                          placeholder="Bullet point..."
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOutlineBulletPoint(index, pointIdx)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => addOutlineBulletPoint(index)}
                      className="text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add bullet point
                    </Button>
                  </div>
                )}

                {/* Instructions */}
                {slide.instructions && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Instructions:</span> {slide.instructions}
                    </p>
                  </div>
                )}

                {/* Image toggle (only when user opted for images) */}
                {questionnaireAnswers.includeImages === 'yes' && (
                  <div className="mt-3 pt-3 border-t">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={slide.needs_image || false}
                        onChange={(e) => updateOutlineSlide(index, 'needs_image', e.target.checked)}
                        className="rounded border-green-300 text-green-500 focus:ring-green-500"
                      />
                      <Image className="w-3.5 h-3.5 text-green-600" />
                      <span className="font-medium">Include image</span>
                    </label>
                    {slide.needs_image && (
                      <div className="mt-2 ml-6">
                        <Input
                          value={slide.suggested_image || ''}
                          onChange={(e) => updateOutlineSlide(index, 'suggested_image', e.target.value)}
                          className="text-xs"
                          placeholder="Describe the image you'd like (e.g., Modern office with team collaboration)"
                        />
                        {slide.suggested_image && (
                          <p className="text-[10px] text-muted-foreground mt-1 italic">
                            AI will generate: {slide.suggested_image}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}

        {/* Skeleton placeholders for remaining slides during streaming */}
        {outlineLoading && Array.from(
          { length: Math.max(0, slideCount - (generatedOutline?.slides?.length || 0)) },
          (_, i) => {
            const idx = (generatedOutline?.slides?.length || 0) + i;
            return (
              <Card key={`skeleton-${idx}`} className="overflow-hidden opacity-50">
                <div className="flex">
                  <div className="w-16 bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-r">
                    <span className="text-2xl font-bold text-muted-foreground">{idx + 1}</span>
                  </div>
                  <div className="flex-1 p-4 space-y-3">
                    <div className="h-5 w-2/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
                    <div className="space-y-2">
                      <div className="h-3 w-full rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
                      <div className="h-3 w-4/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
                      <div className="h-3 w-3/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
                    </div>
                  </div>
                </div>
              </Card>
            );
          }
        )}
      </div>

      {/* Add Slide Button */}
      {!outlineLoading && (
        <Button
          variant="outline"
          onClick={addOutlineSlide}
          className="w-full border-dashed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Slide
        </Button>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => {
          setGeneratedOutline(null);
          setOutlineFromEnhance(false);
          setCurrentStep(1);
        }} disabled={outlineLoading}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={() => handleApplyOutlineAndProceed()}
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          disabled={outlineLoading}
        >
          {outlineLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Presentation
            </>
          )}
        </Button>
      </div>
    </div>

      {/* Right: AI Edit Sidebar */}
      <div className="w-80 shrink-0 hidden lg:block">
        <OutlinePromptSidebar
          outline={generatedOutline}
          onOutlineUpdate={(newOutline) => setGeneratedOutline(newOutline)}
          questionnaireAnswers={questionnaireAnswers}
        />
      </div>
    </div>
  );

  // Step 3 removed — mode/theme selection now in questionnaire

  // Stop an in-flight background generation. The backend (/api/jobs/<id>/cancel)
  // flips a flag the per-slide loop checks between slides, so slides already
  // rendered are preserved and the stream emits a 'cancelled' event.
  const handleCancelGeneration = async () => {
    cancelRequestedRef.current = true;
    setCancelRequested(true);
    const jid = currentJobIdRef.current;
    if (!jid) {
      // r140: an external feed (direct pipeline) is owned by the PARENT —
      // signal it; its early finish keeps the slides already generated.
      if (externalFeedRef.current && onExternalCancel) {
        onExternalCancel();
        toast('Stopping — finished slides are kept.', { id: 'html-gen' });
        return;
      }
      if (generating && streamAbortRef.current) {
        // r137: no background job YET (still preparing / job POST in
        // flight) or at all (direct-stream fallback) — but THIS tab owns
        // the run. The generate flow checks the flag at its seams; aborting
        // kills the stream fetch and the pending early-slide fetch.
        // Finished slides are kept by the AbortError path.
        try { earlyFirstSlideRef.current?.controller?.abort(); } catch { /* noop */ }
        try { streamAbortRef.current.abort(); } catch { /* noop */ }
        toast('Stopping — anything already generated is kept.', { id: 'html-gen' });
      } else {
        // r137b #2: a run this tab does NOT own (reopened-tab poller before
        // it adopts the job id, or a non-HTML flow) — our handles reach
        // nothing. Stay honest and keep the button usable for a retry.
        cancelRequestedRef.current = false;
        setCancelRequested(false);
        toast.error("Can't stop this yet — try again in a moment.");
      }
      return;
    }
    try {
      const resp = await fetch(withBackend(`/api/jobs/${jid}/cancel`), { method: 'POST' });
      if (resp.status === 404) {
        // Job died server-side (e.g. a deploy restarted the worker) — the
        // in-memory job registry was wiped, so there is nothing to stop.
        setCancelRequested(false);
        cancelRequestedRef.current = false; // r137b #3: a failed cancel must not misroute later errors into "Stopped"
        toast.error('This job is no longer running on the server — nothing to stop.');
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast('Stopping after the current slide — finished slides are kept.', { id: 'html-gen' });
    } catch (e) {
      setCancelRequested(false);
      cancelRequestedRef.current = false; // r137b #3
      toast.error('Could not cancel: ' + (e.message || 'unknown error'));
    }
  };

  // Step 4: Generating — shows progressive slide grid with sidebar
  const renderGeneratingStep = () => {
    // External chat mode: vertical slide list, no sidebar (progress is in chat)
    if (externalChatMode) {
      return (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with title and progress */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-500 animate-pulse" />
              <span className="text-sm font-medium">{presentationTitle}</span>
            </span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-green-600 text-xs">
                {generatedSlides.filter(s => s !== null && s !== undefined).length} / {slideCount} slides
              </Badge>
              <button
                onClick={handleCancelGeneration}
                disabled={cancelRequested}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Stop generating; slides already finished are kept"
              >
                <X className="w-3.5 h-3.5" />
                {cancelRequested ? 'Stopping…' : 'Cancel'}
              </button>
            </div>
          </div>
          {/* Progress bar */}
          <div className="px-4 py-2">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground truncate mr-2">{generationStatusMessage}</span>
              <span className="font-medium">{Math.round(generationProgress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${generationProgress}%` }}
              />
            </div>
          </div>
          {/* Vertical slide list */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
            {Array.from({ length: slideCount }, (_, idx) => {
              const slide = generatedSlides[idx];
              if (slide) {
                return (
                  <div key={idx} className="w-full max-w-4xl mx-auto animate-fade-in-up">
                    <div className="rounded-lg overflow-hidden border border-border/50">
                      {/* r120: a live editing document per slide was the real lag.
                  Keep one for anything on screen (so editing is unchanged),
                  for the slide the user is working on, and for any slide whose
                  preview image cannot be trusted yet — a stale image over a
                  freshly edited slide would look like the edit was lost. */}
              {slide.html_source && !(
                visibleSlideIdx.has(idx)
                || idx === currentSlideIndex
                || slide._needsRescreenshot
                || !slide.preview_url
              ) ? (
                <div
                  className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 cursor-pointer"
                  onClick={() => setCurrentSlideIndex(idx)}
                  title="Scroll to this slide to edit it"
                >
                  <SlidePreview slide={slide} idx={idx} className="w-full h-full object-contain" />
                </div>
              ) : slide.html_source ? (
                        <HtmlSlideEditor slide={slide} slideIndex={idx} totalSlides={slideCount} onSlideUpdate={handleInlineSlideUpdate} onElementSelected={() => {}} onFactCheck={externalChatMode ? handleFactCheck : undefined} onDriveFactCheck={userId ? () => setShowDriveFactVerifier(true) : undefined} onRegionSelect={externalChatMode && idx === currentSlideIndex ? (region) => setHtmlHighlightRegion(region ? { ...region, slideIdx: idx } : null) : undefined} onQaSlide={externalChatMode ? handleQaSlide : undefined} onSlideVariation={handleSlideVariation} onVariationKept={(slideIdx, original) => recordSlideHistory(slideIdx, original)} canUndo={((slideHistory[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onUndo={() => undoSlideEdit(idx)} canRedo={((slideRedo[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onRedo={() => redoSlideEdit(idx)} onDelete={() => { if (generatedSlides.length <= 1) { toast.error("Can't delete the last slide"); return; } if (window.confirm(`Delete slide ${idx + 1}?`)) { handleStructuralEdit({ operation: 'delete', target_slide: idx + 1 }); } }} readOnly={generating} externalEditMode={deckEditMode} />
                      ) : (
                        <div className="aspect-video bg-slate-100 dark:bg-slate-800">
                          <SlidePreview slide={slide} idx={idx} className="w-full h-full object-contain" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={idx} className="w-full max-w-4xl mx-auto">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-muted-foreground font-medium">Slide {idx + 1}</span>
                    <span className="text-[10px] text-muted-foreground">{idx + 1} of {slideCount}</span>
                  </div>
                  <div className="aspect-video rounded-lg border-2 border-dashed border-border/50 bg-muted/20 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                      <span className="text-xs">Generating...</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Standard mode: grid layout with sidebar
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Progress sidebar */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <Card>
            <CardContent className="p-4">
              <GenerationProgressSidebar />
            </CardContent>
          </Card>
        </div>

        {/* Slide grid */}
        <div className="lg:col-span-3 order-1 lg:order-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2 min-w-0 flex-1">
                  <Sparkles className="w-5 h-5 text-green-500 animate-pulse flex-shrink-0" />
                  <span className="truncate">{presentationTitle}</span>
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-green-600">
                    {generatedSlides.filter(s => s !== null && s !== undefined).length} / {slideCount} slides
                  </Badge>
                  <button
                    onClick={handleCancelGeneration}
                    disabled={cancelRequested}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Stop generating; slides already finished are kept"
                  >
                    <X className="w-3.5 h-3.5" />
                    {cancelRequested ? 'Stopping…' : 'Cancel'}
                  </button>
                </div>
              </CardTitle>
              <CardDescription>Designing your presentation...</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Array.from({ length: slideCount }, (_, idx) => {
                  const slide = generatedSlides[idx];
                  if (slide && slide !== null) {
                    return (
                      <div key={idx} className="animate-fade-in-up">
                        <div className="aspect-video rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-800">
                          <SlidePreview slide={slide} idx={idx} thumbnail />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 truncate">{slide.title || `Slide ${idx + 1}`}</p>
                      </div>
                    );
                  }
                  return <SlideSkeletonCard key={idx} index={idx} />;
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Old step 4 spinner kept for non-Nano-Banana modes (templates, pptx, video)
  const renderLegacyGeneratingStep = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative mb-8">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
          generationMode === 'templates'
            ? 'bg-gradient-to-r from-amber-500 to-orange-500'
            : generationMode === 'pptx'
            ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
            : generationMode === 'video'
            ? 'bg-gradient-to-r from-pink-500 to-rose-500'
            : 'bg-gradient-to-r from-green-500 to-emerald-500'
        }`}>
          {generationMode === 'templates' ? (
            <Zap className="w-12 h-12 text-white animate-pulse" />
          ) : generationMode === 'pptx' ? (
            <Upload className="w-12 h-12 text-white animate-pulse" />
          ) : generationMode === 'video' ? (
            <Film className="w-12 h-12 text-white animate-pulse" />
          ) : (
            <Sparkles className="w-12 h-12 text-white animate-pulse" />
          )}
        </div>
        <div className={`absolute inset-0 rounded-full border-4 animate-ping ${
          generationMode === 'templates'
            ? 'border-amber-500/30'
            : generationMode === 'pptx'
            ? 'border-blue-500/30'
            : generationMode === 'video'
            ? 'border-pink-500/30'
            : 'border-green-500/30'
        }`} />
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">
        {generationMode === 'video' ? 'Creating Your Video' : 'Creating Your Presentation'}
      </h2>
      <p className="text-muted-foreground mb-8">
        {generationMode === 'templates'
          ? `Applying ${selectedTheme?.name || 'theme'} template...`
          : generationMode === 'pptx'
          ? `Populating ${selectedPPTXTemplate?.name || 'template'} with your content...`
          : generationMode === 'video'
          ? 'Rendering animated video with Remotion...'
          : 'Imaginative mode is designing your slides...'
        }
      </p>

      <div className="w-full max-w-md">
        <div className="flex justify-between text-sm mb-2">
          <span>Progress</span>
          <span>{Math.round(generationProgress)}%</span>
        </div>
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              generationMode === 'templates'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                : generationMode === 'pptx'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                : generationMode === 'video'
                ? 'bg-gradient-to-r from-pink-500 to-rose-500'
                : 'bg-gradient-to-r from-green-500 to-emerald-500'
            }`}
            style={{ width: `${generationProgress}%` }}
          />
        </div>
      </div>

      <div className="mt-8 space-y-2 text-sm text-muted-foreground">
        {generationMode === 'templates' ? (
          <>
            <p>• Applying template layout</p>
            <p>• Rendering slide content</p>
            <p>• Generating preview images</p>
          </>
        ) : generationMode === 'pptx' ? (
          <>
            <p>• Loading your PPTX template</p>
            <p>• Mapping content to placeholders</p>
            <p>• Populating slide text</p>
            <p>• Generating preview images</p>
          </>
        ) : generationMode === 'video' ? (
          <>
            <p>• Preparing slide compositions</p>
            <p>• Applying brand colors and fonts</p>
            <p>• Rendering animations and transitions</p>
            <p>• Encoding video file</p>
          </>
        ) : (
          <>
            <p>• Analyzing content structure</p>
            <p>• Applying brand guidelines</p>
            <p>• Generating visual designs</p>
            <p>• Creating slide layouts</p>
          </>
        )}
      </div>
    </div>
  );

  // Skeleton card for generating slides
  const SlideSkeletonCard = ({ index }) => (
    <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 overflow-hidden p-4 flex flex-col justify-between">
      <div className="space-y-2">
        <div className="h-4 w-3/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
        <div className="h-3 w-2/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
      </div>
      <div className="space-y-2 flex-1 mt-4">
        <div className="h-2.5 w-full rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
        <div className="h-2.5 w-4/5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
        <div className="h-2.5 w-3/4 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] animate-skeleton-shimmer" />
      </div>
      <div className="flex justify-between items-center mt-3">
        <div className="h-2 w-16 rounded bg-slate-200 dark:bg-slate-700" />
        <span className="text-xs text-muted-foreground font-medium">Slide {index + 1}</span>
      </div>
    </div>
  );

  // Progress sidebar showing real-time generation steps
  const GenerationProgressSidebar = () => {
    // Sub-steps that describe what Gemini is actually doing during image generation
    const slideSubSteps = [
      'Composing layout...',
      'Rendering typography...',
      'Generating visual elements...',
      'Applying color scheme...',
      'Refining image details...',
      'Finalizing composition...',
    ];
    const activeSubStep = slideSubSteps[generationElapsed % slideSubSteps.length];

    const steps = [];
    steps.push({
      label: 'Analyzing content',
      status: generationProgress > 0 ? 'completed' : 'active',
    });
    const completedSlideCount = generatedSlides.filter(s => s !== null && s !== undefined).length;
    for (let i = 0; i < slideCount; i++) {
      const slideExists = generatedSlides[i] !== undefined && generatedSlides[i] !== null;
      const isGenerating = activeGeneratingIndices.includes(i);
      // If no parallel info, fall back to showing next sequential slide as active
      const isNextSequential = !slideExists && !isGenerating && activeGeneratingIndices.length === 0 && i === completedSlideCount;
      steps.push({
        label: `Designing slide ${i + 1}`,
        status: slideExists ? 'completed' : (isGenerating || isNextSequential) ? 'active' : 'pending',
        isSlide: true,
      });
    }
    steps.push({
      label: 'Finalizing',
      status: completedSlideCount >= slideCount ? 'completed' : 'pending',
    });

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-green-500 animate-pulse" />
          <h3 className="font-semibold text-sm">Generating</h3>
        </div>
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-foreground truncate mr-2">{generationStatusMessage}</span>
            <span className="font-medium">{Math.round(generationProgress)}%</span>
          </div>
          <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${generationProgress}%` }}
            />
          </div>
        </div>
        <div className="space-y-1">
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex items-center gap-2 py-1.5 px-2 rounded-md text-sm transition-all ${
                step.status === 'active' ? 'bg-green-50 dark:bg-green-950/30' : ''
              }`}>
                {step.status === 'completed' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : step.status === 'active' ? (
                  <Loader2 className="w-4 h-4 text-green-500 animate-spin flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 flex-shrink-0" />
                )}
                <span className={`${
                  step.status === 'completed' ? 'text-muted-foreground line-through' :
                  step.status === 'active' ? 'text-foreground font-medium' :
                  'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
              {/* Show cycling sub-step for the currently active slide */}
              {step.status === 'active' && step.isSlide && (
                <div className="flex items-center gap-2 py-1 px-2 pl-8 text-xs text-muted-foreground transition-all duration-500">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                  <span className="italic">{activeSubStep}</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Step 5: Review
  const renderReviewStep = () => (
    <div className={externalChatMode ? 'flex-1 flex flex-col overflow-hidden' : 'space-y-6'}>
      {/* Live-generation state: deck opened while the background job is
          still rendering (e.g. via a Claude link or a refresh). The
          poller pulls slides in as they complete; here we show completed
          slides plus dashed placeholders for the ones still rendering,
          and a banner telling the user it may take a minute. Gated on
          !generating so it never clashes with the live-SSE branch below. */}
      {liveGenerating && !generating && (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          <div className="w-full max-w-4xl mx-auto rounded-lg border border-violet-300/50 bg-violet-50 dark:bg-violet-500/10 px-4 py-3 flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-violet-600 shrink-0" />
            <div className="text-sm text-violet-900 dark:text-violet-200 flex-1 min-w-0">
              {/* r116b: say what is ACTUALLY happening. The backend narrates
                  every phase of the QA tail (reviewing slide N, aligning
                  furniture, checking brand fidelity), but this banner ignored
                  it and kept counting slides — so once the last slide arrived
                  it froze on "5 of 5 ready" for the whole review pass and read
                  as a hang. The counter now only shows while slides are still
                  arriving; after that the phase message carries it. */}
              <span className="font-medium">
                {generationStatusMessage || 'Generating your slides…'}
              </span>{' '}
              {liveTotal > 0 && generatedSlides.filter(Boolean).length < liveTotal
                ? `${generatedSlides.filter(Boolean).length} of ${liveTotal} ready.`
                : ''} This can take a minute — you can stay here and watch them appear, or come back later.
            </div>
            <button
              onClick={handleCancelGeneration}
              disabled={cancelRequested}
              className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Stop generating; slides already finished are kept"
            >
              <X className="w-3.5 h-3.5" />
              {cancelRequested ? 'Stopping…' : 'Cancel'}
            </button>
          </div>
          {Array.from({ length: Math.max(liveTotal, generatedSlides.length, 1) }, (_, idx) => {
            const slide = generatedSlides[idx];
            if (slide) {
              return (
                <div key={idx} className="w-full max-w-4xl mx-auto animate-fade-in-up">
                  <div className="rounded-lg overflow-hidden border border-border/50 aspect-video bg-slate-100 dark:bg-slate-800">
                    <SlidePreview slide={slide} idx={idx} className="w-full h-full object-contain" />
                  </div>
                </div>
              );
            }
            return (
              <div key={idx} className="w-full max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground font-medium">Slide {idx + 1}</span>
                  {liveTotal > 0 && <span className="text-[10px] text-muted-foreground">{idx + 1} of {liveTotal}</span>}
                </div>
                <div className="aspect-video rounded-lg border-2 border-dashed border-border/50 bg-muted/20 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                    <span className="text-xs">Generating slide {idx + 1}…</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Generating state */}
      {generating && externalChatMode && (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {Array.from({ length: slideCount }, (_, idx) => {
            const slide = generatedSlides[idx];
            if (slide) {
              return (
                <div key={idx} className="w-full max-w-4xl mx-auto animate-fade-in-up">
                  <div className="rounded-lg overflow-hidden border border-border/50">
                    {slide.html_source ? (
                      <HtmlSlideEditor slide={slide} slideIndex={idx} totalSlides={slideCount} onSlideUpdate={handleInlineSlideUpdate} onElementSelected={() => {}} onFactCheck={externalChatMode ? handleFactCheck : undefined} onDriveFactCheck={userId ? () => setShowDriveFactVerifier(true) : undefined} onRegionSelect={externalChatMode && idx === currentSlideIndex ? (region) => setHtmlHighlightRegion(region ? { ...region, slideIdx: idx } : null) : undefined} onQaSlide={externalChatMode ? handleQaSlide : undefined} onSlideVariation={handleSlideVariation} onVariationKept={(slideIdx, original) => recordSlideHistory(slideIdx, original)} canUndo={((slideHistory[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onUndo={() => undoSlideEdit(idx)} canRedo={((slideRedo[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onRedo={() => redoSlideEdit(idx)} onDelete={() => { if (generatedSlides.length <= 1) { toast.error("Can't delete the last slide"); return; } if (window.confirm(`Delete slide ${idx + 1}?`)) { handleStructuralEdit({ operation: 'delete', target_slide: idx + 1 }); } }} externalEditMode={deckEditMode} />
                    ) : (
                      <div className="aspect-video bg-slate-100 dark:bg-slate-800">
                        <SlidePreview slide={slide} idx={idx} className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div key={idx} className="w-full max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground font-medium">Slide {idx + 1}</span>
                  <span className="text-[10px] text-muted-foreground">{idx + 1} of {slideCount}</span>
                </div>
                <div className="aspect-video rounded-lg border-2 border-dashed border-border/50 bg-muted/20 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                    <span className="text-xs">Generating...</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {generating && !externalChatMode && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 order-2 lg:order-1">
            <Card>
              <CardContent className="p-4">
                <GenerationProgressSidebar />
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-3 order-1 lg:order-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-green-500 animate-pulse" />
                    {presentationTitle}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-green-600">
                      {generatedSlides.filter(s => s !== null && s !== undefined).length} / {slideCount} slides
                    </Badge>
                    <button
                      onClick={handleCancelGeneration}
                      disabled={cancelRequested}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Stop generating; slides already finished are kept"
                    >
                      <X className="w-3.5 h-3.5" />
                      {cancelRequested ? 'Stopping…' : 'Cancel'}
                    </button>
                  </div>
                </CardTitle>
                <CardDescription>Designing your presentation...</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Array.from({ length: slideCount }, (_, idx) => {
                    const slide = generatedSlides[idx];
                    if (slide && slide !== null) {
                      return (
                        <div
                          key={idx}
                          className="animate-fade-in-up cursor-pointer"
                          onClick={() => setCurrentSlideIndex(idx)}
                        >
                          <div className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                            currentSlideIndex === idx
                              ? 'border-green-500 ring-2 ring-green-500/30'
                              : 'border-slate-200 dark:border-slate-700 hover:border-green-300'
                          }`}>
                            <SlidePreview slide={slide} idx={idx} />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate">{slide.title || `Slide ${idx + 1}`}</p>
                        </div>
                      );
                    }
                    return <SlideSkeletonCard key={idx} index={idx} />;
                  })}
                </div>

                {/* Enlarged preview of selected slide */}
                {generatedSlides.length > 0 && generatedSlides[currentSlideIndex] && (
                  <div className="mt-6 animate-fade-in">
                    <h4 className="text-sm font-medium mb-2">
                      Slide {currentSlideIndex + 1}: {generatedSlides[currentSlideIndex].title}
                    </h4>
                    <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border-2 border-green-500/30">
                      <SlidePreview slide={generatedSlides[currentSlideIndex]} idx={currentSlideIndex} className="w-full h-full object-contain" />
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Vertical scroll layout for external chat mode (review, not generating) */}
      {!generating && !liveGenerating && externalChatMode && (
      /* r125: the editing surface is a ROW — slides scroll on the left, and
         the Style panel DOCKS on the right like PowerPoint's format pane,
         pushing content instead of floating over it. The r124 floating panel
         could cover the slide and could not be moved. It appears when an
         element is selected and leaves when the selection does (Esc or its
         close button). */
      <div className="flex-1 flex overflow-hidden">
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
        {/* r131: proposal placeholders — facts the user has not supplied yet */}
        {(() => {
          const _ph = generatedSlides.filter((s) => ((s?.html_source || s?.html || '').includes('[PLACEHOLDER'))).length;
          return _ph > 0 ? (
            <div className="w-full max-w-4xl mx-auto flex items-start gap-2 text-xs font-medium text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2">
              <span className="font-semibold shrink-0">{_ph} slide{_ph > 1 ? 's' : ''} need{_ph > 1 ? '' : 's'} your input</span>
              <span className="text-amber-700/80 dark:text-amber-400/80">[PLACEHOLDER] marks facts you have not supplied — edit the slide directly or ask the chat to fill them.</span>
            </div>
          ) : null;
        })()}
        {generatedSlides.map((slide, idx) => slide && (
          <div
            key={idx}
            className="w-full max-w-4xl mx-auto"
            data-slide-idx={idx}
            ref={(node) => registerSlideCard(idx, node)}
          >
            <div className="relative group">
              {(htmlChatLoading || editingSlides) && (activeEditIdx != null ? activeEditIdx === idx : currentSlideIndex === idx) && (
                <div className="absolute inset-0 z-30 rounded-lg overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px]" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-3 bg-background/90 backdrop-blur-md px-5 py-3 rounded-xl shadow-lg border border-border/50">
                      <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                      <span className="text-sm font-medium text-foreground">Redesigning slide...</span>
                    </div>
                  </div>
                </div>
              )}
              {slide.html_source ? (
                <HtmlSlideEditor
                  ref={currentSlideIndex === idx ? htmlSlideEditorRef : undefined}
                  slide={slide}
                  slideIndex={idx}
                  totalSlides={generatedSlides.length}
                  onSlideUpdate={handleInlineSlideUpdate}
                  onElementSelected={(el) => {
                    setCurrentSlideIndex(idx);
                    setSelectedCssElement(el);
                    if (el) setRightPanelTab('style');
                  }}
                  onFactCheck={externalChatMode ? handleFactCheck : undefined}
                  onDriveFactCheck={userId ? () => setRightPanelTab('verify') : undefined}
                  onRegionSelect={externalChatMode ? (region) => { setCurrentSlideIndex(idx); setHtmlHighlightRegion(region ? { ...region, slideIdx: idx } : null); } : undefined}
                  onQaSlide={externalChatMode ? handleQaSlide : undefined} onSlideVariation={handleSlideVariation} onVariationKept={(slideIdx, original) => recordSlideHistory(slideIdx, original)} canUndo={((slideHistory[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onUndo={() => undoSlideEdit(idx)} canRedo={((slideRedo[generatedSlides[idx]?._uid || `i${idx}`])?.length || 0) > 0} onRedo={() => redoSlideEdit(idx)}
                  onDelete={() => { if (generatedSlides.length <= 1) { toast.error("Can't delete the last slide"); return; } if (window.confirm(`Delete slide ${idx + 1}?`)) { handleStructuralEdit({ operation: 'delete', target_slide: idx + 1 }); } }}
                  externalEditMode={deckEditMode}
                />
              ) : (
                <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                  <SlidePreview slide={slide} idx={idx} className="w-full h-full object-contain" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {(selectedCssElement || deckEditMode) && (
        <aside className="w-80 shrink-0 border-l border-border bg-card flex flex-col overflow-hidden">
          {/* r144: in deck edit mode the sidebar is PERSISTENT — it stops
              mounting/unmounting with every selection (the flicker the user
              reported). The X only clears the selection then. */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
            <span className="text-xs font-semibold text-foreground truncate">
              Style · {selectedCssElement?.tag?.toLowerCase?.() || (deckEditMode ? 'nothing selected' : 'element')}
            </span>
            <button
              onClick={() => setSelectedCssElement(null)}
              className="text-muted-foreground hover:text-foreground"
              title="Close (or press Esc to deselect)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <CssPropertyPanel
              selectedElement={selectedCssElement}
              onPropertyChange={(property, value) => {
                setSelectedCssElement(prev => prev ? {
                  ...prev,
                  computedStyles: { ...prev.computedStyles, [property]: value }
                } : null);
                htmlSlideEditorRef.current?.applyStyleChange(property, value);
              }}
            />
          </div>
        </aside>
      )}
      </div>
      )}

      {/* 3-column layout — standard (non-external) review mode */}
      {!generating && !liveGenerating && !externalChatMode && (
      <>
      {/* Title bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-foreground" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="flex items-center gap-2 text-base font-semibold min-w-0">
            <Image className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="truncate">{presentationTitle}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {peers.length > 0 && (
            <div className="flex items-center gap-1.5" title={peers.map(p => p.name).join(', ')}>
              <div className="flex -space-x-1.5">
                {peers.slice(0, 4).map(p => (
                  <div
                    key={p.user_id}
                    title={`${p.name}${p.editingSlide != null ? ` — on slide ${p.editingSlide + 1}` : ''}`}
                    className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-accent text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-background"
                  >
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                ))}
              </div>
              {peers.length > 4 && (
                <span className="text-[10px] text-muted-foreground">+{peers.length - 4}</span>
              )}
            </div>
          )}
          <span className="text-xs text-muted-foreground">Slide {currentSlideIndex + 1} of {generatedSlides.length}</span>
        </div>
      </div>
      {/* Collaboration notices: a peer saved, or our save hit a newer rev */}
      {(conflictInfo || remoteUpdate) && (
        <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-1.5">
          <p className="text-xs text-foreground min-w-0 truncate">
            {conflictInfo
              ? `${conflictInfo.by || 'A teammate'} saved a newer version of this deck — your save was not applied.`
              : `${remoteUpdate?.by || 'A teammate'} saved changes to this deck.`}
            <span className="text-muted-foreground"> Refreshing replaces your unsaved edits.</span>
          </p>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button onClick={refreshFromRemote} className="text-xs font-semibold text-amber-600 dark:text-amber-400 hover:underline">
              Refresh deck
            </button>
            <button onClick={() => { setConflictInfo(null); setRemoteUpdate(null); }} className="text-xs text-muted-foreground hover:text-foreground">
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-3" style={{ height: 'calc(100vh - 70px)' }}>

        {/* LEFT: Vertical Thumbnail Panel (desktop only) */}
        <div className="hidden lg:flex w-[140px] flex-shrink-0 flex-col">
          <ScrollArea className="flex-1">
            <div className="flex flex-col gap-1.5 p-1">
              {generatedSlides.map((slide, idx) => (
                slide ? (
                  <button
                    key={idx}
                    onClick={() => { setAnimatingSlideIndex(null); setNavigationAnimating(true); setCurrentSlideIndex(idx); }}
                    className={`relative w-full rounded overflow-hidden transition-all ${
                      currentSlideIndex === idx
                        ? 'ring-2 ring-green-500 ring-offset-1 dark:ring-offset-slate-900'
                        : 'ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-slate-300'
                    }`}
                  >
                    <div className="aspect-video w-full bg-slate-100 dark:bg-slate-800">
                      <SlidePreview slide={slide} idx={idx} className="w-full h-full object-cover" thumbnail />
                    </div>
                    <div className="text-[10px] text-center py-0.5 text-muted-foreground">{idx + 1}</div>
                    {peers.some(p => p.editingSlide === idx) && (
                      <span
                        className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-white dark:ring-slate-900"
                        title={`${peers.filter(p => p.editingSlide === idx).map(p => p.name).join(', ')} viewing this slide`}
                      />
                    )}
                  </button>
                ) : (
                  <div key={idx} className="w-full rounded border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 flex flex-col items-center justify-center py-2">
                    <Loader2 className="w-3 h-3 text-green-500 animate-spin mb-0.5" />
                    <span className="text-[9px] text-muted-foreground">{idx + 1}</span>
                  </div>
                )
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* CENTER: Slide Preview */}
        <div className="flex-1 min-w-0 flex flex-col px-6 lg:px-10">
          <div className="flex-1 flex items-center justify-center relative group">
            <div className="w-full max-w-4xl mx-auto relative">
              {/* Shimmer overlay during AI editing — r145: only when THIS
                  slide is the one being worked (apply-all walks the deck) */}
              {(htmlChatLoading || editingSlides) && (activeEditIdx == null || activeEditIdx === currentSlideIndex) && (
                <div className="absolute inset-0 z-30 rounded-lg overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px]" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent dark:via-white/5 bg-[length:200%_100%] animate-skeleton-shimmer" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="flex items-center gap-3 bg-background/90 backdrop-blur-md px-5 py-3 rounded-xl shadow-lg border border-border/50">
                      <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                      <span className="text-sm font-medium text-foreground">Redesigning slide...</span>
                    </div>
                  </div>
                </div>
              )}
              {generatedSlides[currentSlideIndex] && (
                generatedSlides[currentSlideIndex].extractedLayers?.source === 'template' ? (
                  <InlineSlideEditor
                    slide={generatedSlides[currentSlideIndex]}
                    slideIndex={currentSlideIndex}
                    totalSlides={generatedSlides.length}
                    onSlideUpdate={handleInlineSlideUpdate}
                    onOpenFullEditor={() => {
                      const currentSlide = generatedSlides[currentSlideIndex];
                      if (currentSlide?.extractedLayers) {
                        setExtractedLayers(currentSlide.extractedLayers);
                        setSelectedLayerIndex(0);
                        setShowLayerEditor(true);
                      }
                    }}
                    theme={selectedTheme}
                    colorScheme={customColorScheme || selectedTheme?.colorScheme}
                    animateTextIn={animatingSlideIndex === currentSlideIndex}
                    navigationFade={navigationAnimating}
                    onAnimationComplete={() => {
                      setAnimatingSlideIndex(null);
                      setNavigationAnimating(false);
                    }}
                  />
                ) : generatedSlides[currentSlideIndex].html_source ? (
                  <HtmlSlideEditor
                    key={`slide-editor-${currentSlideIndex}`}
                    ref={htmlSlideEditorRef}
                    slide={generatedSlides[currentSlideIndex]}
                    slideIndex={currentSlideIndex}
                    totalSlides={generatedSlides.length}
                    onSlideUpdate={handleInlineSlideUpdate}
                    onElementSelected={(el) => {
                      setSelectedCssElement(el);
                      if (el) setRightPanelTab('style');
                    }}
                    onFactCheck={externalChatMode ? handleFactCheck : undefined}
                    onDriveFactCheck={userId ? () => setRightPanelTab('verify') : undefined}
                    onRegionSelect={externalChatMode ? (region) => setHtmlHighlightRegion(region ? { ...region, slideIdx: currentSlideIndex } : null) : undefined}
                    onQaSlide={externalChatMode ? handleQaSlide : undefined}
                    onSlideVariation={handleSlideVariation}
                    onVariationKept={(slideIdx, original) => recordSlideHistory(slideIdx, original)}
                    canUndo={((slideHistory[generatedSlides[currentSlideIndex]?._uid || `i${currentSlideIndex}`])?.length || 0) > 0}
                    onUndo={() => undoSlideEdit(currentSlideIndex)}
                    canRedo={((slideRedo[generatedSlides[currentSlideIndex]?._uid || `i${currentSlideIndex}`])?.length || 0) > 0}
                    onRedo={() => redoSlideEdit(currentSlideIndex)}
                    onDelete={() => { if (generatedSlides.length <= 1) { toast.error("Can't delete the last slide"); return; } if (window.confirm(`Delete slide ${currentSlideIndex + 1}?`)) { handleStructuralEdit({ operation: 'delete', target_slide: currentSlideIndex + 1 }); } }}
                    readOnly={generating}
                    externalEditMode={deckEditMode}
                  />
                ) : (
                  <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 relative">
                    {generatedSlides[currentSlideIndex].video_url ? (
                      <video
                        src={generatedSlides[currentSlideIndex].video_url}
                        controls
                        className="w-full h-full object-contain"
                        poster={generatedSlides[currentSlideIndex].preview_url}
                      >
                        Your browser does not support video playback.
                      </video>
                    ) : (
                      <SlidePreview slide={generatedSlides[currentSlideIndex]} idx={currentSlideIndex} className="w-full h-full object-contain" />
                    )}
                  </div>
                )
              )}

              {/* Region selection overlay for HTML chat — inside slide wrapper for correct coordinates */}
              {htmlRegionMode && generationMode === 'html' && (
                <div
                  className="absolute inset-0 z-20 cursor-crosshair"
                  onMouseDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    setDrawStart({ x, y });
                    setDrawCurrent({ x, y });
                  }}
                  onMouseMove={(e) => {
                    if (!drawStart) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                    setDrawCurrent({ x, y });
                  }}
                  onMouseUp={() => {
                    if (drawStart && drawCurrent) {
                      const region = {
                        x: Math.min(drawStart.x, drawCurrent.x),
                        y: Math.min(drawStart.y, drawCurrent.y),
                        width: Math.abs(drawCurrent.x - drawStart.x),
                        height: Math.abs(drawCurrent.y - drawStart.y),
                      };
                      if (region.width > 2 && region.height > 2) {
                        setHtmlHighlightRegion(region ? { ...region, slideIdx: currentSlideIndex } : null);
                      }
                    }
                    setDrawStart(null);
                    setDrawCurrent(null);
                    setHtmlRegionMode(false);
                  }}
                >
                  {/* Draw rectangle while dragging */}
                  {drawStart && drawCurrent && (
                    <div
                      className="absolute border-2 border-cyan-500 bg-cyan-500/10 rounded"
                      style={{
                        left: `${Math.min(drawStart.x, drawCurrent.x)}%`,
                        top: `${Math.min(drawStart.y, drawCurrent.y)}%`,
                        width: `${Math.abs(drawCurrent.x - drawStart.x)}%`,
                        height: `${Math.abs(drawCurrent.y - drawStart.y)}%`,
                      }}
                    />
                  )}
                  {/* Instruction overlay */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs px-3 py-1 rounded-full shadow">
                    Draw a rectangle on the area to edit
                  </div>
                </div>
              )}

              {/* Show selected region overlay */}
              {htmlHighlightRegion && generationMode === 'html' && !htmlRegionMode && (
                <div
                  className="absolute border-2 border-cyan-400 bg-cyan-400/10 rounded pointer-events-none z-10"
                  style={{
                    left: `${htmlHighlightRegion.x}%`,
                    top: `${htmlHighlightRegion.y}%`,
                    width: `${htmlHighlightRegion.width}%`,
                    height: `${htmlHighlightRegion.height}%`,
                  }}
                />
              )}
            </div>

            {/* Overlay Previous Arrow */}
            {currentSlideIndex > 0 && (
              <button
                onClick={() => { setAnimatingSlideIndex(null); setNavigationAnimating(true); setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1)); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity z-20"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}

            {/* Overlay Next Arrow */}
            {currentSlideIndex < generatedSlides.length - 1 && (
              <button
                onClick={() => { setAnimatingSlideIndex(null); setNavigationAnimating(true); setCurrentSlideIndex(Math.min(generatedSlides.length - 1, currentSlideIndex + 1)); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity z-20"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Mobile thumbnail strip (below lg) */}
          <div className="lg:hidden mt-3 flex gap-2 overflow-x-auto pb-2">
            {generatedSlides.map((slide, idx) => (
              slide ? (
                <button
                  key={idx}
                  onClick={() => { setAnimatingSlideIndex(null); setNavigationAnimating(true); setCurrentSlideIndex(idx); }}
                  className={`flex-shrink-0 w-20 h-[45px] rounded border-2 overflow-hidden transition-all ${
                    currentSlideIndex === idx
                      ? 'border-green-500 ring-2 ring-green-500/30'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <SlidePreview slide={slide} idx={idx} className="w-full h-full object-cover" thumbnail />
                </button>
              ) : null
            ))}
          </div>
        </div>

        {/* RIGHT: Control Panel (hidden in external chat mode) */}
        {!externalChatMode && (
        <div className={`w-full ${generationMode === 'html' ? 'lg:w-[340px]' : 'lg:w-[280px]'} flex-shrink-0 flex flex-col bg-background`}>

          {/* HTML mode: Genspark-inspired chat editor */}
          {generationMode === 'html' ? (
            <>
              {/* Tab switcher: AI Chat / Style / Verify */}
              <div className="flex border-b border-border">
                <button
                  onClick={() => setRightPanelTab('chat')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${
                    rightPanelTab === 'chat'
                      ? 'text-foreground border-b-2 border-emerald-500'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  AI Chat
                </button>
                <button
                  onClick={() => setRightPanelTab('style')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${
                    rightPanelTab === 'style'
                      ? 'text-foreground border-b-2 border-indigo-500'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Style
                  {selectedCssElement && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
                  )}
                </button>
              </div>

              {/* Style panel */}
              {rightPanelTab === 'style' ? (
                <div className="flex-1 overflow-y-auto">
                  <CssPropertyPanel
                    selectedElement={selectedCssElement}
                    onPropertyChange={(property, value) => {
                      // Update local state for panel reactivity
                      setSelectedCssElement(prev => prev ? {
                        ...prev,
                        computedStyles: { ...prev.computedStyles, [property]: value }
                      } : null);
                      // Push change into the iframe
                      htmlSlideEditorRef.current?.applyStyleChange(property, value);
                    }}
                  />
                </div>
              ) : rightPanelTab === 'verify' ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                  <DriveFactVerifier slides={generatedSlides} userId={userId} />
                </div>
              ) : (
              <>
              {/* Chat messages area */}
              <div
                ref={htmlChatScrollRef}
                className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
              >
                {htmlChatHistory.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center mb-3">
                      <Wand2 className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">AI Slide Editor</p>
                    <p className="text-xs text-muted-foreground max-w-[220px]">
                      Describe changes to your slides and the AI will edit them instantly
                    </p>
                    {/* Quick suggestion chips */}
                    <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                      {['Fix overlapping text', 'More whitespace', 'Smaller fonts', 'Better spacing', 'Larger title'].map(s => (
                        <button
                          key={s}
                          onClick={() => handleHtmlChatSend(s)}
                          disabled={htmlChatLoading}
                          className="px-2.5 py-1 text-[11px] rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground hover:text-foreground hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {htmlChatHistory.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role !== 'user' && (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-foreground text-background'
                        : msg.isError
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                        : 'bg-slate-100 dark:bg-slate-800 text-foreground'
                    }`}>
                      {msg.role === 'user' && msg.slideIndex !== undefined && (
                        <span className="text-[10px] opacity-60 block mb-0.5">Slide {msg.slideIndex + 1}</span>
                      )}
                      {msg.type === 'image_preview' ? (
                        <div>
                          <img src={msg.image_url} alt="Generated" className="max-h-32 rounded-lg mb-2 border" />
                          <p className="text-xs text-muted-foreground mb-2">Is this the image you want?</p>
                          <div className="flex gap-1.5">
                            <Button size="sm" className="h-6 text-[10px] px-2" onClick={() => handleConfirmImage(msg)}>
                              <Check className="w-3 h-3 mr-1" /> Use this
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleRegenerateImage(msg)}>
                              Regenerate
                            </Button>
                          </div>
                        </div>
                      ) : (
                        msg.content
                      )}
                      {msg.region && !msg.type && (
                        <span className="text-[10px] opacity-60 block mt-1 flex items-center gap-1">
                          <Crosshair className="w-2.5 h-2.5" /> Region selected
                        </span>
                      )}
                      {msg.images?.length > 0 && (
                        <span className="text-[10px] opacity-60 block mt-1 flex items-center gap-1">
                          <Paperclip className="w-2.5 h-2.5" /> {msg.images.length} image(s)
                        </span>
                      )}
                      {msg.qaIssues?.length > 0 && (
                        <div className="mt-2">
                          <Button
                            size="sm"
                            className="h-6 text-[10px] px-2"
                            onClick={() => handleApplyQaFixes(msg.slideIndex, msg.qaIssues)}
                          >
                            <Check className="w-3 h-3 mr-1" /> Apply all {msg.qaIssues.length} fix{msg.qaIssues.length > 1 ? 'es' : ''}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {htmlChatLoading && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0 mr-2">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-2.5">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Attached images & region indicators — above input */}
              {(htmlChatImages.length > 0 || htmlHighlightRegion) && (
                <div className="px-4 pb-1 flex items-center gap-2 flex-wrap">
                  {htmlChatImages.map((img, i) => (
                    <div key={i} className="relative w-11 h-11 rounded-lg border border-border/50 overflow-hidden group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setHtmlChatImages(prev => prev.filter((_, j) => j !== i))}
                        className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  ))}
                  {htmlHighlightRegion && (
                    <div className="flex items-center gap-1.5 text-[11px] text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg px-2.5 py-1.5">
                      <Crosshair className="w-3 h-3" />
                      Region
                      <button onClick={() => setHtmlHighlightRegion(null)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Genspark-inspired input area */}
              <div className="px-3 pb-3">
                <div
                  className={`rounded-xl border bg-card shadow-sm focus-within:border-emerald-400/50 focus-within:shadow-md transition-all ${
                    chatDragOver ? 'border-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10' : 'border-border/60'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setChatDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setChatDragOver(false); }}
                  onDrop={handleChatDrop}
                >
                  {chatDragOver && (
                    <div className="px-3.5 pt-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      Drop images here...
                    </div>
                  )}
                  <textarea
                    value={htmlChatInput}
                    onChange={(e) => setHtmlChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!uploadingImages && !htmlChatLoading) handleHtmlChatSend();
                      }
                    }}
                    onPaste={handleChatPaste}
                    placeholder={uploadingImages ? 'Processing attachment...' : 'Enter your edit request here'}
                    rows={2}
                    className="w-full text-sm bg-transparent px-3.5 pt-3 pb-1 resize-none focus:outline-none placeholder:text-muted-foreground/60"
                    disabled={htmlChatLoading || uploadingImages}
                  />
                  <div className="flex items-center justify-between px-2 pb-2">
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => htmlChatFileRef.current?.click()}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground transition-colors"
                        title="Attach image"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setHtmlRegionMode(!htmlRegionMode)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          htmlRegionMode
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground'
                        }`}
                        title="Select region on slide"
                      >
                        <Crosshair className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setHtmlApplyAll(!htmlApplyAll)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                          htmlApplyAll
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground'
                        }`}
                        title="Apply edit to all slides"
                      >
                        <Layers className="w-3.5 h-3.5" />
                        All
                      </button>
                      {(brandIdentityId || brandIdentityIdRef.current) && (
                        <button
                          onClick={() => setTeachBrandOpen(true)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground transition-colors"
                          title="Teach the brand from this slide — record a rule every future deck follows"
                        >
                          <GraduationCap className="w-4 h-4" />
                        </button>
                      )}
                      <input
                        ref={htmlChatFileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleHtmlChatImageUpload}
                      />
                    </div>
                    <button
                      onClick={() => handleHtmlChatSend()}
                      disabled={!htmlChatInput.trim() || htmlChatLoading || uploadingImages}
                      className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {htmlChatLoading || uploadingImages ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowLeft className="w-4 h-4 rotate-90" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              </>
              )}
            </>
          ) : (
            <>
              {/* Non-HTML modes: AI Edit / Manual Edit tabs */}
              <div className="p-3 border-b border-border">
                <Tabs value={editModeTab} onValueChange={setEditModeTab} className="w-full">
                  <TabsList className="w-full grid grid-cols-2 h-9">
                    <TabsTrigger value="prompt" className="text-xs gap-1.5">
                      <Wand2 className="w-3.5 h-3.5" />
                      AI Edit
                    </TabsTrigger>
                    <TabsTrigger value="manual" className="text-xs gap-1.5">
                      <Layers className="w-3.5 h-3.5" />
                      Manual
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {editModeTab === 'prompt' && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Use AI to edit slides with a text prompt. You can highlight regions and reference other slides for consistency.
                    </p>
                    <Button
                      size="sm"
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                      onClick={() => setShowEditDialog(true)}
                      disabled={editingSlides}
                    >
                      {editingSlides ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Editing...</>
                      ) : (
                        <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Edit with AI</>
                      )}
                    </Button>
                  </div>
                )}

                {editModeTab === 'manual' && (
                  <div className="space-y-3">
                    {generatedSlides[currentSlideIndex]?.extractedLayers?.layers?.length > 0 ? (
                      <>
                        <p className="text-xs text-muted-foreground">
                          This slide has {generatedSlides[currentSlideIndex].extractedLayers.layers.length} editable layers.
                        </p>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const currentSlide = generatedSlides[currentSlideIndex];
                            if (currentSlide?.extractedLayers) {
                              setExtractedLayers(currentSlide.extractedLayers);
                              setSelectedLayerIndex(0);
                              setShowLayerEditor(true);
                            }
                          }}
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1.5" />
                          Open Layer Editor
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">
                          Extract layers from the current slide to enable manual editing of individual elements.
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={handleMakeCurrentSlideEditable}
                          disabled={makingEditable}
                        >
                          {makingEditable ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting...</>
                          ) : (
                            <><Layers className="w-3.5 h-3.5 mr-1.5" /> Make Editable</>
                          )}
                        </Button>
                        <button
                          onClick={handleMakeAllSlidesEditable}
                          disabled={makingEditable}
                          className="text-xs text-muted-foreground hover:text-foreground w-full text-center py-1 transition-colors"
                        >
                          {makingEditable && editableProgress.total > 1
                            ? `Making editable (${editableProgress.current}/${editableProgress.total})...`
                            : 'Make all slides editable'}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Layout sidebar (template mode only) */}
                {generatedSlides[currentSlideIndex]?.extractedLayers?.source === 'template' && (
                  <div className="space-y-2 border-t border-border pt-3">
                    <h4 className="text-xs font-semibold flex items-center gap-1.5">
                      <LayoutGrid className="w-3.5 h-3.5" />
                      Slide Layout
                    </h4>
                    <div className="space-y-1.5">
                      {layoutOptions.map(opt => {
                        const isActive = (generatedSlides[currentSlideIndex]?.slideType || '') === opt.id;
                        const thumb = layoutThumbnails[opt.id];
                        return (
                          <button
                            key={opt.id}
                            onClick={() => handleChangeSlideType(opt.id)}
                            disabled={changingSlideType}
                            className={`w-full text-left rounded-md text-xs transition-all overflow-hidden ${
                              isActive
                                ? 'ring-2 ring-green-500 ring-offset-1 dark:ring-offset-slate-900'
                                : 'ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-slate-300 dark:hover:ring-slate-600'
                            } ${changingSlideType ? 'opacity-50 cursor-wait' : ''}`}
                          >
                            {thumb ? (
                              <div className="aspect-video w-full bg-slate-100 dark:bg-slate-800">
                                <img src={thumb} alt={opt.name} className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="aspect-video w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                <LayoutGrid className="w-4 h-4 text-slate-300" />
                              </div>
                            )}
                            <div className="px-2 py-1">
                              <div className={`font-medium text-[10px] ${isActive ? 'text-green-700 dark:text-green-400' : ''}`}>{opt.name}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Pinned bottom: Compact action buttons */}
          <div className="border-t border-border p-2 space-y-1.5">
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="text-xs h-8 px-2" onClick={openVersions} disabled={!projectId || liveGenerating || generating} title="Version history">
                <History className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={handleSaveProject} disabled={saving || generatedSlides.length === 0}>
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1" /> Save</>
                )}
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => setShowPresentMode(true)} disabled={generatedSlides.length === 0}>
                <Play className="w-3.5 h-3.5 mr-1" /> Present
              </Button>
              {(brandIdentityId || brandIdentityIdRef.current) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTeachBrandOpen(true)}
                  className="h-8 w-8 p-0 shrink-0"
                  title="Teach this brand — record a correction every future deck follows"
                >
                  <GraduationCap className="w-4 h-4" />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    disabled={exporting || generatedSlides.length === 0}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs h-8"
                  >
                    {exporting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><Download className="w-3.5 h-3.5 mr-1" /> Export <ChevronDown className="w-3 h-3 ml-1" /></>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleExportPptxSvg} className="text-xs gap-2">
                    <FileText className="w-3.5 h-3.5" /> PPTX (Editable)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPDF} className="text-xs gap-2">
                    <File className="w-3.5 h-3.5" /> PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleTestStructuredSlide} className="text-xs gap-2 border-t mt-1 pt-2">
                    <Sparkles className="w-3.5 h-3.5 text-purple-500" /> [DEV] Test Structured Model
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSpikeSvgExport} className="text-xs gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-500" /> [SPIKE] SVG → PPTX (current slide)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {generationMode === 'templates' && selectedTheme && (
              <Button variant="ghost" size="sm" className="w-full text-xs h-7 text-muted-foreground hover:text-foreground" onClick={() => setShowThemeCustomizer(true)}>
                <Palette className="w-3 h-3 mr-1.5" /> Customize Theme
              </Button>
            )}
            <button
              onClick={() => setCurrentStep(1)}
              className="text-[10px] text-muted-foreground hover:text-foreground w-full text-center py-0.5 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>
        )}
      </div>
      </>
      )}

      {/* Edit Dialog */}
      {showEditDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-green-500" />
              Regenerate with Edits
            </h3>

            <div className="flex gap-6">
              {/* Left Column: Slide Preview + Region Drawing */}
              <div className="flex-1 min-w-0">
                <label className="text-sm font-medium mb-2 block">
                  Highlight region to edit <span className="text-muted-foreground font-normal">(optional — drag to select)</span>
                </label>
                <div
                  ref={editSlidePreviewRef}
                  className="relative aspect-video w-full bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden cursor-crosshair select-none border border-slate-200 dark:border-slate-700"
                  onMouseDown={handleRegionMouseDown}
                  onMouseMove={handleRegionMouseMove}
                  onMouseUp={handleRegionMouseUp}
                  onMouseLeave={() => { if (isDrawingRegion) handleRegionMouseUp(); }}
                >
                  {generatedSlides[currentSlideIndex]?.preview_url && (
                    <img
                      src={generatedSlides[currentSlideIndex].preview_url}
                      alt={`Slide ${currentSlideIndex + 1}`}
                      className="w-full h-full object-contain pointer-events-none"
                      draggable={false}
                    />
                  )}
                  {/* Drawing rectangle */}
                  {isDrawingRegion && drawStart && drawCurrent && (
                    <div
                      className="absolute border-2 border-dashed border-green-500 bg-green-500/15 pointer-events-none"
                      style={{
                        left: `${Math.min(drawStart.x, drawCurrent.x)}%`,
                        top: `${Math.min(drawStart.y, drawCurrent.y)}%`,
                        width: `${Math.abs(drawCurrent.x - drawStart.x)}%`,
                        height: `${Math.abs(drawCurrent.y - drawStart.y)}%`,
                      }}
                    />
                  )}
                  {/* Finalized region */}
                  {highlightRegion && !isDrawingRegion && (
                    <div
                      className="absolute border-2 border-green-500 bg-green-500/20 pointer-events-none"
                      style={getRegionStyle()}
                    />
                  )}
                </div>
                {highlightRegion && (
                  <button
                    onClick={() => setHighlightRegion(null)}
                    className="mt-1 text-xs text-red-500 hover:text-red-600 transition-colors"
                  >
                    Clear region
                  </button>
                )}

                {/* Reference Slides */}
                <div className="mt-4">
                  <label className="text-sm font-medium mb-2 block">
                    Reference slides <span className="text-muted-foreground font-normal">(click to select for consistency)</span>
                  </label>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {generatedSlides.map((slide, idx) => (
                      idx !== currentSlideIndex && (
                        <button
                          key={idx}
                          onClick={() => toggleReferenceSlide(idx)}
                          className={`flex-shrink-0 w-24 rounded-md overflow-hidden border-2 transition-all ${
                            referenceSlideIndices.includes(idx)
                              ? 'border-green-500 ring-1 ring-green-500/50'
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
                          }`}
                          title={`Slide ${idx + 1}: ${slide.title || ''}`}
                        >
                          <div className="aspect-video bg-slate-100 dark:bg-slate-800 relative">
                            <SlidePreview slide={slide} idx={idx} className="w-full h-full object-cover" draggable={false} thumbnail />
                            {referenceSlideIndices.includes(idx) && (
                              <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="text-[10px] px-1 py-0.5 truncate text-center">{idx + 1}</div>
                        </button>
                      )
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Scope, Prompt, Suggestions */}
              <div className="w-72 flex-shrink-0 flex flex-col">
                {/* Scope Selection */}
                <div className="mb-3">
                  <label className="text-sm font-medium mb-2 block">Apply to:</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditScope('all')}
                      className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all border ${
                        editScope === 'all'
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      All ({generatedSlides.length})
                    </button>
                    <button
                      onClick={() => setEditScope('current')}
                      className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-all border ${
                        editScope === 'current'
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Slide #{currentSlideIndex + 1}
                    </button>
                  </div>
                </div>

                {/* Prompt */}
                <Textarea
                  placeholder={editScope === 'all'
                    ? "Describe the changes you'd like..."
                    : `Describe changes for slide ${currentSlideIndex + 1}...`
                  }
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  rows={6}
                  className="mb-3 text-sm"
                  autoFocus
                />

                {/* Quick suggestions */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-1.5 block">Quick suggestions:</label>
                  <div className="flex flex-wrap gap-1.5">
                    {['Less cluttered', 'More whitespace', 'Bigger titles', 'Simpler layout', 'More professional', 'Match reference slides'].map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => setEditPrompt(prev => prev ? `${prev}, ${suggestion.toLowerCase()}` : suggestion)}
                        className="px-2 py-1 text-[11px] rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditPrompt('');
                      setEditScope('all');
                      setHighlightRegion(null);
                      setReferenceSlideIndices([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                    onClick={handleEditSlides}
                    disabled={!editPrompt.trim() || editingSlides}
                  >
                    {editingSlides ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Editing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Apply
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extracted Layers Modal */}
      {showLayersModal && extractedLayers && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-5xl w-full mx-4 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Layers className="w-5 h-5 text-green-500" />
                  Edit Slide {currentSlideIndex + 1}
                  <Badge variant="outline" className="ml-2">
                    {extractedLayers.layers?.length || 0} layers
                  </Badge>
                </h3>
                {/* Slide navigation within modal */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newIdx = Math.max(0, currentSlideIndex - 1);
                      setCurrentSlideIndex(newIdx);
                      const newSlide = generatedSlides[newIdx];
                      if (newSlide?.extractedLayers) {
                        setExtractedLayers(newSlide.extractedLayers);
                        setSelectedLayerIndex(0);
                      }
                    }}
                    disabled={currentSlideIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {currentSlideIndex + 1} / {generatedSlides.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newIdx = Math.min(generatedSlides.length - 1, currentSlideIndex + 1);
                      setCurrentSlideIndex(newIdx);
                      const newSlide = generatedSlides[newIdx];
                      if (newSlide?.extractedLayers) {
                        setExtractedLayers(newSlide.extractedLayers);
                        setSelectedLayerIndex(0);
                      }
                    }}
                    disabled={currentSlideIndex === generatedSlides.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowLayersModal(false);
                  setExtractedLayers(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-hidden flex gap-4">
              {/* Layer List */}
              <div className="w-64 flex-shrink-0 border rounded-lg overflow-y-auto">
                <div className="p-2 border-b bg-slate-50 dark:bg-slate-800">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Layers</span>
                </div>
                <div className="p-2 space-y-1">
                  {extractedLayers.layers?.map((layer, idx) => (
                    <button
                      key={layer.id}
                      onClick={() => setSelectedLayerIndex(idx)}
                      className={`w-full text-left p-2 rounded-md transition-colors ${
                        selectedLayerIndex === idx
                          ? 'bg-green-100 dark:bg-green-900/30 border border-green-500'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {layer.type}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {layer.content || 'No content'}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Layer Preview */}
              <div className="flex-1 flex flex-col">
                <div className="p-2 border-b bg-slate-50 dark:bg-slate-800 rounded-t-lg border-x border-t">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Preview</span>
                </div>
                <div className="flex-1 border rounded-b-lg bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjBmMGYwIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNmMGYwZjAiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] p-4 overflow-auto">
                  {extractedLayers.layers?.[selectedLayerIndex] && (
                    <div className="space-y-4">
                      <img
                        src={extractedLayers.layers[selectedLayerIndex].image}
                        alt={`Layer ${selectedLayerIndex}`}
                        className="max-w-full h-auto border rounded shadow-lg"
                      />
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Type:</span>
                            <span className="ml-2 font-medium">{extractedLayers.layers[selectedLayerIndex].type}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Position:</span>
                            <span className="ml-2 font-medium">
                              [{extractedLayers.layers[selectedLayerIndex].bbox?.join(', ')}]
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Content:</span>
                            <span className="ml-2 font-medium">
                              {extractedLayers.layers[selectedLayerIndex].content || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                Detection: {extractedLayers.detection_model} | Extraction: {extractedLayers.extraction_method}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowLayersModal(false);
                    setExtractedLayers(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowLayersModal(false);
                    setShowLayerEditor(true);
                  }}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  Edit in Canvas
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Layer Editor */}
      {showLayerEditor && extractedLayers && (
        <LayerEditor
          extractedLayers={extractedLayers}
          onClose={() => {
            setShowLayerEditor(false);
          }}
          onSave={(updatedLayers, imageDataUrl) => {
            // Sanitize layers for serialization - remove Image objects, keep imageSrc
            const serializableLayers = updatedLayers.map(layer => {
              const { image, ...rest } = layer; // Remove non-serializable Image object
              return rest;
            });

            // Save layer edits AND update the visible slide preview
            setGeneratedSlides(prev => prev.map((slide, idx) =>
              idx === currentSlideIndex
                ? {
                    ...slide,
                    preview_url: imageDataUrl,
                    extractedLayers: {
                      ...slide.extractedLayers,
                      background: imageDataUrl,
                      layers: serializableLayers
                    }
                  }
                : slide
            ));
            setShowLayerEditor(false);
            setExtractedLayers(null);
            toast.success('Edits saved! Click "Update Project" to persist.', { duration: 3000 });
          }}
          onExport={(imageDataUrl, updatedLayers) => {
            // Update the slide with new image AND preserve layer data for continued editing
            // Sanitize layers for serialization
            const serializableLayers = (updatedLayers || extractedLayers?.layers || []).map(layer => {
              const { image, ...rest } = layer; // Remove non-serializable Image object
              return rest;
            });

            setGeneratedSlides(prev => prev.map((slide, idx) =>
              idx === currentSlideIndex
                ? {
                    ...slide,
                    preview_url: imageDataUrl,
                    // Preserve layer data so edits persist when saved
                    extractedLayers: {
                      ...slide.extractedLayers,
                      background: imageDataUrl,
                      layers: serializableLayers
                    }
                  }
                : slide
            ));
            setShowLayerEditor(false);
            setExtractedLayers(null);
            toast.success('Slide updated! Click "Update Project" to save.', { duration: 4000 });
          }}
        />
      )}
    </div>
  );

  return (
    <div className={externalChatMode ? "h-full flex flex-col bg-muted/30 overflow-hidden" : "min-h-screen bg-background"}>
      {/* Header — hidden in review mode (step 5) and external chat mode */}
      {currentStep !== 5 && !externalChatMode && (
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className={`mx-auto py-4 px-6 ${[2, 4].includes(currentStep) ? 'max-w-7xl' : 'max-w-5xl'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack}>
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">Create PPT</h1>
                  <p className="text-xs text-muted-foreground">AI-powered presentation creator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Content */}
      <div className={`${externalChatMode ? 'flex-1 flex flex-col overflow-hidden' : 'mx-auto'} ${currentStep === 5 ? (externalChatMode ? '' : 'px-4 py-4 max-w-full') : (externalChatMode ? '' : 'px-6')} ${currentStep !== 5 && !externalChatMode ? ([2, 4].includes(currentStep) ? 'py-8 max-w-7xl' : 'py-8 max-w-5xl') : ''}`}>
        {currentStep !== 5 && !externalChatMode && renderStepIndicator()}

        {currentStep === 1 && renderContentStep()}
        {currentStep === 1.5 && renderQuestionnaireStep()}
        {currentStep === 2 && (externalChatMode ? (
          /* r138: chat mode has no outline-review page — the outline was
             already reviewed in the chat. Step 2 here is either the brief
             auto-generate parking state or the landing after a 0-slide
             stop/failure, so render a compact ready-state panel instead of
             the legacy wizard page. */
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 text-center space-y-4">
              <div className="w-12 h-12 mx-auto rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{presentationTitle || generatedOutline?.title || 'Your presentation'}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {generatedOutline?.slides?.length
                    ? `Outline ready — ${generatedOutline.slides.length} slides. Nothing has been generated yet.`
                    : 'Nothing has been generated yet. Use the chat on the left to start over.'}
                </p>
              </div>
              {generatedOutline?.slides?.length > 0 && (
                <Button onClick={() => handleApplyOutlineAndProceed()} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate presentation
                </Button>
              )}
            </div>
          </div>
        ) : renderOutlineReviewStep())}
        {/* Step 3 removed — mode/theme selection now in questionnaire */}
        {currentStep === 4 && (['ai', 'html'].includes(generationMode) ? renderGeneratingStep() : renderLegacyGeneratingStep())}
        {currentStep === 5 && renderReviewStep()}
      </div>

      {/* Theme Customizer Sidebar */}
      {showThemeCustomizer && selectedTheme && (
        <ThemeCustomizer
          theme={selectedTheme}
          colorScheme={customColorScheme || selectedTheme.colorScheme}
          onColorSchemeChange={handleColorSchemeChange}
          onFontChange={(font) => {
            // Font changes are part of the color scheme for rendering
            handleColorSchemeChange({ ...(customColorScheme || selectedTheme.colorScheme), fontFamily: font });
          }}
          onApplyToAll={handleApplyThemeToAll}
          onClose={() => setShowThemeCustomizer(false)}
        />
      )}

      {/* Present Mode */}
      {/* Turn-into-template modal */}
      {showTemplateModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !savingTemplate && setShowTemplateModal(false)}
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground mb-1">Turn into a template</h3>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Saves this deck's design and slide layouts as a reusable template. Future
              presentations can be generated in exactly this style.
            </p>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Template name</label>
            <input
              value={templateNameDraft}
              onChange={(e) => setTemplateNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !savingTemplate) handleSaveAsTemplate(); }}
              placeholder="e.g. Client quarterly deck"
              autoFocus
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 mb-4 focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowTemplateModal(false)} disabled={savingTemplate}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveAsTemplate} disabled={savingTemplate}>
                {savingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />}
                {savingTemplate ? 'Saving…' : 'Save template'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showBrandFidelity && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowBrandFidelity(false)} role="dialog" aria-modal="true" aria-label="Brand fidelity report">
          <div className="flex max-h-[75vh] w-full max-w-xl flex-col rounded-2xl border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
              <h2 className="text-base font-semibold text-foreground">Brand & consistency check</h2>
              <button type="button" onClick={() => setShowBrandFidelity(false)} aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            {brandFidelityLoading ? (
              <div className="flex items-center justify-center gap-2 py-14 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Analyzing your deck…
              </div>
            ) : brandFidelity ? (
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {brandFidelity.brand && (
                    <div className="rounded-xl border border-border p-4">
                      <p className="text-xs text-muted-foreground mb-1">Brand fidelity{brandFidelity.brand.name ? ` · ${brandFidelity.brand.name}` : ''}</p>
                      <p className={`text-3xl font-bold ${brandFidelity.brand.score == null ? 'text-muted-foreground' : brandFidelity.brand.score >= 85 ? 'text-emerald-600' : brandFidelity.brand.score >= 65 ? 'text-amber-600' : 'text-red-600'}`}>{brandFidelity.brand.score ?? '—'}</p>
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {['colors', 'fonts', 'logo'].map(k => brandFidelity.brand.components?.[k] != null ? `${k} ${brandFidelity.brand.components[k]}` : null).filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  )}
                  <div className={`rounded-xl border border-border p-4 ${brandFidelity.brand ? '' : 'col-span-2'}`}>
                    <p className="text-xs text-muted-foreground mb-1">Deck consistency</p>
                    <p className={`text-3xl font-bold ${brandFidelity.consistency.score >= 85 ? 'text-emerald-600' : brandFidelity.consistency.score >= 65 ? 'text-amber-600' : 'text-red-600'}`}>{brandFidelity.consistency.score}</p>
                    <p className="text-[11px] text-muted-foreground mt-1.5">One visual system across all slides</p>
                  </div>
                </div>
                {[...(brandFidelity.brand?.violations || []), ...(brandFidelity.consistency?.violations || [])].length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No issues found — the deck is clean.</p>
                ) : (
                  <div className="space-y-1.5">
                    {[...(brandFidelity.brand?.violations || []), ...(brandFidelity.consistency?.violations || [])].map((v, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2">
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-muted-foreground w-20">{v.slide > 0 ? `Slide ${v.slide}` : 'Deck'} · {v.type}</span>
                        <span className="flex-1 min-w-0 text-xs text-foreground">{v.detail}</span>
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={() => fixFidelityViolation(v)}>Fix</Button>
                      </div>
                    ))}
                  </div>
                )}
                {!brandFidelity.vision_ran && (
                  <p className="text-[11px] text-muted-foreground">Visual review skipped (no slide previews yet) — scores are from code analysis only.</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {showPresentMode && generatedSlides.length > 0 && (
        <PresentationViewer
          slides={generatedSlides}
          startIndex={currentSlideIndex}
          onClose={() => setShowPresentMode(false)}
          title={presentationTitle || 'Presentation'}
        />
      )}

      {/* Floating indicator for automatic background extraction (per-slide) */}
      {bgExtractionProgress.isActive && bgExtractionProgress.total > 0 && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-card border border-border rounded-lg shadow-lg p-4 flex items-center gap-3">
            <div className="relative">
              <Layers className="w-5 h-5 text-purple-500" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">Making slides editable</span>
              <span className="text-xs text-muted-foreground">
                {bgExtractionProgress.current}/{bgExtractionProgress.total} done
              </span>
            </div>
            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                style={{ width: `${(bgExtractionProgress.current / bgExtractionProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Floating indicator for manual editable processing (when user clicks Make Editable) */}
      {makingEditable && editableProgress.total > 0 && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-card border border-border rounded-lg shadow-lg p-4 flex items-center gap-3">
            <div className="relative">
              <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">Making slides editable</span>
              <span className="text-xs text-muted-foreground">
                {editableProgress.current}/{editableProgress.total} done
              </span>
            </div>
            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300"
                style={{ width: `${(editableProgress.current / editableProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* r160: version history — every save is a restorable snapshot */}
      {versionsOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setVersionsOpen(false)}>
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <History className="w-4 h-4" /> Version history
            </h3>
            {versionList === null ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : versionList.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No versions yet — every save creates one.</p>
            ) : (
              <div className="space-y-2">
                {versionList.map((v, vi) => (
                  <div key={v.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">
                        {v.label || 'save'}{vi === 0 ? ' · latest' : ''}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {new Date(v.created_at).toLocaleString()} · {v.slide_count ?? '?'} slides{v.rev != null ? ` · rev ${v.rev}` : ''}
                      </p>
                    </div>
                    {vi !== 0 && (
                      <Button
                        size="sm" variant="outline" className="text-xs shrink-0"
                        disabled={!!versionBusy}
                        onClick={() => restoreVersion(v)}
                      >
                        {versionBusy === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Restore'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              Restoring never deletes anything — the current deck stays saved as its own version.
            </p>
          </div>
        </div>
      )}

      {/* Share modal */}
      {/* r130: teach-the-brand dialog — the current slide rides along as evidence */}
      {teachBrandOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !teachBusy && setTeachBrandOpen(false)}>
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <GraduationCap className="w-4 h-4" /> Teach this brand
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Spotted something off on this slide? Record the correction — every future
                generation with this brand (and the quality check) will follow it.
              </p>
            </div>
            {teachIncludeShot && generatedSlides[teachSlideIdx]?.preview_url && (
              <div className="relative">
                <img src={generatedSlides[teachSlideIdx].preview_url} alt={`Slide ${teachSlideIdx + 1}`} className="w-full rounded-lg border border-border" />
                {generatedSlides.length > 1 && (
                  <div className="absolute inset-x-0 bottom-1.5 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setTeachSlideIdx(i => Math.max(0, i - 1))}
                      disabled={teachSlideIdx === 0}
                      className="w-6 h-6 rounded-full bg-black/60 text-white text-sm leading-none disabled:opacity-40"
                      title="Previous slide"
                    >‹</button>
                    <span className="px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px]">
                      Slide {teachSlideIdx + 1} of {generatedSlides.length}
                    </span>
                    <button
                      onClick={() => setTeachSlideIdx(i => Math.min(generatedSlides.length - 1, i + 1))}
                      disabled={teachSlideIdx >= generatedSlides.length - 1}
                      className="w-6 h-6 rounded-full bg-black/60 text-white text-sm leading-none disabled:opacity-40"
                      title="Next slide"
                    >›</button>
                  </div>
                )}
              </div>
            )}
            <textarea
              value={teachText}
              onChange={(e) => setTeachText(e.target.value)}
              placeholder={'e.g. "Never use this hexagon shape on title slides"'}
              rows={2}
              maxLength={500}
              autoFocus
              className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
            <input
              value={teachScope}
              onChange={(e) => setTeachScope(e.target.value)}
              placeholder="Applies to (optional): e.g. title slides"
              maxLength={120}
              className="w-full text-xs px-3 py-1.5 rounded-lg border border-border bg-background text-foreground focus:outline-none"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={teachIncludeShot} onChange={(e) => setTeachIncludeShot(e.target.checked)} />
              Attach slide {teachSlideIdx + 1} as the example
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setTeachBrandOpen(false)} disabled={teachBusy}>Cancel</Button>
              <Button size="sm" onClick={submitTeachBrand} disabled={teachBusy || !teachText.trim()}>
                {teachBusy ? 'Saving…' : 'Save rule'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ShareContentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contentType="presentation"
        title={presentationTitle || 'Presentation'}
        userId={userId}
        slidesData={generatedSlides.filter(Boolean).map(s => ({ html_source: s.html_source, preview_url: s.preview_url, title: s.title }))}
      />

      {/* Drive Fact Verifier modal (used from externalChatMode where there's no right-panel Verify tab) */}
      {showDriveFactVerifier && userId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDriveFactVerifier(false)}
        >
          <div
            className="bg-background rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold">Fact Check with Drive Data</h3>
              </div>
              <button
                onClick={() => setShowDriveFactVerifier(false)}
                className="p-1 rounded hover:bg-muted transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col">
              <DriveFactVerifier slides={generatedSlides} userId={userId} />
            </div>
          </div>
        </div>
      )}

      {/* [DEV] Structured Slide Model viewer — Phase 1 test */}
      {showStructuredModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowStructuredModal(false)}
        >
          <div
            className="bg-background rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <div>
                  <h3 className="font-semibold">Structured Slide Model (Phase 1 Test)</h3>
                  <p className="text-xs text-muted-foreground">
                    Slide {currentSlideIndex + 1}: {generatedSlides[currentSlideIndex]?.title || '(no title)'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowStructuredModal(false)}
                className="p-1 rounded hover:bg-muted transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {structuredTesting && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating structured model with Gemini Flash...
                </div>
              )}
              {structuredError && (
                <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-3 text-xs mb-3">
                  <strong>Error:</strong> {structuredError}
                </div>
              )}
              {structuredModel && !structuredTesting && (
                <>
                  {!structuredModel._raw && (
                    <div className="mb-3 flex flex-wrap gap-3 text-xs">
                      <span className="px-2 py-1 rounded bg-muted">
                        Canvas: {structuredModel.canvas?.w}x{structuredModel.canvas?.h}
                      </span>
                      <span className="px-2 py-1 rounded bg-muted">
                        Elements: {structuredModel.elements?.length || 0}
                      </span>
                      {structuredModel.background?.color && (
                        <span className="px-2 py-1 rounded bg-muted flex items-center gap-1">
                          BG:
                          <span
                            className="inline-block w-3 h-3 rounded border border-border"
                            style={{ background: structuredModel.background.color }}
                          />
                          {structuredModel.background.color}
                        </span>
                      )}
                      {Array.isArray(structuredModel.elements) && (
                        <span className="px-2 py-1 rounded bg-muted">
                          Types: {Object.entries(
                            structuredModel.elements.reduce((acc, el) => {
                              acc[el.type] = (acc[el.type] || 0) + 1;
                              return acc;
                            }, {})
                          ).map(([t, c]) => `${t}×${c}`).join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                  <pre className="text-[11px] font-mono bg-muted p-3 rounded overflow-auto leading-relaxed">
                    {JSON.stringify(structuredModel._raw || structuredModel, null, 2)}
                  </pre>
                </>
              )}
            </div>
            <div className="p-3 border-t border-border flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                This is Phase 1: schema validation only. No HTML/PPTX rendering yet.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleTestStructuredSlide}
                disabled={structuredTesting}
                className="text-xs"
              >
                {structuredTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Re-generate'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CreatePPTWorkspace;
